import './polyfills';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  BUILTIN_STATIONS,
  findFetcher,
  findScheduleFetcher,
  loadBuiltinStations,
} from './builtins';
import type { ScheduleDay } from './metadata';
import { createBuildChecker, fetchDeployedVersion } from './buildCheck';
import { searchITunes, isLowResCoverUrl } from './coverArt';
import { FavoritesCoverStore, type FavCoverEntry } from './favoritesMetadata';
import { lookupLyrics } from './lyrics';
import type { LyricsResult } from './lyrics';
import { MetadataPoller, icyFetcher } from './metadata';
import { AudioPlayer } from './player';
import { track } from './telemetry';
import { pseudoFrequency } from './radioBrowser';
import { PAGE_SIZE, fetchStations, searchStations } from './stations';
import { GENRES, findGenre, stationMatchesGenre } from './genre-taxonomy';
import { stationQualityBucket, type QualityBucket } from './quality';
import { type BrowseSort, cycleSort, sortStations, orderFeaturedFirst } from './sort';
import {
  discoveryCounts,
  genreChips,
  countryChips,
  abbreviateCount,
  DISCOVERY_HIGHLIGHT_LIMIT,
  DISCOVERY_BROWSE_ALL_LOGO_LIMIT,
  type DiscoveryCounts,
} from './discovery';
import { loadDiscoverySummary, getDiscoverySummary } from './discovery-summary';
import {
  getSurpriseExclude,
  loadSurpriseExclude,
  parseRecentPicks,
  pickSurprise,
  rememberPick,
} from './surprise';
import {
  loadHighlights,
  resolveHighlights,
  todayISO,
  type Highlight,
  type ResolvedHighlight,
} from './highlights';
import {
  addCustom,
  clearLegacyWakeKeys,
  getCustom,
  getFavorites,
  getRecents,
  getString,
  isFavorite,
  pushRecent,
  removeCustom,
  reorderFavorites,
  setCustom,
  setFavorites,
  setRecents,
  setString,
  toggleFavorite,
  getQualityPref,
  setQualityPref,
} from './storage';
import type { BackupSettings } from './backup';
import {
  BackupParseError,
  backupFilename,
  mergeSnapshot,
  parseBackup,
  serializeBackup,
  summaryMessage,
} from './backup';
import {
  type StationList,
  addToList,
  createList,
  deleteList,
  getList,
  getLists,
  listContains,
  removeFromList,
  renameList,
  reorderLists,
  reorderListStations,
  setLists,
} from './lists';
import { STATS_WORKER_BASE } from './config';
import { countryName } from './country';
import {
  fetchUserRegion,
  geoRestrictionLabel,
  isAvailableInUserRegion,
} from './region';
import { playbackErrorView, REGION_LOCK_SUFFIX } from './playback-error';
import {
  aggregateDashboard,
  type DashboardData,
  type PublicLocationItem,
  type PublicTotals,
  type TopStationItem,
} from './dashboard';
import { emptyState, statusLine } from './empty';
import {
  installGlobalErrorHandlers,
  reportStreamError,
  reportWorkerError,
  truncateErrorMessage,
} from './errors';
import {
  addReceipt,
  applyStatuses,
  canSendReport,
  idsToPoll,
  loadReceipts,
  pruneReceipts,
  receiptForStation,
  receiptStatusText,
  REPORT_CATEGORIES,
  resolvedToastText,
  saveReceipts,
  type ReportCategory,
} from './brokenReports';
import { fetchReportStatuses, reportBrokenStation } from './reportBroken';
import { SHORTCUTS, shortcutFor, type ShortcutAction } from './shortcuts';
import { fmtSharePct, normalizeForSearch } from './format';
import {
  type MiniRefs,
  renderMiniPlayer as renderMiniPlayerImpl,
} from './render-mini';
import {
  type LyricsPaneRefs,
  type NowPlayingRefs,
  renderLyricsPane as renderLyricsPaneImpl,
  renderNowPlaying as renderNowPlayingImpl,
} from './render-np';
import { faviconClass, flagEmoji, stationInitials } from './station-display';
import {
  ICON_BACK,
  ICON_CHECK,
  ICON_CHEVRON_RIGHT,
  ICON_CLOSE,
  ICON_EMPTY,
  ICON_FAV,
  ICON_GRIP,
  ICON_HEART_FILL,
  ICON_HEART_LINE_CLASSED,
  ICON_LIST,
  ICON_LIST_ADD,
  ICON_MINUS_CIRCLE,
  ICON_PENCIL,
  ICON_PLAY,
  ICON_PLUS,
  ICON_RECENT,
  ICON_SEARCH,
  ICON_SHUFFLE,
  ICON_TRASH,
} from './icons';
import { isListQueue, resolveQueue, stepQueue, type QueueSource } from './queue';
import {
  bootstrapTheme,
  applyTheme,
  readStoredTheme,
  effectiveTheme,
} from './theme';
import { bootstrapAccent, readAccent, setAccent, DEFAULT_ACCENT } from './accent';
import { safeUrl, urlDisplay } from './url';
import { SLEEP_FADE_MS, SleepFade } from './sleepFade';
import type { NowPlaying, Station } from './types';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const SLEEP_CYCLE_MIN = [0, 15, 30, 60];

type Tab = 'browse' | 'fav' | 'library' | 'recent' | 'playing';
type ListTab = Exclude<Tab, 'playing'>;

// ─────────────────────────────────────────────────────────────
// Element refs
// ─────────────────────────────────────────────────────────────

// Audit #76: install error handlers before any other module-level work
// so a thrown exception during catalog load or a stray promise rejection
// in builtins.ts surfaces as an `error/runtime` or `error/promise`
// GoatCounter event instead of dying silently in the console.
installGlobalErrorHandlers();

const player = new AudioPlayer();
player.configure({
  qualityPref: getQualityPref(),
  // Favorites / recents saved before stream variants shipped carry only
  // `streamUrl`; pick up the catalog's `streams[]` by id (ADR 001).
  hydrate: (s) => {
    if (s.streams) return s;
    const streams = stationById(s.id)?.streams;
    return streams ? { ...s, streams } : s;
  },
  // Region-locked streams fail for good — don't walk the retry ladder.
  isPermanentFailure: (s) => !isAvailableInUserRegion(s),
});

let coverEnrichToken = 0;
let coverEnrichController: AbortController | undefined;

// Per-station "now playing" cover art for library feeds (Favorites, Lists,
// Recents) — iOS FavoriteNowPlayingStore parity. Polls the visible rows and
// paints each station's current-track cover into its card in place.
const favCovers = new FavoritesCoverStore(() => paintFavCovers());

const meta = new MetadataPoller((parsed) => {
  if (!parsed) {
    player.setTrackTitle(undefined);
    resetLyrics();
    return;
  }
  const display = parsed.track
    ? parsed.artist
      ? `${parsed.artist} — ${parsed.track}`
      : parsed.track
    : undefined;
  player.setTrackTitle(display, {
    ...parsed,
    programName: parsed.program?.name,
    programSubtitle: parsed.program?.subtitle,
  });

  // Lyrics — only when both artist + track are present (filters out
  // station IDs and news segments where parsed.artist stays undefined).
  if (parsed.artist && parsed.track) {
    loadLyrics(parsed.artist, parsed.track);
  } else {
    resetLyrics();
  }

  // iTunes Search serves two purposes:
  //
  //   1. Cover-art upgrade — when the station's metadata feed has no
  //      cover or supplies a known low-res one (Grrif's 246×246 JPEGs
  //      at /Medias/Covers/m/, visibly upscaled on retina inside our
  //      ~260 CSS-px frame), we prefer iTunes' 600×600.
  //   2. Track verification — `resultCount > 0` confirms the ICY title
  //      resolves to a real song. News/talk channels emit show names
  //      and station IDs ("BR24 Aktuell", "Nachrichten 12:00") that
  //      iTunes won't match; render-np gates the Spotify / Apple Music
  //      / YT Music search links on this signal so we don't ship users
  //      off to garbage search results.
  //
  // Both signals come from one request, so we issue the search on every
  // track change (not just when cover is missing/low-res) and let the
  // module-level cache short-circuit repeats. The cover-upgrade step
  // still gates on `!coverUrl || lowRes` so we don't downgrade good
  // station-supplied covers when the station's cover wins anyway.
  if (parsed.track) {
    const myToken = ++coverEnrichToken;
    coverEnrichController?.abort();
    coverEnrichController = new AbortController();
    const lowRes = parsed.coverUrl ? isLowResCoverUrl(parsed.coverUrl) : false;
    const wantsCoverUpgrade = !parsed.coverUrl || lowRes;
    void searchITunes(parsed.artist, parsed.track, coverEnrichController.signal).then(
      (result) => {
        if (myToken !== coverEnrichToken) return;
        const nextCover = wantsCoverUpgrade && result.cover ? result.cover : parsed.coverUrl;
        player.setTrackTitle(display, {
          ...parsed,
          coverUrl: nextCover,
          trackVerified: result.hit,
          programName: parsed.program?.name,
          programSubtitle: parsed.program?.subtitle,
        });
      },
    );
  }
});
const $body = document.body;

const $wordmark = document.getElementById('wordmark') as HTMLButtonElement;
const $search = document.getElementById('search') as HTMLInputElement;
const $searchClear = document.getElementById('search-clear') as HTMLButtonElement;
// Filter sheet (iOS BrowseFiltersSheet port) — collapsible multi-select
// sections built into #bf-sections, with a draft-model footer.
const $bfSections = document.getElementById('bf-sections') as HTMLElement;
const $bfCancel = document.getElementById('bf-cancel') as HTMLButtonElement;
const $bfClear = document.getElementById('bf-clear') as HTMLButtonElement;
const $bfApply = document.getElementById('bf-apply') as HTMLButtonElement;
const $tabStatus = document.getElementById('tab-status') as HTMLElement;
const $content = document.getElementById('content') as HTMLElement;
const $tabbar = document.getElementById('tabbar') as HTMLElement;
const $topnavNav = document.querySelector('.topnav-nav') as HTMLElement;

const $mini = document.getElementById('mini') as HTMLElement;
const $miniFav = document.getElementById('mini-fav') as HTMLElement;
const $miniArt = document.getElementById('mini-art') as HTMLElement;
const $miniName = document.getElementById('mini-name') as HTMLElement;
const $miniTrack = document.getElementById('mini-track') as HTMLElement;
const $miniMeta = document.getElementById('mini-meta') as HTMLElement;
const $miniBadge = document.getElementById('mini-badge') as HTMLElement;
const $miniFlag = document.getElementById('mini-flag') as HTMLElement;
const $miniProgram = document.getElementById('mini-program') as HTMLElement;
const $miniState = document.getElementById('mini-state') as HTMLElement;
const $miniSleep = document.getElementById('mini-sleep') as HTMLElement;
const $miniToggle = document.getElementById('mini-toggle') as HTMLElement;
const $miniPrev = document.getElementById('mini-prev') as HTMLElement;
const $miniSkip = document.getElementById('mini-skip') as HTMLElement;
const $miniVolume = document.getElementById('mini-volume') as HTMLElement;
const $miniVolumeSlider = document.getElementById('mini-volume-slider') as HTMLInputElement;
const $miniSlide = document.getElementById('mini-slide') as HTMLElement;
const $miniClose = document.getElementById('mini-close') as HTMLButtonElement;
const $miniCloseX = document.getElementById('mini-close-x') as HTMLButtonElement;

const $np = document.getElementById('np') as HTMLElement;
const $npName = document.getElementById('np-name') as HTMLElement;
const $npStationLogo = document.getElementById('np-station-logo') as HTMLImageElement;
const $npStationLogoBtn = document.getElementById('np-station-logo-btn') as HTMLButtonElement;
const $npBitrate = document.getElementById('np-bitrate') as HTMLElement;
const $npQuality = document.getElementById('np-quality') as HTMLElement;
const $npQualitySeg = document.getElementById('np-quality-seg') as HTMLElement;
const $npOrigin = document.getElementById('np-origin') as HTMLElement;
const $npListeners = document.getElementById('np-listeners') as HTMLElement;
const $npPaneTabs = document.getElementById('np-pane-tabs') as HTMLElement;
const $npPaneNow = document.getElementById('np-pane-now') as HTMLButtonElement;
const $npPaneProgram = document.getElementById('np-pane-program') as HTMLButtonElement;
const $npPaneLyrics = document.getElementById('np-pane-lyrics') as HTMLButtonElement;
const $npProgramPane = document.getElementById('np-program-pane') as HTMLElement;
const $npProgramHead = document.getElementById('np-program-head') as HTMLElement;
const $npProgramHeadName = document.getElementById('np-program-head-name') as HTMLElement;
const $npProgramHeadSub = document.getElementById('np-program-head-sub') as HTMLElement;
const $npProgramMeta = document.getElementById('np-program-meta') as HTMLElement;
const $npProgramCount = document.getElementById('np-program-count') as HTMLElement;
const $npProgramList = document.getElementById('np-program-list') as HTMLElement;
const $npProgramEmpty = document.getElementById('np-program-empty') as HTMLElement;
const $npLyricsPane = document.getElementById('np-lyrics-pane') as HTMLElement;
const $npLyricsHead = document.getElementById('np-lyrics-head') as HTMLElement;
const $npLyricsTitle = document.getElementById('np-lyrics-title') as HTMLElement;
const $npLyricsArtist = document.getElementById('np-lyrics-artist') as HTMLElement;
const $npLyricsText = document.getElementById('np-lyrics-text') as HTMLElement;
const $npLyricsSource = document.getElementById('np-lyrics-source') as HTMLAnchorElement;
const $npLyricsSourceText = document.getElementById('np-lyrics-source-text') as HTMLElement;
const $npLyricsEmpty = document.getElementById('np-lyrics-empty') as HTMLElement;
const $npSecondaryEmpty = document.getElementById('np-secondary-empty') as HTMLElement;
const $npClose = document.getElementById('np-close') as HTMLButtonElement;
const $npTrackRow = document.getElementById('np-track-row') as HTMLElement;
const $npTrackTitle = document.getElementById('np-track-title') as HTMLElement;
const $npTrackArtist = document.getElementById('np-track-artist') as HTMLElement;
const $npTrackProgram = document.getElementById('np-track-program') as HTMLElement;
const $npTrackStatus = document.getElementById('np-track-status') as HTMLElement;
const $npTrackStatusText = document.getElementById('np-track-status-text') as HTMLElement;
const $npError = document.getElementById('np-error') as HTMLElement;
const $npErrorHint = document.getElementById('np-error-hint') as HTMLElement;
const $npErrorRetry = document.getElementById('np-error-retry') as HTMLButtonElement;
const $npErrorReport = document.getElementById('np-error-report') as HTMLButtonElement;
const $npTrackCover = document.getElementById('np-track-cover') as HTMLImageElement;
const $npTrackCoverWrap = document.getElementById('np-track-cover-wrap') as HTMLElement;
const $npTrackSpotify = document.getElementById('np-track-spotify') as HTMLAnchorElement;
const $npTrackAppleMusic = document.getElementById('np-track-apple-music') as HTMLAnchorElement;
const $npTrackYoutubeMusic = document.getElementById('np-track-youtube-music') as HTMLAnchorElement;
const $npTrackOpenInWrap = document.getElementById('np-track-open-in-wrap') as HTMLElement;
const $npStream = document.getElementById('np-stream') as HTMLAnchorElement;
const $npStreamHost = document.getElementById('np-stream-host') as HTMLElement;
const $npHome = document.getElementById('np-home') as HTMLAnchorElement;
const $npHomeHost = document.getElementById('np-home-host') as HTMLElement;
const $npReportBroken = document.getElementById('np-report-broken') as HTMLButtonElement;
const $npReportBrokenLabel = document.getElementById('np-report-broken-label') as HTMLElement;
const $npReportStatus = document.getElementById('np-report-status') as HTMLElement;
const $npReportMail = document.getElementById('np-report-mail') as HTMLAnchorElement;
const $npReport = document.getElementById('np-report') as HTMLElement;
const $npReportScrim = document.getElementById('np-report-scrim') as HTMLElement;
const $npReportForm = document.getElementById('np-report-form') as HTMLFormElement;
const $npReportStation = document.getElementById('np-report-station') as HTMLElement;
const $npReportComment = document.getElementById('np-report-comment') as HTMLTextAreaElement;
const $npReportSend = document.getElementById('np-report-send') as HTMLButtonElement;
const $npReportClose = document.getElementById('np-report-close') as HTMLButtonElement;
const $npFav = document.getElementById('np-fav') as HTMLButtonElement;
const $npAdd = document.getElementById('np-add') as HTMLButtonElement;
const $npPrev = document.getElementById('np-prev') as HTMLButtonElement;
const $npNext = document.getElementById('np-next') as HTMLButtonElement;
const $npSleep = document.getElementById('np-sleep') as HTMLButtonElement;
const $npSleepChip = document.getElementById('np-sleep-chip') as HTMLElement;
const $npPlay = document.getElementById('np-play') as HTMLButtonElement;
const $npMute = document.getElementById('np-mute') as HTMLButtonElement;
const $npVolume = document.getElementById('np-volume') as HTMLElement;
const $npVolumeSlider = document.getElementById('np-volume-slider') as HTMLInputElement;
const $npVolumeValue = document.getElementById('np-volume-value') as HTMLElement;
const $npInfo = document.getElementById('np-info') as HTMLElement;
const $npInfoScrim = document.getElementById('np-info-scrim') as HTMLElement;
const $npInfoClose = document.getElementById('np-info-close') as HTMLButtonElement;

const $addForm = document.getElementById('add-form') as HTMLFormElement;
const $addError = document.getElementById('add-error') as HTMLElement;
const $customList = document.getElementById('custom-list') as HTMLElement;

const $listSheet = document.getElementById('list-sheet') as HTMLElement;
const $listCancel = document.getElementById('list-cancel') as HTMLButtonElement;
const $listSheetLabel = document.getElementById('list-sheet-label') as HTMLElement;
const $listSheetBody = document.getElementById('list-sheet-body') as HTMLElement;

const $dashboardSheet = document.getElementById('dashboard-sheet') as HTMLElement;

// iOS-style top toolbar: filter funnel + settings gear, each opening a
// slide-up sheet. The inline filter controls are relocated into
// #filter-sheet at boot (see below).
const $filterBtn = document.getElementById('filter-btn') as HTMLButtonElement;
const $filterDot = document.getElementById('filter-dot') as HTMLElement;
const $filterSheet = document.getElementById('filter-sheet') as HTMLElement;
const $filterClose = document.getElementById('filter-close') as HTMLButtonElement;
const $settingsBtn = document.getElementById('settings-btn') as HTMLButtonElement;
const $settingsSheet = document.getElementById('settings-sheet') as HTMLElement;
const $settingsClose = document.getElementById('settings-close') as HTMLButtonElement;
const $themeSeg = document.getElementById('theme-seg') as HTMLElement;
const $accentSeg = document.getElementById('accent-seg') as HTMLElement;
const $accentRow = document.getElementById('accent-row') as HTMLElement;
const $accentPicker = document.getElementById('accent-picker') as HTMLInputElement;
const $landingSeg = document.getElementById('landing-seg') as HTMLElement;
const $msApple = document.getElementById('ms-apple') as HTMLButtonElement;
const $msSpotify = document.getElementById('ms-spotify') as HTMLButtonElement;
const $msYoutube = document.getElementById('ms-youtube') as HTMLButtonElement;
const $settingsBackup = document.getElementById('settings-backup') as HTMLButtonElement;
const $settingsRestore = document.getElementById('settings-restore') as HTMLButtonElement;
const $settingsStats = document.getElementById('settings-stats') as HTMLButtonElement;
const $settingsTabs = document.getElementById('settings-tabs') as HTMLElement;
const $dashboardClose = document.getElementById('dashboard-close') as HTMLButtonElement;
const $dashPlays = document.getElementById('dash-plays') as HTMLElement;
const $dashVisits = document.getElementById('dash-visits') as HTMLElement;
const $dashMap = document.getElementById('dash-map') as HTMLElement;
const $dashCountryTable = document.querySelector('#dash-country-table tbody') as HTMLTableSectionElement;
const $dashStationTable = document.querySelector('#dash-station-table tbody') as HTMLTableSectionElement;

// ─────────────────────────────────────────────────────────────
// State
// ─────────────────────────────────────────────────────────────

let activeTab: Tab = 'browse';
// Favorites edit mode: trash toggles a per-row remove affordance (the
// red minus iOS delete mode shows). Survives the re-render the toggle
// triggers; resets when leaving the tab or emptying the list.
let favEditing = false;
// Library edit mode: the header trash toggles a per-row delete affordance
// on the lists index (mirrors favEditing). The delete control lives at the
// top, not on every card. Resets when leaving Library or emptying the lists.
let libEditing = false;

// Browse multi-select "add to <target>" mode — the + on a Favorites or
// list-detail header switches to Browse and lets the user tick stations,
// then "Add N" adds them all to the target and returns there (iOS #57).
// `picked` holds the chosen Station objects; `existing` is the snapshot of
// stations already in the target (shown ticked + disabled, not re-pickable).
type SelectTarget =
  | { kind: 'favorites' }
  | { kind: 'list'; id: string; name: string }
  // A list that doesn't exist yet — created on save (iOS atomic create+add),
  // so cancelling step 2 of the add-list flow leaves no empty list behind.
  | { kind: 'newList'; name: string };
let selectMode: {
  target: SelectTarget;
  picked: Map<string, Station>;
  existing: Set<string>;
  // Show the "1 Choose list ✓ — 2 Pick stations" strip above the dock — set
  // only when the flow reached step 2 via the choose-list popup (pickStations).
  withSteps?: boolean;
} | null = null;
let $selectDock: HTMLElement | null = null;
/** Last list tab we were on, so closing Now Playing returns there. */
let lastListTab: ListTab = 'browse';
// Which list is open in the Library detail view (null = the Library home).
let openListId: string | null = null;
// Where the current station was started from — previous/next step through
// it (see ./queue). null = no known context → Favorites.
let activeQueue: QueueSource | null = null;
// Add-to-list popup (iOS AddListPopupCard). null when closed. 'addNow' adds the
// known `station`; 'pickStations' chooses/creates a list then hands off to the
// Browse select dock. `target` is the chosen existing list or a drafted new
// list (created only on save); `nameDraft` backs the "Create list" field.
type AddListMode = 'addNow' | 'pickStations';
type AddListTarget = { kind: 'existing'; id: string } | { kind: 'new' };
let addListState: {
  mode: AddListMode;
  station: Station | null;
  target: AddListTarget | null;
  nameDraft: string;
} | null = null;
// Transient in-app list-management UI state (replaces native prompt/confirm).
// All cleared on navigation so a half-typed create/rename never lingers.
let listCreateOpen = false; // inline "name your list" row in the lists index
let listRenameOpen = false; // inline rename input in the list-detail header
let listDeleteConfirmId: string | null = null; // inline "Delete list?" confirm
// List-detail edit mode (#641): the header trash toggles a per-row remove
// (−) affordance, mirroring Favorites' favEditing. Deleting the whole list
// lives on the Library-home row control.
let listEditing = false;
// Browse filter — multi-select, mirroring the iOS BrowseFilter model.
// Genre ids (from GENRES) and uppercase ISO country codes; News is the
// in-filter toggle iOS keeps in the Genre section. When ANY of these (or
// activeQuality) is set, the Browse list is matched locally against the
// catalog — no Radio Browser fetch — so the live "Show N" count is real.
const filterGenres = new Set<string>();
const filterCountries = new Set<string>();
let filterNews = false;
// Alphabet sort for the result list (off → A–Z → Z–A). Lives on the page
// (the results row), not in the filter. Suppressed while a text query is
// active (relevance order wins).
let activeSort: BrowseSort = null;
// Stream-quality buckets to keep (empty = no quality filter). Part of the
// filter; matched locally, never forwarded to Radio Browser.
const activeQuality = new Set<QualityBucket>();
// True once the user taps "Browse all" on the discovery landing — drops
// into the flat catalog list. Cleared by back-to-discovery / goHome.
let browseAll = false;
// Raw editorial highlights feed (loaded once at boot); resolved against
// the catalog at render time for the discovery Featured rail.
let highlightsRaw: Highlight[] = [];
// Memoised discovery counts, recomputed when the catalog size changes.
let discoveryCountsCache: DiscoveryCounts | null = null;
let discoveryCountsForLen = -1;

// countryName lives in ./country.

/** Distinct uppercase country codes present in the catalog, ordered by
 *  display name. Memoised against catalog size; feeds the filter sheet's
 *  Country section. Empty before stations.json loads. */
let catalogCountriesCache: string[] | null = null;
let catalogCountriesForLen = -1;
function catalogCountries(): string[] {
  if (catalogCountriesCache && catalogCountriesForLen === BUILTIN_STATIONS.length) {
    return catalogCountriesCache;
  }
  const codes = new Set<string>();
  for (const s of BUILTIN_STATIONS) {
    if (s.country && s.country.length >= 2) codes.add(s.country.toUpperCase());
  }
  catalogCountriesCache = [...codes].sort((a, b) => countryName(a).localeCompare(countryName(b)));
  catalogCountriesForLen = BUILTIN_STATIONS.length;
  return catalogCountriesCache;
}
let queryToken = 0;
let sleepIndex = 0;
let sleepTimer: number | undefined;
let currentNP: NowPlaying = {
  station: { id: '', name: '', streamUrl: '' },
  state: 'idle',
};
let lastBrowseStations: Station[] = [];
// Browse pagination state — Radio Browser pages 60 stations at a
// time. We refetch from offset 0 whenever the query/tag filter
// changes; "Load more" appends the next page.
let browseOffset = 0;
let browseHasMore = false;
let browseLoadingMore = false;

// Infinite scroll: the active result list's "load more" action, or null
// when nothing more is available. renderContent() recomputes it each render;
// the $content scroll listener (wired in init) fires it as the user nears the
// bottom — iOS parity, so stations load on scroll, not via a button tap. The
// manual "Load more" / "Show more" buttons stay as a keyboard/fallback path.
let pendingLoadMore: (() => void) | null = null;
// A short first page (RB dedupes a 60-page down to ~25) may not fill a tall
// viewport, leaving nothing to scroll to engage infinite scroll. Auto-pull the
// next batch(es) until the list overflows — capped per view so a degenerate
// run of all-duplicate RB pages can't loop forever (the manual button remains).
let autoFillTries = 0;
const AUTO_FILL_MAX = 6;

// Local-catalog pagination — once the curated YAML grew past ~2k
// stations, rendering the whole list on every renderContent() call
// (tab switch, filter change) added ~1s of DOM-build time. Cap the
// initial render and add a "Show more" button. The cap resets when
// the active view changes (different tab / mode / filter / search);
// it persists across "Show more" clicks within the same view.
const HOME_VIEW_PAGE_SIZE = 100;
let homeViewLimit = HOME_VIEW_PAGE_SIZE;
let lastViewSig = '';

// SVG icon constants live in ./icons (audit #77 — split large modules).

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

// stationInitials + faviconClass live in ./station-display.

function favIdSet(): Set<string> {
  return new Set(getFavorites().map((s) => s.id));
}

function filterStations(stations: Station[], query: string): Station[] {
  const q = query.trim().toLowerCase();
  if (!q) return stations;
  const qNorm = normalizeForSearch(q);
  return stations.filter((s) => {
    if (s.name.toLowerCase().includes(q)) return true;
    if ((s.tags ?? []).some((t) => t.toLowerCase().includes(q))) return true;
    if (s.country && s.country.toLowerCase().includes(q)) return true;
    if (!qNorm) return false;
    if (normalizeForSearch(s.name).includes(qNorm)) return true;
    if ((s.tags ?? []).some((t) => normalizeForSearch(t).includes(qNorm))) return true;
    return false;
  });
}

function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let t: number | undefined;
  return (...args: A) => {
    if (t !== undefined) window.clearTimeout(t);
    t = window.setTimeout(() => fn(...args), ms);
  };
}

function buildFavicon(station: Station, size = 38): HTMLElement {
  const fav = document.createElement('div');
  fav.className = faviconClass(station.id);
  fav.style.width = `${size}px`;
  fav.style.height = `${size}px`;
  if (station.bitrate) fav.title = `${station.bitrate} kbps`;

  const drawInitials = (): void => {
    fav.replaceChildren();
    const span = document.createElement('span');
    span.textContent = stationInitials(station.name);
    fav.append(span);
    if (station.frequency) {
      const freq = document.createElement('span');
      freq.className = 'freq-mini';
      freq.textContent = station.frequency;
      fav.append(freq);
    }
  };

  if (station.favicon) {
    const img = document.createElement('img');
    img.src = station.favicon;
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', drawInitials, { once: true });
    fav.append(img);
  } else {
    drawInitials();
  }
  return fav;
}

function buildEq(paused: boolean): HTMLElement {
  const eq = document.createElement('div');
  eq.className = 'eq' + (paused ? ' paused' : '');
  eq.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 4; i++) eq.append(document.createElement('span'));
  return eq;
}

function buildHeart(isFav: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'heart' + (isFav ? ' is-fav' : '');
  btn.setAttribute('aria-label', isFav ? 'Remove favorite' : 'Add favorite');
  btn.innerHTML = ICON_HEART_LINE_CLASSED + ICON_HEART_FILL;
  return btn;
}

function buildAddListBtn(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'row-addlist';
  btn.setAttribute('aria-label', 'Add to list');
  btn.title = 'Add to a list';
  btn.innerHTML = ICON_LIST_ADD;
  return btn;
}

interface RowOptions {
  /** Library feeds (Favorites / Lists / Recents) carry a trailing cover-art
   *  slot showing the station's current-track art (iOS parity). Browse rows
   *  don't. */
  cover?: boolean;
  /** Playback context a tap on this row starts — becomes the active queue
   *  previous/next step through. Omitted → Favorites fallback. */
  queue?: QueueSource;
}

function buildRow(
  station: Station,
  currentId: string,
  state: NowPlaying['state'],
  favs: Set<string>,
  opts: RowOptions = {},
): HTMLDivElement {
  const isCurrent = !!currentId && station.id === currentId;
  const isPaused = isCurrent && state !== 'playing';
  const isFav = favs.has(station.id);
  // `availableIn` is a curated list of countries where the stream is
  // known to be reachable. When the visitor's country (from the
  // worker's CF-IPCountry lookup) is outside that list, dim the row
  // and append a "Switzerland only" / "Only in CH, FR" badge so the
  // user knows up front rather than discovering it on a tap.
  const geoRestricted = !isAvailableInUserRegion(station);
  const geoLabel = geoRestricted ? geoRestrictionLabel(station, countryName) : null;

  const row = document.createElement('div');
  row.className =
    'row' + (isCurrent ? ' is-playing' : '') + (geoRestricted ? ' is-geo-restricted' : '');
  row.setAttribute('role', 'button');
  row.tabIndex = 0;
  row.dataset.id = station.id;

  const fav = buildFavicon(station, 38);

  const info = document.createElement('div');
  info.className = 'row-info';
  const name = document.createElement('div');
  name.className = 'row-name';
  name.textContent = station.name;
  const flag = flagEmoji(station.country);
  if (flag) {
    const flagSpan = document.createElement('span');
    flagSpan.className = 'row-flag';
    flagSpan.textContent = flag;
    flagSpan.title = countryName(station.country!);
    name.append(' ', flagSpan);
  }
  const tags = document.createElement('div');
  tags.className = 'row-tags';
  if (geoLabel) {
    const geo = document.createElement('span');
    geo.className = 'row-geo';
    geo.textContent = geoLabel;
    // Hover/long-press tooltip — same copy the player error path
    // falls back to when AVPlayer or the browser fails on the 401.
    geo.title = `${geoLabel} — likely a music-licensing geo-block from the broadcaster.`;
    tags.append(geo);
  }
  // Genre tags appear on Browse rows only. Library cards (Favorites / Lists
  // / Recents) show the now-playing track under the name instead — or just
  // the station name when nothing is playing (iOS favorites layout).
  if (!opts.cover) {
    const tagsText = document.createElement('span');
    tagsText.className = 'row-tags__text';
    tagsText.textContent = (station.tags ?? []).slice(0, 3).join(' · ');
    tags.append(tagsText);
  }
  info.append(name);
  // Only attach the tags line when it has content: Browse always has the
  // genre text; a library card only when the station is geo-restricted.
  if (tags.childElementCount > 0) info.append(tags);
  // Library feeds carry a now-playing subtitle (artist — track) shown once
  // the cover poll resolves a track.
  if (opts.cover) {
    const now = document.createElement('div');
    now.className = 'row-now';
    now.hidden = true;
    info.append(now);
  }

  // Browse multi-select: the row's trailing control is a pick tick, and a
  // tap toggles selection instead of playing. Stations already in Favorites
  // show a filled tick and are inert. (Library cards never enter this mode.)
  if (selectMode && !opts.cover) {
    const already = selectMode.existing.has(station.id);
    const picked = selectMode.picked.has(station.id);
    const tick = document.createElement('span');
    tick.className = 'row-tick' + (already ? ' is-added' : picked ? ' is-picked' : '');
    if (already || picked) tick.innerHTML = ICON_CHECK;
    if (already) row.classList.add('is-added');
    row.append(fav, info, tick);
    if (!already) {
      row.addEventListener('click', () => toggleSelectPick(station, tick));
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSelectPick(station, tick);
        }
      });
    }
    return row;
  }

  // Library feeds (Favorites / Lists / Recents) render as iOS-style cards:
  // no inline add/heart/equalizer — the trailing slot is the station's
  // now-playing cover art, and the subtitle carries the current track.
  // Browse rows keep the equalizer + add-to-list + favorite controls.
  if (opts.cover) {
    const cover = document.createElement('span');
    cover.className = 'row-cover';
    const entry = favCovers.get(station.id);
    if (entry) setRowCover(cover, entry.coverUrl);
    row.append(fav, info, cover);
    applyRowNowPlaying(row, entry);
  } else {
    const right = document.createElement('div');
    right.className = 'row-right';
    const eq = buildEq(isPaused);
    const addList = buildAddListBtn();
    addList.addEventListener('click', (e) => {
      e.stopPropagation();
      openAddList({ mode: 'addNow', station });
    });
    const heart = buildHeart(isFav);
    heart.addEventListener('click', (e) => {
      e.stopPropagation();
      onToggleFav(station);
    });
    right.append(eq, addList, heart);
    row.append(fav, info, right);
  }

  row.addEventListener('click', () => onRowPlay(station, opts.queue));
  row.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowPlay(station, opts.queue);
    }
  });

  return row;
}

/** Fill a row's cover slot with a track-art image. Idempotent (no-op when the
 *  URL is unchanged). A broken/blocked cover removes itself so the slot
 *  collapses back to the station-logo-only layout. */
function setRowCover(slot: HTMLElement, url: string): void {
  let img = slot.querySelector('img');
  if (img && img.getAttribute('src') === url) return;
  if (!img) {
    img = document.createElement('img');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.addEventListener('error', () => {
      slot.classList.remove('has-cover');
      img?.remove();
    });
    slot.appendChild(img);
  }
  img.src = url;
  slot.classList.add('has-cover');
}

/** Build the now-playing subtitle line ("artist — track", or just the
 *  track) from a cover-store entry. Empty when there's no resolved track. */
function nowPlayingLine(entry: FavCoverEntry | undefined): string {
  if (!entry?.title) return '';
  return entry.artist ? `${entry.artist} — ${entry.title}` : entry.title;
}

/** Swap a library row's subtitle between the genre tags and the live
 *  now-playing line. When a track is known it wins (matching iOS, which
 *  shows the current track under the station name); otherwise the genre
 *  tags stay visible. */
function applyRowNowPlaying(row: HTMLElement, entry: FavCoverEntry | undefined): void {
  const now = row.querySelector<HTMLElement>('.row-now');
  if (!now) return;
  const tags = row.querySelector<HTMLElement>('.row-tags');
  const line = nowPlayingLine(entry);
  if (line) {
    now.textContent = line;
    now.hidden = false;
    if (tags) tags.hidden = true;
  } else {
    now.hidden = true;
    if (tags) tags.hidden = false;
  }
}

/** Repaint every visible cover slot + now-playing line from the store.
 *  Called when a poll cycle lands new art — patches the DOM in place (no
 *  re-render, no scroll loss). */
function paintFavCovers(): void {
  for (const slot of document.querySelectorAll<HTMLElement>('.row-cover')) {
    const row = slot.closest<HTMLElement>('.row');
    const id = row?.dataset.id;
    if (!id || !row) continue;
    const entry = favCovers.get(id);
    if (entry) setRowCover(slot, entry.coverUrl);
    applyRowNowPlaying(row, entry);
  }
}

/** Point the cover poll at the rows a library feed just rendered. */
function armFavCovers(stations: Station[]): void {
  favCovers.setVisibleStations(stations);
  favCovers.start();
}

// ─────────────────────────────────────────────────────────────
// Status text helpers
// ─────────────────────────────────────────────────────────────

// Pure NP/mini label helpers live in ./np-labels.

// ─────────────────────────────────────────────────────────────
// Render — Mini Player
// ─────────────────────────────────────────────────────────────

// setMiniArt + renderMiniPlayer live in ./render-mini (refs-based).
const MINI_REFS: MiniRefs = {
  mini: $mini,
  miniFav: $miniFav,
  miniArt: $miniArt,
  miniName: $miniName,
  miniTrack: $miniTrack,
  miniMeta: $miniMeta,
  miniBadge: $miniBadge,
  miniFlag: $miniFlag,
  miniProgram: $miniProgram,
  miniState: $miniState,
  miniSleep: $miniSleep,
  miniToggle: $miniToggle,
};

function renderMiniPlayer(np: NowPlaying): void {
  renderMiniPlayerImpl(MINI_REFS, np, {
    // Armed through the final fade, too (the timer handle clears when the
    // fade starts).
    sleepArmed: sleepTimer !== undefined || sleepFade.active,
    fromList: activeQueue?.kind === 'list',
  });
}

// ─────────────────────────────────────────────────────────────
// Render — Now Playing
// ─────────────────────────────────────────────────────────────

// renderNowPlaying lives in ./render-np (refs-based). The local
// wrapper closes over the production refs and
// the popup-cleanup callback so the rest of main.ts can call it
// with just (np).
const NP_REFS: NowPlayingRefs = {
  body: $body,
  npName: $npName,
  npStationLogo: $npStationLogo,
  npStationLogoBtn: $npStationLogoBtn,
  npBitrate: $npBitrate,
  npQuality: $npQuality,
  npQualitySeg: $npQualitySeg,
  npOrigin: $npOrigin,
  npListeners: $npListeners,
  npTrackRow: $npTrackRow,
  npTrackTitle: $npTrackTitle,
  npTrackArtist: $npTrackArtist,
  npTrackProgram: $npTrackProgram,
  npTrackStatus: $npTrackStatus,
  npTrackStatusText: $npTrackStatusText,
  npError: $npError,
  npErrorHint: $npErrorHint,
  npTrackCover: $npTrackCover,
  npTrackCoverFallback: document.getElementById(
    'np-track-cover-fallback',
  ) as HTMLElement,
  npTrackSpotify: $npTrackSpotify,
  npTrackAppleMusic: $npTrackAppleMusic,
  npTrackYoutubeMusic: $npTrackYoutubeMusic,
  npTrackOpenInWrap: $npTrackOpenInWrap,
  npStream: $npStream,
  npStreamHost: $npStreamHost,
  npHome: $npHome,
  npHomeHost: $npHomeHost,
  npReportBroken: $npReportBroken,
  npFav: $npFav,
  npPlay: $npPlay,
};

function renderNowPlaying(np: NowPlaying): void {
  renderNowPlayingImpl(NP_REFS, np, {
    isFavorite,
    onClearOpenIn: () => {},
    qualityPref: player.getQualityPref(),
  });
  // The inline open-in row is always-visible (when verified), so reflect the
  // per-service Settings toggles on every render.
  syncMusicServiceLinks();
  // Keep the wide-layout body class (np-twocol) in sync as
  // the station loads/changes/stops — has-station has just been toggled
  // upstream, so the mode is current here.
  syncNpTabs();
}

// ─────────────────────────────────────────────────────────────
// Render — Top bar (search/tags/status visibility)
// ─────────────────────────────────────────────────────────────

function renderTopBar(): void {
  // Search is available on the list tabs. Genre filter is Browse-only.
  // The Playing tab keeps the topbar quiet (no search/genre input —
  // they don't apply to a single-station view).
  // Filters apply to Browse only, but the top nav is stable chrome: rather
  // than removing the funnel off-Browse (which would reflow the centred
  // nav+search group), keep its slot and just hide it visually.
  $filterBtn.classList.toggle('slot-hidden', activeTab !== 'browse');
  $search.placeholder =
    activeTab === 'fav'
      ? 'Search your favorites…'
      : activeTab === 'recent'
        ? 'Search recently played…'
        : activeTab === 'library'
          ? 'Search lists…'
          : 'Search stations, genres, places…';
  // tab-status used to repeat the section name + count under the search
  // bar on Library views; the section label below already says it. Always
  // hidden now; kept in the DOM in case a future tab wants the slot.
  $tabStatus.hidden = true;
}

function renderTabBar(): void {
  // Active-state spans both the bottom tab bar (mobile) and the top-nav
  // section links (desktop) so they never disagree.
  document
    .querySelectorAll<HTMLButtonElement>('.tabbar .tab-btn, .topnav-nav .tab-btn')
    .forEach((btn) => {
    const t = btn.dataset.tab;
    // The Library nav button stays active across the Library home and its
    // sub-views (Recents, a list detail) so the nav doesn't blink.
    const isActive = t === activeTab || (t === 'library' && isLibraryTab(activeTab));
    btn.classList.toggle('active', isActive);
  });
}

// ─────────────────────────────────────────────────────────────
// Render — Content
// ─────────────────────────────────────────────────────────────

function sectionLabel(
  label: string,
  count: number,
  actions?: HTMLElement[],
  lead?: HTMLElement | HTMLElement[],
): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'section-label';
  // A `lead` (e.g. the search icon, optionally preceded by a back arrow)
  // promotes the label to the iOS LibraryPageStatusBar layout: a 3-column
  // grid (lead · centred title · actions). Without one, the existing centred
  // / space-between flex applies.
  const leads = lead ? (Array.isArray(lead) ? lead : [lead]) : [];
  if (leads.length) wrap.classList.add('section-label--header');
  else if (actions?.length) wrap.classList.add('section-label--with-actions');

  if (leads.length) {
    const zone = document.createElement('div');
    zone.className = 'section-label__lead';
    zone.append(...leads);
    wrap.append(zone);
  }

  const title = document.createElement('div');
  title.className = 'section-label__title';
  const left = document.createElement('span');
  left.textContent = label;
  const right = document.createElement('span');
  right.className = 'count';
  right.textContent = String(count).padStart(2, '0');
  title.append(left, right);
  wrap.append(title);

  if (actions?.length) {
    const slot = document.createElement('div');
    slot.className = 'section-label__actions';
    slot.append(...actions);
    wrap.append(slot);
  }
  return wrap;
}

/** Tabs that live under the Library nav button: the Library home and its
 *  Recents sub-view. (Favorites is its own top-level tab, not grouped here.) */
function isLibraryTab(tab: Tab): boolean {
  return tab === 'library' || tab === 'recent';
}

// Default to a 7-day window to match the admin dashboard's headline
// numbers.
const STATS_DAYS = 7;
// Public stats sheet uses a single batched endpoint so totals + top
// stations + locations all come from the same in-Worker snapshot.
// Splitting them across four endpoints with independent edge-cache
// windows (and four browser HTTP cache entries) was the cause of
// "huge differences between devices" — each device could be reading
// any combination of four different points in time.
const DASHBOARD_URL = `${STATS_WORKER_BASE}/api/public/dashboard?days=${STATS_DAYS}`;

// Schedule (program guide) state for the currently-open Now Playing
// station. Fetched once when NP opens for stations whose broadcaster
// has a schedule API; null otherwise (the program panel stays hidden).
let npSchedule: ScheduleDay[] | null = null;
let npScheduleStationId: string | null = null;
let npScheduleAbort: AbortController | null = null;
let npSelectedDayIdx = 0;

// Lyrics state — fetched per track when artist+title are both available.
// Null means "we asked, neither LRCLIB nor Lyrics.ovh had it"; undefined
// means "haven't asked yet". Cache lives inside src/lyrics.ts.
let npLyrics: LyricsResult | null | undefined;
let npLyricsKey = ''; // `<artist>::<track>` lowercase
let npLyricsAbort: AbortController | undefined;

type NpView = 'now' | 'program' | 'lyrics';
let npView: NpView = 'now';

async function loadSchedule(station: Station): Promise<void> {
  // Cancel any in-flight load for a previous station, reset cached data.
  if (npScheduleAbort) npScheduleAbort.abort();
  npSchedule = null;
  npScheduleStationId = station.id;
  npView = 'now';
  npSelectedDayIdx = 0;
  syncNpTabs();

  const found = findScheduleFetcher(station);
  if (!found) {
    syncNpTabs();
    // Populate the schedule column's empty-state (wide 4-col layout).
    renderProgramPane();
    return;
  }
  const ctrl = new AbortController();
  npScheduleAbort = ctrl;
  try {
    const days = await found.fetcher(found.station, ctrl.signal);
    if (ctrl.signal.aborted || npScheduleStationId !== station.id) return;
    npSchedule = days;
    if (days && days.length > 0) {
      // Default to whichever day contains "now" — usually today.
      const now = Date.now();
      const idx = days.findIndex((d) => d.broadcasts.some((b) => b.start <= now && now < b.end));
      npSelectedDayIdx = Math.max(0, idx);
    }
    syncNpTabs();
    // Render eagerly so the schedule column is populated in the wide
    // 4-column layout without the user tapping the program tab.
    renderProgramPane();
  } catch {
    /* silent — program panel just stays hidden */
  }
}

/** Look up lyrics for the current track. Cached by key in lyrics.ts;
 *  this fn just gates the request on whether we already asked for the
 *  same key, and aborts in-flight fetches when the track changes. */
function loadLyrics(artist: string, track: string): void {
  const key = `${artist.toLowerCase().trim()}::${track.toLowerCase().trim()}`;
  if (key === npLyricsKey) return;
  npLyricsAbort?.abort();
  const ctrl = new AbortController();
  npLyricsAbort = ctrl;
  npLyricsKey = key;
  npLyrics = undefined;
  syncNpTabs();
  void lookupLyrics(artist, track, ctrl.signal)
    .then((result) => {
      if (ctrl.signal.aborted || key !== npLyricsKey) return;
      npLyrics = result;
      syncNpTabs();
      // Render eagerly (not only when the lyrics tab is active) so the
      // lyrics column populates in the wide 4-column layout.
      renderLyricsPane();
    })
    .catch(() => {
      /* abort or network — silently leave the tab hidden */
    });
}

/** Reset lyrics state (called on station change, or when the live
 *  metadata fetcher reports "no track currently playing"). */
function resetLyrics(): void {
  npLyricsAbort?.abort();
  npLyricsAbort = undefined;
  npLyrics = undefined;
  npLyricsKey = '';
  if (npView === 'lyrics') npView = 'now';
  syncNpTabs();
  // Clear text + show the empty-state (wide 4-col lyrics column).
  renderLyricsPane();
}

/** Now Playing wide-desktop layout mode. 'narrow' is the tabbed single
 *  view (mobile, 1024–1400px desktop, and album-only stations). At ≥1400px
 *  with a Schedule or Lyrics pane it's 'twocol' (Album + a switchable
 *  Schedule/Lyrics column). The former 3-column mode is retired (#620). */
const wideNpMq = matchMedia('(min-width: 1400px)');
type NpLayout = 'narrow' | 'twocol';
function npLayoutMode(): NpLayout {
  if (!wideNpMq.matches || !currentNP.station.id) return 'narrow';
  // The wide NP is the golden split — album on the left, a switchable
  // Schedule/Lyrics column on the right (the pane-tab strip toggles which) —
  // whenever there's at least one secondary pane to show. Album-only
  // stations stay single-column (centred).
  const hasProgram = !!(npSchedule && npSchedule.length > 0);
  const hasLyrics = !!(npLyrics && (npLyrics.plain || npLyrics.synced));
  if (hasProgram || hasLyrics) return 'twocol';
  return 'narrow';
}

/** Synchronise the Now Playing tab pills + pane visibility with the
 *  three sources (track row, program guide, lyrics) and the layout mode.
 *  The tab pill for a given source only shows when that source has
 *  content; in the 2-column wide layout the strip doubles as the
 *  Schedule/Lyrics switcher (the 'now'/album pill is dropped — album is
 *  always its own column there). */
function syncNpTabs(): void {
  const hasProgram = !!(npSchedule && npSchedule.length > 0);
  const hasLyrics = !!(npLyrics && (npLyrics.plain || npLyrics.synced));
  const mode = npLayoutMode();

  // Layout body class drives the wide grid (CSS); cleared on narrow.
  $body.classList.toggle('np-twocol', mode === 'twocol');

  if (mode === 'twocol') {
    // Album owns column 1; the second column is Schedule OR Lyrics, so
    // npView is restricted to those. Fall back to whichever exists; if
    // neither, 'now' (→ the secondary empty-state shows).
    if (
      npView === 'now' ||
      (npView === 'program' && !hasProgram) ||
      (npView === 'lyrics' && !hasLyrics)
    ) {
      npView = hasProgram ? 'program' : hasLyrics ? 'lyrics' : 'now';
    }
  } else {
    // narrow: drop to 'now' if the active secondary is gone.
    if (npView === 'program' && !hasProgram) npView = 'now';
    if (npView === 'lyrics' && !hasLyrics) npView = 'now';
  }

  // Show the tab strip whenever at least one secondary source has content.
  // The 'now' pill is redundant in the 2-column layout (album is always
  // shown), so hide it there.
  $npPaneTabs.hidden = !hasProgram && !hasLyrics;
  $npPaneNow.hidden = mode === 'twocol';
  $npPaneProgram.hidden = !hasProgram;
  $npPaneLyrics.hidden = !hasLyrics;

  $npPaneNow.classList.toggle('is-active', npView === 'now');
  $npPaneNow.setAttribute('aria-pressed', String(npView === 'now'));
  $npPaneProgram.classList.toggle('is-active', npView === 'program');
  $npPaneProgram.setAttribute('aria-pressed', String(npView === 'program'));
  $npPaneLyrics.classList.toggle('is-active', npView === 'lyrics');
  $npPaneLyrics.setAttribute('aria-pressed', String(npView === 'lyrics'));

  // Pane [hidden] flags. The narrow docked view obeys them directly; the
  // wide 2-col grid overrides via CSS (always shows album, plus whichever
  // secondary matches npView). render-np
  // writes content into npTrackRow but never touches its `hidden`. Track
  // also stays hidden with no station so we don't show an em-dashed shell.
  $npTrackRow.hidden = npView !== 'now' || !currentNP.station.id;
  $npProgramPane.hidden = npView !== 'program';
  $npLyricsPane.hidden = npView !== 'lyrics';

  // 2-column with neither schedule nor lyrics → the second column shows a
  // small empty state (the only layout where it can surface).
  $npSecondaryEmpty.hidden = !(mode === 'twocol' && !hasProgram && !hasLyrics);
}

// Recompute the wide layout when the 1400px boundary is crossed.
wideNpMq.addEventListener('change', syncNpTabs);

// renderLyricsPane lives in ./render-np (refs-based). The local wrapper
// closes over the production refs + the current lyrics/track state so the
// rest of main.ts can call it with no args.
const NP_LYRICS_REFS: LyricsPaneRefs = {
  npLyricsText: $npLyricsText,
  npLyricsEmpty: $npLyricsEmpty,
  npLyricsHead: $npLyricsHead,
  npLyricsTitle: $npLyricsTitle,
  npLyricsArtist: $npLyricsArtist,
  npLyricsSource: $npLyricsSource,
  npLyricsSourceText: $npLyricsSourceText,
};

function renderLyricsPane(): void {
  renderLyricsPaneImpl(NP_LYRICS_REFS, npLyrics, currentNP);
}

/** Populate the iOS-parity schedule header: the current show name +
 *  subtitle (from the live now-playing metadata, not the schedule rows)
 *  and a "N broadcasts" count caption. The header collapses entirely
 *  when there's no current-show name, so the column doesn't carry a
 *  redundant label (the tab / column header already says "Schedule"). */
function renderProgramHead(broadcastCount: number): void {
  const programName = currentNP.programName?.trim() ?? '';
  const programSub = currentNP.programSubtitle?.trim() ?? '';
  $npProgramHeadName.textContent = programName;
  $npProgramHeadSub.textContent = programSub;
  $npProgramHeadSub.hidden = programSub.length === 0;
  $npProgramHead.hidden = programName.length === 0;
  $npProgramCount.textContent =
    broadcastCount === 1 ? '1 broadcast' : `${broadcastCount} broadcasts`;
  $npProgramMeta.hidden = broadcastCount === 0;
}

function renderProgramPane(): void {
  if (!npSchedule || npSchedule.length === 0) {
    // Don't touch hidden — that's syncNpTabs's job (which drops the
    // user back to 'now' when hasProgram becomes false). Touching it
    // here used to fight the tab-state and cause the same cover-bleed
    // bug we hit on the lyrics pane (gh #84).
    $npProgramList.replaceChildren();
    renderProgramHead(0);
    $npProgramHead.hidden = true;
    // Empty-state line — only ever visible in the wide 4-column layout
    // (the narrow docked view hides the whole pane when there's no
    // schedule, since the program tab doesn't appear).
    $npProgramEmpty.hidden = false;
    return;
  }
  $npProgramEmpty.hidden = true;
  // Today's broadcasts only — broadcaster APIs we hit only return
  // today + past, so a multi-day picker is dead weight.
  $npProgramList.replaceChildren();
  const day = npSchedule[npSelectedDayIdx];
  renderProgramHead(day.broadcasts.length);
  const now = Date.now();
  let liveRow: HTMLDivElement | null = null;
  let nextRow: HTMLDivElement | null = null;
  for (const b of day.broadcasts) {
    const isLive = b.start <= now && now < b.end;
    const isPast = b.end <= now;
    const row = document.createElement('div');
    row.className =
      'np-program-row' +
      (isLive ? ' is-live' : '') +
      (isPast ? ' is-past' : '');
    const time = document.createElement('div');
    time.className = 'np-program-row__time';
    time.textContent = new Date(b.start).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    const text = document.createElement('div');
    text.className = 'np-program-row__text';
    const title = document.createElement('div');
    title.className = 'np-program-row__title';
    title.textContent = b.title;
    text.append(title);
    if (b.subtitle) {
      const sub = document.createElement('div');
      sub.className = 'np-program-row__sub';
      sub.textContent = b.subtitle;
      text.append(sub);
    }
    row.append(time, text);
    // iOS parity: a LIVE capsule pinned to the right of the on-air row.
    if (isLive) {
      const badge = document.createElement('span');
      badge.className = 'np-program-row__live';
      badge.textContent = 'Live';
      row.append(badge);
    }
    $npProgramList.append(row);
    if (isLive && !liveRow) liveRow = row;
    else if (!isLive && !isPast && !nextRow) nextRow = row;
  }
  // Center the now-on-air row (or the next upcoming one if we hit a gap
  // between broadcasts) WITHIN the program pane only. We must not use
  // Element.scrollIntoView here: it scrolls every scrollable ancestor,
  // including the .app shell (overflow:hidden, but still scrollable
  // programmatically), which on desktop pushes the whole layout — and the
  // top nav / search bar — off the top of the viewport. Setting the pane's
  // own scrollTop keeps the scroll contained. Deferred a frame so layout
  // is settled before we measure.
  const target = liveRow ?? nextRow;
  if (target) {
    requestAnimationFrame(() => {
      const pane = $npProgramPane;
      const delta =
        target.getBoundingClientRect().top -
        pane.getBoundingClientRect().top -
        (pane.clientHeight - target.offsetHeight) / 2;
      pane.scrollTop += delta;
    });
  }
}

function setNpView(view: NpView): void {
  npView = view;
  syncNpTabs();
  if (npView === 'program') renderProgramPane();
  else if (npView === 'lyrics') renderLyricsPane();
}

$npPaneNow.addEventListener('click', () => setNpView('now'));
$npPaneProgram.addEventListener('click', () => setNpView('program'));
$npPaneLyrics.addEventListener('click', () => setNpView('lyrics'));

/** Available NP tabs in display order, filtered to those that have
 *  content right now. Always includes 'now'; program / lyrics appear
 *  only when their data sources have something to show. Used by
 *  swipe navigation to decide where each gesture lands. */
function availableNpViews(): NpView[] {
  const out: NpView[] = ['now'];
  if (npSchedule && npSchedule.length > 0) out.push('program');
  if (npLyrics && (npLyrics.plain || npLyrics.synced)) out.push('lyrics');
  return out;
}

/** Horizontal swipe on the Now Playing body navigates between the
 *  visible tabs. Threshold: at least 50px horizontal AND horizontal
 *  movement larger than vertical (so a finger that scrolls the lyrics
 *  pane vertically doesn't accidentally flip tabs). We listen passively
 *  via pointer events — no preventDefault, so vertical scroll inside
 *  panes keeps working. */
const SWIPE_THRESHOLD_PX = 50;
let swipeStartX = 0;
let swipeStartY = 0;
let swipeActivePointer: number | null = null;
const $npBody = document.querySelector('.np-body') as HTMLElement;

$npBody.addEventListener('pointerdown', (e) => {
  // Ignore right/middle clicks; touch + left mouse only.
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  swipeActivePointer = e.pointerId;
  swipeStartX = e.clientX;
  swipeStartY = e.clientY;
});

$npBody.addEventListener('pointerup', (e) => {
  if (swipeActivePointer !== e.pointerId) return;
  swipeActivePointer = null;
  const dx = e.clientX - swipeStartX;
  const dy = e.clientY - swipeStartY;
  if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
  if (Math.abs(dx) <= Math.abs(dy)) return;
  const tabs = availableNpViews();
  const idx = tabs.indexOf(npView);
  if (idx < 0) return;
  // Swipe left (dx<0) advances forward through tabs; right (dx>0) goes back.
  const next = dx < 0 ? idx + 1 : idx - 1;
  const clamped = Math.max(0, Math.min(tabs.length - 1, next));
  if (clamped === idx) return;
  setNpView(tabs[clamped]);
});

$npBody.addEventListener('pointercancel', () => {
  swipeActivePointer = null;
});

// Site visit counter (footer of Browse). Pulled from GoatCounter's
// public counter endpoint — no auth, edge-cached 30 min by GC. We
// fetch once per page load and remember the value for re-renders.
function renderRows(stations: Station[], opts: RowOptions = {}): DocumentFragment {
  const frag = document.createDocumentFragment();
  const favs = favIdSet();
  for (const s of stations)
    frag.append(buildRow(s, currentNP.station.id, currentNP.state, favs, opts));
  return frag;
}

/** Browse/Discovery row groups get wrapped in a `.rows` container so the
 *  desktop breakpoint can lay them out as a responsive card grid. A bare
 *  `<div>` on mobile (rows stack exactly as before). Favorites/Recents
 *  intentionally do NOT use this — their drag-reorder assumes a single
 *  vertical column of direct `.row` children. */
function rowsGrid(stations: Station[], opts: RowOptions = {}): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'rows';
  wrap.append(renderRows(stations, opts));
  return wrap;
}

/** Options shared by the column (grip) and grid (native DnD) reorderers:
 *  which direct children are reorderable, how to read each one's stable id,
 *  and where to persist the resulting order. */
interface ReorderOpts {
  itemSelector: string;
  idOf: (el: HTMLElement) => string;
  onCommit: (orderedIds: string[]) => void;
  /** Grip side for the column path; defaults to trailing (favorites idiom). */
  gripPosition?: 'append' | 'prepend';
}

/** Read the container's current child order and persist it. */
function commitReorder(container: HTMLElement, opts: ReorderOpts): void {
  const ids = Array.from(container.querySelectorAll<HTMLElement>(opts.itemSelector))
    .map(opts.idOf)
    .filter(Boolean);
  opts.onCommit(ids);
}

/** Pick the reorder mechanism by layout: a single-column (mobile) stack uses
 *  the grip + pointer drag; a wide grid uses native HTML5 drag-and-drop on
 *  the whole card (touch doesn't fire native DnD, so the two are exclusive).
 *  Used for Favorites, Library-home lists, and list-detail stations. */
function enableReorder(container: HTMLElement, opts: ReorderOpts): void {
  if (matchMedia('(min-width: 1024px)').matches) enableGridReorder(container, opts);
  else enableColumnReorder(container, opts);
}

/** Append a grip handle to each reorderable child of `container` and wire
 *  pointer-event drag-to-reorder. On drop, persist via opts.onCommit and
 *  don't re-render — the DOM order already matches. The caller only invokes
 *  this where reordering makes sense (no active search query, etc).
 *
 *  Drag mechanics:
 *    1. pointerdown snapshots all rows + their indices and the row
 *       height; the dragged row gets is-dragging (z-index lift).
 *    2. pointermove translates the dragged row by clientY-startY.
 *       The target index is computed as `originalIndex + round(dragY
 *       / rowHeight)`, and siblings between the original and target
 *       slots are translated +/- one row height to vacate the slot.
 *       No DOM mutation happens during drag — that avoids re-anchoring
 *       the pointer math after each swap.
 *    3. pointerup does a single atomic insertBefore to commit the
 *       new index, clears all transforms, and persists the order. */
function enableColumnReorder(container: HTMLElement, opts: ReorderOpts): void {
  const rows = Array.from(container.querySelectorAll<HTMLElement>(opts.itemSelector));
  if (rows.length < 2) return;

  for (const row of rows) {
    if (row.querySelector(':scope > .row-grip')) continue;
    const grip = document.createElement('button');
    grip.type = 'button';
    grip.className = 'row-grip';
    grip.setAttribute('aria-label', 'Drag to reorder');
    grip.innerHTML = ICON_GRIP;
    if (opts.gripPosition === 'prepend') row.prepend(grip);
    else row.append(grip);
    attachGripDrag(grip, row, container, opts);
  }
}

/** Reorder a wide card grid via native HTML5 drag-and-drop. The whole card is
 *  the drag source — no visible grip handle on desktop, so the cards stay
 *  clean (the mobile column path keeps its grip). The grid reflows live on
 *  dragover; dragend reads the settled order and persists it. */
function enableGridReorder(container: HTMLElement, opts: ReorderOpts): void {
  const items = Array.from(container.querySelectorAll<HTMLElement>(opts.itemSelector));
  if (items.length < 2) return;
  let dragged: HTMLElement | null = null;

  for (const item of items) {
    // `draggable` doubles as the already-wired marker (idempotent re-runs).
    if (item.draggable) continue;
    // The whole card is the drag source (most reliable across browsers).
    item.draggable = true;
    // Stop the browser grabbing an inner favicon/cover instead of the card.
    item.querySelectorAll('img').forEach((img) => (img.draggable = false));

    item.addEventListener('dragstart', (e) => {
      dragged = item;
      item.classList.add('is-drag-source');
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        // Some browsers won't start a drag without payload.
        try { e.dataTransfer.setData('text/plain', opts.idOf(item)); } catch { /* ignore */ }
      }
    });
    item.addEventListener('dragend', () => {
      item.classList.remove('is-drag-source');
      if (dragged) {
        dragged = null;
        commitReorder(container, opts);
      }
    });
    item.addEventListener('dragover', (e) => {
      if (!dragged || dragged === item) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      // Insert before the hovered card when the pointer is in its leading
      // half (reading order: x within a row), else after.
      const r = item.getBoundingClientRect();
      const after = e.clientX > r.left + r.width / 2;
      container.insertBefore(dragged, after ? item.nextSibling : item);
    });
  }
  // Keep the drop allowed while crossing the gaps between cards.
  container.addEventListener('dragover', (e) => {
    if (dragged) e.preventDefault();
  });
}

function attachGripDrag(
  grip: HTMLElement,
  row: HTMLElement,
  container: HTMLElement,
  opts: ReorderOpts,
): void {
  let pointerId: number | null = null;
  let startY = 0;
  let originalIndex = -1;
  let targetIndex = -1;
  let allRows: HTMLElement[] = [];
  let rowHeight = 0;

  const clearShiftClasses = (): void => {
    for (const r of allRows) r.classList.remove('is-shifting-up', 'is-shifting-down');
  };

  const onPointerMove = (ev: PointerEvent): void => {
    if (ev.pointerId !== pointerId) return;
    // On iOS Safari an upward touch move is otherwise interpreted as
    // page scroll, fires pointercancel mid-gesture, and the drop
    // commit never runs — so the row "floats up then snaps back".
    // preventDefault here keeps the browser from claiming the gesture.
    ev.preventDefault();
    const dragY = ev.clientY - startY;
    row.style.setProperty('--drag-y', `${dragY}px`);

    // round() so half a row's drag advances the target by one slot —
    // symmetric for both directions.
    const offset = rowHeight > 0 ? Math.round(dragY / rowHeight) : 0;
    const newTarget = Math.max(0, Math.min(allRows.length - 1, originalIndex + offset));
    if (newTarget === targetIndex) return;
    targetIndex = newTarget;

    // Translate siblings between the original and target slots so
    // the user sees a visible "gap" sliding to where the row will
    // land. Rows below the dragged row shift up; rows above shift
    // down. The CSS classes carry the transition so the motion is
    // animated rather than snapping.
    for (let i = 0; i < allRows.length; i++) {
      const r = allRows[i];
      if (r === row) continue;
      r.classList.remove('is-shifting-up', 'is-shifting-down');
      if (i > originalIndex && i <= targetIndex) r.classList.add('is-shifting-up');
      else if (i < originalIndex && i >= targetIndex) r.classList.add('is-shifting-down');
    }
  };

  const onPointerUp = (ev: PointerEvent): void => {
    if (ev.pointerId !== pointerId) return;
    grip.removeEventListener('pointermove', onPointerMove);
    grip.removeEventListener('pointerup', onPointerUp);
    grip.removeEventListener('pointercancel', onPointerUp);
    if (pointerId !== null) {
      try { grip.releasePointerCapture(pointerId); } catch {/* ignore */}
    }
    pointerId = null;

    // Single atomic reorder: place the row at its target slot. Use
    // the snapshot's row at targetIndex as the anchor (it hasn't been
    // mutated during the drag — only its transform was animated).
    if (targetIndex !== originalIndex) {
      if (targetIndex < originalIndex) {
        const anchor = allRows[targetIndex];
        container.insertBefore(row, anchor);
      } else {
        const anchor = allRows[targetIndex];
        container.insertBefore(row, anchor.nextElementSibling);
      }
    }
    clearShiftClasses();
    for (const r of allRows) r.style.removeProperty('--row-h');
    row.classList.remove('is-dragging');
    row.style.removeProperty('--drag-y');

    commitReorder(container, opts);
  };

  grip.addEventListener('pointerdown', (ev) => {
    // Left mouse / primary touch only; ignore right-click, middle,
    // and secondary pointers.
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    pointerId = ev.pointerId;
    startY = ev.clientY;
    allRows = Array.from(container.querySelectorAll<HTMLElement>(opts.itemSelector));
    originalIndex = allRows.indexOf(row);
    targetIndex = originalIndex;
    // Slot pitch = top-to-top distance to an adjacent row, which folds in
    // the inter-card gap now that feed rows are spaced cards (not the old
    // contiguous list). Falls back to the row's own height for a lone row.
    rowHeight = row.getBoundingClientRect().height;
    const below = allRows[originalIndex + 1];
    const above = allRows[originalIndex - 1];
    if (below) {
      rowHeight = below.getBoundingClientRect().top - row.getBoundingClientRect().top;
    } else if (above) {
      rowHeight = row.getBoundingClientRect().top - above.getBoundingClientRect().top;
    }
    // Siblings need the row height to know how far to shift. Set on
    // each non-dragged row so the .is-shifting-up/down rules resolve.
    for (const r of allRows) {
      if (r !== row) r.style.setProperty('--row-h', `${rowHeight}px`);
    }
    row.classList.add('is-dragging');
    row.style.setProperty('--drag-y', '0px');
    grip.setPointerCapture(ev.pointerId);
    grip.addEventListener('pointermove', onPointerMove);
    grip.addEventListener('pointerup', onPointerUp);
    grip.addEventListener('pointercancel', onPointerUp);
  });
  // Click would otherwise bubble to the row's onRowPlay handler — a
  // tap on the grip should never start playback.
  grip.addEventListener('click', (ev) => ev.stopPropagation());
}

// ─────────────────────────────────────────────────────────────
// Browse discovery landing + refinements (sort / quality / featured)
// ─────────────────────────────────────────────────────────────

/** Is the Browse tab showing its discovery landing? True only when
 *  nothing narrows the catalog: no query, genre, country, mode,
 *  curated-only, map, or Browse-all. */
/** True when any filter narrows the catalog (genres / countries / news /
 *  quality). Drives the local-catalog match path + the funnel dot. */
function hasActiveFilter(): boolean {
  return (
    filterGenres.size > 0 || filterCountries.size > 0 || filterNews || activeQuality.size > 0
  );
}

function inDiscovery(): boolean {
  return (
    activeTab === 'browse' &&
    !$search.value.trim() &&
    !hasActiveFilter() &&
    !browseAll
  );
}

/** Per-genre / per-country counts over the curated catalog, memoised
 *  until the catalog size changes. */
function getDiscoveryCounts(): DiscoveryCounts {
  // Before the full catalog lands, paint the chips from the precomputed
  // discovery summary (a few KB) so the landing is instant; once
  // BUILTIN_STATIONS has hydrated, compute from it (identical counts) and
  // memoise against its size.
  if (BUILTIN_STATIONS.length === 0) {
    const summary = getDiscoverySummary();
    if (summary) return summary.counts;
  }
  if (!discoveryCountsCache || discoveryCountsForLen !== BUILTIN_STATIONS.length) {
    discoveryCountsCache = discoveryCounts(BUILTIN_STATIONS);
    discoveryCountsForLen = BUILTIN_STATIONS.length;
  }
  return discoveryCountsCache;
}

function stationById(id: string): Station | undefined {
  return BUILTIN_STATIONS.find((s) => s.id === id);
}

/** Apply the local quality filter, then ordering. Quality always
 *  applies; ordering is skipped while a text query is active (relevance
 *  wins). Otherwise the alphabet sort wins, else featured-first for the
 *  un-queried catalog. */
function refine(
  list: Station[],
  opts: { textQuery: string; featuredFirst: boolean },
): Station[] {
  let out = list;
  if (activeQuality.size > 0) {
    out = out.filter((s) => activeQuality.has(stationQualityBucket(s)));
  }
  if (!opts.textQuery) {
    if (activeSort) out = sortStations(out, activeSort);
    else if (opts.featuredFirst) out = orderFeaturedFirst(out);
  }
  return out;
}

/** Does a station pass the active filter? Mirrors iOS
 *  `CatalogStationSearch.matchesBrowseFilters`: countries OR within the
 *  set, genres OR within the set, news + quality as extra ANDed gates.
 *  An empty category doesn't constrain. */
function matchesBrowseFilter(
  s: Station,
  genres: Set<string>,
  countries: Set<string>,
  news: boolean,
  quality: Set<QualityBucket>,
): boolean {
  if (countries.size > 0) {
    const code = (s.country ?? '').toUpperCase();
    if (!code || !countries.has(code)) return false;
  }
  if (genres.size > 0) {
    let ok = false;
    for (const id of genres) {
      const g = findGenre(id);
      if (g && stationMatchesGenre(s, g)) {
        ok = true;
        break;
      }
    }
    if (!ok) return false;
  }
  if (news) {
    const ng = findGenre('news');
    if (!ng || !stationMatchesGenre(s, ng)) return false;
  }
  if (quality.size > 0 && !quality.has(stationQualityBucket(s))) return false;
  return true;
}

/** Count of catalog stations the given filter selection matches — the
 *  live "Show N stations" figure on the filter sheet. */
function filterMatchCount(
  genres: Set<string>,
  countries: Set<string>,
  news: boolean,
  quality: Set<QualityBucket>,
): number {
  // An empty selection constrains nothing, so it matches the whole
  // catalog — mirrors iOS, where the accept button shows the full count
  // ("Show 17k stations") rather than zero on an untouched draft.
  let n = 0;
  for (const s of BUILTIN_STATIONS) {
    if (matchesBrowseFilter(s, genres, countries, news, quality)) n += 1;
  }
  return n;
}

/** Short human label for the active filter, used as the result section
 *  header (e.g. "Rock · News · Germany"). Falls back to "Results". */
function filterSummaryLabel(): string {
  const parts: string[] = [];
  for (const id of filterGenres) {
    const g = findGenre(id);
    if (g) parts.push(g.label);
  }
  if (filterNews) parts.push('News');
  for (const code of filterCountries) parts.push(countryName(code));
  return parts.length > 0 ? parts.join(' · ') : 'Results';
}

// ─── Discovery render ───

function discoverySection(title: string): HTMLDivElement {
  const h = document.createElement('div');
  h.className = 'disc-section-label';
  h.textContent = title;
  return h;
}

function discoveryChip(
  label: string,
  count: number,
  onPick: () => void,
  flag?: string,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'disc-chip';
  // Country chips lead with a flag glyph (iOS DiscoveryChip parity); genre
  // chips pass none. Decorative — the country name carries the label, so
  // the flag is aria-hidden and the aria-label stays "{name}, N stations".
  if (flag) {
    const f = document.createElement('span');
    f.className = 'disc-chip__flag';
    f.textContent = flag;
    f.setAttribute('aria-hidden', 'true');
    btn.append(f);
  }
  const name = document.createElement('span');
  name.className = 'disc-chip__name';
  name.textContent = label;
  const c = document.createElement('span');
  c.className = 'disc-chip__count';
  c.textContent = abbreviateCount(count);
  btn.append(name, c);
  btn.setAttribute('aria-label', `${label}, ${count} stations`);
  btn.addEventListener('click', onPick);
  return btn;
}

function featuredCard(item: ResolvedHighlight): HTMLButtonElement {
  const card = document.createElement('button');
  card.type = 'button';
  card.className = 'feat-card';
  card.dataset.id = item.station.id;
  // Per-highlight editorial accent. Mirrors the current iOS HighlightCard,
  // where the accent survives only as a soft tint on the badge dot (no left
  // stripe, no play button); falls back to the app accent.
  if (item.badge?.accent) card.style.setProperty('--feat-accent', item.badge.accent);
  const art = buildFavicon(item.station, 72);
  art.classList.add('feat-card__art');
  const body = document.createElement('div');
  body.className = 'feat-card__body';
  if (item.badge?.label) {
    const badge = document.createElement('div');
    badge.className = 'feat-card__badge';
    badge.textContent = item.badge.label;
    body.append(badge);
  }
  const name = document.createElement('div');
  name.className = 'feat-card__name';
  name.textContent = item.station.name;
  body.append(name);
  if (item.blurb) {
    const blurb = document.createElement('div');
    blurb.className = 'feat-card__blurb';
    blurb.textContent = item.blurb;
    body.append(blurb);
  }
  // The whole card is the play affordance (tapping anywhere plays), matching
  // iOS — no separate play button.
  card.append(art, body);
  card.addEventListener('click', () => onRowPlay(item.station));
  return card;
}

/** Let a horizontal scroller respond to a vertical mouse wheel, so
 *  desktop/mouse users can scroll the featured rail without a trackpad
 *  swipe. No-op when the gesture is already horizontal or the rail is at
 *  an edge — there the event bubbles so the page keeps scrolling. */
function enableWheelScroll(el: HTMLElement): void {
  el.addEventListener(
    'wheel',
    (e) => {
      if (e.deltaY === 0 || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      if ((e.deltaY < 0 && el.scrollLeft <= 0) || (e.deltaY > 0 && el.scrollLeft >= max)) return;
      el.scrollLeft = Math.max(0, Math.min(max, el.scrollLeft + e.deltaY));
      e.preventDefault();
    },
    { passive: false },
  );
}

function renderDiscovery(): void {
  const featured = resolveHighlights(
    highlightsRaw,
    stationById,
    todayISO(),
    DISCOVERY_HIGHLIGHT_LIMIT,
  );

  // Section order mirrors the iOS BrowseDiscoveryView: genre chips,
  // country chips, the "Browse all" header + logo rail, then the
  // Featured carousel at the bottom. Each chip row is a single
  // horizontally-scrolling line (not a wrapping grid) to match iOS.
  const counts = getDiscoveryCounts();
  const gChips = genreChips(counts);
  if (gChips.length > 0) {
    // The genre header carries the compact "Surprise me" control (#97) —
    // discovery's one-tap "just play something" next to the chips.
    const head = document.createElement('div');
    head.className = 'disc-section-head';
    head.append(discoverySection('Browse by genre'), surpriseButton('disc-surprise', true));
    $content.append(head);
    const row = document.createElement('div');
    row.className = 'disc-chips';
    for (const c of gChips) row.append(discoveryChip(c.label, c.count, () => selectGenreChip(c.id)));
    enableWheelScroll(row);
    $content.append(row);
  }
  const cChips = countryChips(counts, countryName);
  if (cChips.length > 0) {
    $content.append(discoverySection('Browse by country'));
    const row = document.createElement('div');
    row.className = 'disc-chips';
    for (const c of cChips)
      row.append(discoveryChip(c.label, c.count, () => selectCountryChip(c.id), flagEmoji(c.id)));
    enableWheelScroll(row);
    $content.append(row);
  }

  $content.append(browseAllSection(featured));

  if (featured.length > 0) {
    $content.append(discoverySection('Featured'));
    const rail = document.createElement('div');
    rail.className = 'feat-rail';
    for (const f of featured) rail.append(featuredCard(f));
    enableWheelScroll(rail);
    $content.append(rail);
  }
}

// ─── Surprise me (#97) ───

const SURPRISE_RECENT_KEY = 'rrradio.surprise-recent';

/** Shuffle control. `labelled` renders the text label next to the icon
 *  (discovery landing); the results row uses the icon alone. */
function surpriseButton(className: string, labelled: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = className;
  const filtered = hasActiveFilter();
  btn.setAttribute(
    'aria-label',
    filtered ? 'Surprise me: play a random station matching the filter' : 'Surprise me: play a random station',
  );
  btn.title = filtered ? 'Random station from this filter' : 'Play a random station';
  btn.innerHTML = ICON_SHUFFLE;
  if (labelled) {
    const lbl = document.createElement('span');
    lbl.textContent = 'Surprise me';
    btn.append(lbl);
  }
  btn.addEventListener('click', () => void surpriseMe());
  return btn;
}

/** Play a random published station, inside the active Browse filter when one
 *  is set. Plays through onRowPlay so recents, analytics, and the URL behave
 *  exactly like a row tap. */
async function surpriseMe(): Promise<void> {
  // The catalog normally hydrated long before the tap; only a very fast tap
  // on a cold load waits for it. The exclusion list is preloaded at boot —
  // if it hasn't landed yet the pick just skips nothing extra.
  const list = BUILTIN_STATIONS.length > 0 ? BUILTIN_STATIONS : await loadBuiltinStations();
  void loadSurpriseExclude();
  const filtered = hasActiveFilter();
  const recent = parseRecentPicks(getString(SURPRISE_RECENT_KEY));
  const pick = pickSurprise(list, {
    matches: filtered
      ? (s) => matchesBrowseFilter(s, filterGenres, filterCountries, filterNews, activeQuality)
      : undefined,
    exclude: getSurpriseExclude(),
    avoid: [currentNP.station.id, ...recent],
  });
  if (!pick) return;
  setString(SURPRISE_RECENT_KEY, JSON.stringify(rememberPick(recent, pick.id)));
  track(filtered ? 'surprise/filtered' : 'surprise');
  onRowPlay(pick);
}

/** "Browse all" — a full-width tappable header row plus a horizontal
 *  logo rail (iOS BrowseDiscoveryView.browseAllSection). Tapping the
 *  header or any logo drops into the full catalog list. The rail shows
 *  up to DISCOVERY_BROWSE_ALL_LOGO_LIMIT featured-first stations that
 *  carry real artwork. */
function browseAllSection(featured: ResolvedHighlight[]): DocumentFragment {
  const frag = document.createDocumentFragment();

  const header = document.createElement('button');
  header.type = 'button';
  header.className = 'disc-browse-all';
  header.setAttribute('aria-label', 'Browse all stations');
  const lbl = document.createElement('span');
  lbl.className = 'disc-browse-all__label';
  lbl.textContent = 'Browse all stations';
  const cnt = document.createElement('span');
  cnt.className = 'disc-browse-all__count';
  // Falls back to the summary's total until the full catalog hydrates.
  cnt.textContent = abbreviateCount(BUILTIN_STATIONS.length || getDiscoverySummary()?.total || 0);
  header.append(lbl, cnt);
  header.addEventListener('click', enterBrowseAll);
  frag.append(header);

  // Prefer the featured stations (recognisable artwork) up front, then
  // fill from the featured-first catalog ordering. Dedupe by id and keep
  // only stations that carry a favicon.
  const seen = new Set<string>();
  const pool: Station[] = [];
  for (const f of featured) {
    if (f.station.favicon && !seen.has(f.station.id)) {
      seen.add(f.station.id);
      pool.push(f.station);
    }
  }
  for (const s of orderFeaturedFirst(BUILTIN_STATIONS)) {
    if (pool.length >= DISCOVERY_BROWSE_ALL_LOGO_LIMIT) break;
    if (s.favicon && !seen.has(s.id)) {
      seen.add(s.id);
      pool.push(s);
    }
  }

  if (pool.length > 0) {
    const rail = document.createElement('div');
    rail.className = 'disc-browse-all-rail';
    rail.setAttribute('aria-hidden', 'true');
    for (const s of pool.slice(0, DISCOVERY_BROWSE_ALL_LOGO_LIMIT)) {
      const item = document.createElement('button');
      item.type = 'button';
      item.tabIndex = -1;
      item.className = 'disc-browse-all-logo';
      const fav = buildFavicon(s, 38);
      // The rail can carry ~100 logos; defer off-screen fetches.
      const img = fav.querySelector('img');
      if (img) {
        img.loading = 'lazy';
        img.decoding = 'async';
      }
      item.append(fav);
      item.addEventListener('click', enterBrowseAll);
      rail.append(item);
    }
    enableWheelScroll(rail);
    frag.append(rail);
  }
  return frag;
}

/** Tapping a discovery genre chip is a one-tap shortcut for the Genre
 *  filter: it replaces the selection with just this genre (mirrors iOS,
 *  where the chip pre-selects that section). */
function selectGenreChip(id: string): void {
  filterGenres.clear();
  filterGenres.add(id);
  filterCountries.clear();
  filterNews = false;
  browseAll = false;
  syncFilterDot();
  track(`discovery/genre/${id}`);
  void runQuery();
}

function selectCountryChip(code: string): void {
  filterCountries.clear();
  filterCountries.add(code.toUpperCase());
  filterGenres.clear();
  filterNews = false;
  browseAll = false;
  syncFilterDot();
  track(`discovery/country/${code}`);
  void runQuery();
}

function enterBrowseAll(): void {
  browseAll = true;
  track('discovery/browse-all');
  void runQuery();
}

/** Clear every Browse narrowing and return to the discovery landing. */
function resetToDiscovery(): void {
  clearSearch(false);
  filterGenres.clear();
  filterCountries.clear();
  filterNews = false;
  activeQuality.clear();
  browseAll = false;
  activeSort = null;
  syncFilterDot();
  void runQuery();
}

/** Results header row above the list (mirrors iOS BrowseSortRow): a
 *  back-to-discovery chevron, the alphabet sort toggle (off → A–Z → Z–A),
 *  and the match count centered. Sort is suppressed while a text query is
 *  active (relevance order wins). When `label` is given (the active filter
 *  summary, e.g. a country) it prefixes the count so this single row carries
 *  the context — no separate section label is needed below it. */
function resultsRow(count: number, label?: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'results-row';

  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'results-row__back';
  back.setAttribute('aria-label', 'Back to Browse');
  back.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>';
  back.addEventListener('click', resetToDiscovery);

  const queryActive = $search.value.trim().length > 0;
  const sort = document.createElement('button');
  sort.type = 'button';
  sort.className = 'results-row__sort';
  sort.classList.toggle('is-active', activeSort !== null && !queryActive);
  sort.disabled = queryActive;
  sort.dataset.sort = activeSort ?? 'off';
  sort.setAttribute(
    'aria-label',
    activeSort === 'az' ? 'Sort Z to A' : activeSort === 'za' ? 'Clear sort' : 'Sort A to Z',
  );
  sort.innerHTML =
    activeSort === 'az'
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14"/><path d="m6 11 6 6 6-6"/></svg>'
      : activeSort === 'za'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5"/><path d="m6 11 6-6 6 6"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 3v18"/><path d="m4 7 4-4 4 4"/><path d="M16 21V3"/><path d="m12 17 4 4 4-4"/></svg>';
  sort.addEventListener('click', () => {
    if (sort.disabled) return;
    activeSort = cycleSort(activeSort);
    track(`sort/${activeSort ?? 'off'}`);
    void runQuery();
  });

  const countEl = document.createElement('span');
  countEl.className = 'results-row__count';
  const n = count.toLocaleString('en-US');
  countEl.textContent = label ? `${label} · ${n}` : n;

  row.append(back, sort, countEl);
  // Shuffle within the current filter (#97) — only for the un-queried
  // catalog/filter views, where "random from this set" is meaningful.
  if (!queryActive) row.append(surpriseButton('results-row__surprise', false));
  return row;
}

function renderContent(): void {
  $content.replaceChildren();
  // The /ios promo banner shows only on the Browse discovery landing; resync
  // it here so Browse-all, search, filters, and back-to-discovery (none of
  // which fire setTab) flip it correctly.
  syncPromo();
  // Recomputed below per view; a full re-render invalidates the prior loader.
  pendingLoadMore = null;
  // Stop the cover poll by default; the library feeds (Favorites / Lists /
  // Recents) re-arm it with their visible rows via armFavCovers() below.
  favCovers.stop();

  // View-signature reset for the local-catalog cap. Same view across
  // calls = persist the user's "Show more" clicks; new view = reset.
  const sig = `${activeTab}|${[...filterGenres].sort().join('+')}|${[...filterCountries].sort().join('+')}|${filterNews}|${$search.value.trim()}|${browseAll}|${activeSort}|${[...activeQuality].sort().join(',')}`;
  if (sig !== lastViewSig) {
    homeViewLimit = HOME_VIEW_PAGE_SIZE;
    autoFillTries = 0;
    lastViewSig = sig;
  }

  if (activeTab === 'browse') {
    const query = $search.value.trim();
    const filtered = hasActiveFilter();
    syncFilterDot();

    // Discovery landing is the default unfiltered Browse view; anything
    // that narrows the catalog (a filter, Browse-all, or a search) drops
    // into a result list with the results row (back + sort + count).
    const onDiscovery = inDiscovery();
    if (onDiscovery) {
      renderDiscovery();
      return;
    }

    // ── Local-catalog filter (genre / country / news / quality) ──
    // Matched directly against the catalog — no Radio Browser — so this
    // count equals the sheet's "Show N stations" (iOS parity).
    if (filtered && !query) {
      // A filter can be active before the catalog hydrates (a `?genre=` deep
      // link); show the tuning line instead of a false "No stations match".
      // loadBuiltinStations().then re-renders once it lands.
      if (BUILTIN_STATIONS.length === 0) {
        $content.append(statusLine('Tuning in…'));
        return;
      }
      const matched = BUILTIN_STATIONS.filter((s) =>
        matchesBrowseFilter(s, filterGenres, filterCountries, filterNews, activeQuality),
      );
      const ordered = activeSort
        ? sortStations(matched, activeSort)
        : orderFeaturedFirst(matched);
      $content.append(resultsRow(ordered.length, filterSummaryLabel()));
      if (ordered.length === 0) {
        $content.append(emptyState(ICON_EMPTY, 'No stations match', 'Try removing a filter'));
        return;
      }
      const visible = ordered.slice(0, homeViewLimit);
      $content.append(rowsGrid(visible, { queue: { kind: 'results', stations: ordered } }));
      const remaining = ordered.length - visible.length;
      if (remaining > 0) {
        $content.append(homeShowMoreButton(remaining));
        pendingLoadMore = (): void => {
          homeViewLimit += HOME_VIEW_PAGE_SIZE;
          renderContent();
        };
      }
      return;
    }

    // ── Text search (Radio-Browser-backed) ──
    // Built-ins + custom matches first ("My stations"), then the RB
    // long-tail ("Results"). Sort is suppressed (relevance wins).
    if (query) {
      const mySource = [...BUILTIN_STATIONS, ...getCustom()];
      const myFiltered = refine(filterStations(mySource, query), {
        textQuery: query,
        featuredFirst: true,
      });
      const results = refine(lastBrowseStations, { textQuery: query, featuredFirst: false });
      $content.append(resultsRow(myFiltered.length + results.length));
      const searchQueue: QueueSource = { kind: 'results', stations: [...myFiltered, ...results] };
      if (myFiltered.length > 0) {
        $content.append(sectionLabel('My stations', myFiltered.length));
        const visibleMy = myFiltered.slice(0, homeViewLimit);
        $content.append(rowsGrid(visibleMy, { queue: searchQueue }));
        const remainingMy = myFiltered.length - visibleMy.length;
        if (remainingMy > 0) $content.append(homeShowMoreButton(remainingMy));
      }
      if (results.length > 0) {
        $content.append(sectionLabel('Results', results.length));
        $content.append(rowsGrid(results, { queue: searchQueue }));
        if (browseHasMore) {
          $content.append(loadMoreButton());
          pendingLoadMore = (): void => void loadMore();
        }
      } else if (myFiltered.length === 0) {
        $content.append(
          emptyState(ICON_EMPTY, 'No stations match', 'Try a different search'),
        );
      }
      maybeAutoFill();
      return;
    }

    // ── Browse all (unfiltered, no query) — the whole published catalog ──
    // Featured first, then the catalog order (or the user's A–Z / Z–A
    // sort); paged in place, no Radio Browser rows. Custom stations the
    // user added ride along like they do in search.
    const all = refine([...BUILTIN_STATIONS, ...getCustom()], { textQuery: '', featuredFirst: true });
    $content.append(resultsRow(all.length, 'All stations'));
    if (all.length > 0) {
      const visibleAll = all.slice(0, homeViewLimit);
      $content.append(rowsGrid(visibleAll, { queue: { kind: 'results', stations: all } }));
      const remainingAll = all.length - visibleAll.length;
      if (remainingAll > 0) {
        $content.append(homeShowMoreButton(remainingAll));
        pendingLoadMore = (): void => {
          homeViewLimit += HOME_VIEW_PAGE_SIZE;
          renderContent();
        };
      }
    }
    maybeAutoFill();
    return;
  }

  const query = $search.value.trim();

  if (activeTab === 'library') {
    renderLibraryHome(query);
    return;
  }

  if (activeTab === 'fav') {
    const all = getFavorites();
    const list = filterStations(all, query);
    const label = query ? 'Results' : 'Favorites';
    const wide = matchMedia('(min-width: 1024px)').matches;
    // Header chrome only on the unfiltered list (it acts on the full stored
    // set, not a search subset). Both breakpoints mirror the iOS
    // LibraryPageStatusBar +/trash actions; mobile adds the search-icon
    // accessory (left), while desktop keeps search in the top bar (and
    // backup/restore in Settings).
    if (query) {
      $content.append(sectionLabel(label, list.length));
    } else if (wide) {
      $content.append(sectionLabel(label, list.length, favoriteHeaderActions()));
    } else {
      $content.append(
        sectionLabel(label, list.length, favoriteHeaderActions(), headerSearchLead('Search favorites')),
      );
    }
    if (all.length === 0) {
      favEditing = false;
      $content.append(
        emptyState(ICON_FAV, 'No favorites yet', 'Tap the heart on any station to save it here'),
      );
    } else if (list.length === 0) {
      $content.append(
        emptyState(ICON_EMPTY, 'No matches', 'Nothing in your favorites matches that search'),
      );
    } else {
      // Desktop lays favorites out as a card grid (iOS landscape tile view);
      // mobile keeps the single-column list. rowsGrid wraps in `.rows`, which
      // is a plain vertical stack on mobile and a card grid at ≥1024px.
      const grid = rowsGrid(list, { cover: true, queue: { kind: 'favorites' } });
      $content.append(grid);
      armFavCovers(list);
      // Unfiltered list: edit mode reveals a per-row remove affordance (iOS
      // delete mode) on both layouts. Otherwise the cards are reorderable —
      // grip drag on the mobile column, native drag-and-drop on the desktop
      // grid (enableReorder picks per layout).
      if (!query) {
        if (favEditing) decorateFavoriteRemoval(grid, list);
        else
          enableReorder(grid, {
            itemSelector: ':scope > .row',
            idOf: (el) => el.dataset.id ?? '',
            onCommit: reorderFavorites,
          });
      }
    }
    return;
  }

  if (activeTab === 'recent') {
    const all = getRecents();
    const list = filterStations(all, query);
    const label = query ? 'Results' : 'Recently played';
    // Recents is a Library sub-view — offer a back affordance to the home.
    const back = listActionBtn(ICON_BACK, 'Back to library', () => setTab('library'));
    back.classList.add('section-label__back');
    // Mobile gets the [back · search] lead (the brand-row field is hidden on
    // the Library tab); desktop keeps the back-prepended header.
    const wide = matchMedia('(min-width: 1024px)').matches;
    const recLabel = wide
      ? sectionLabel(label, list.length)
      : sectionLabel(label, list.length, undefined, [back, headerSearchLead('Search recently played')]);
    recLabel.classList.add('section-label--list-detail');
    if (wide) recLabel.prepend(back);
    $content.append(recLabel);
    if (all.length === 0) {
      $content.append(
        emptyState(ICON_RECENT, 'No history yet', 'Stations you play will show up here'),
      );
    } else if (list.length === 0) {
      $content.append(
        emptyState(ICON_EMPTY, 'No matches', 'Nothing in your history matches that search'),
      );
    } else {
      // Recents matches the Favorites layout: iOS-style cards on desktop
      // (single-column stack on mobile) with now-playing cover + track.
      $content.append(rowsGrid(list, { cover: true, queue: { kind: 'recents', stations: list } }));
      armFavCovers(list);
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Render — Lists (named station lists, gh #520)
// ─────────────────────────────────────────────────────────────

/** Small icon button for a section-label action slot (new/rename/delete). */
function listActionBtn(icon: string, label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'section-label__action';
  btn.setAttribute('aria-label', label);
  btn.title = label;
  btn.innerHTML = icon;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    onClick();
  });
  return btn;
}

/** Clear the transient list-management UI flags. Called on navigation so
 *  a half-typed create/rename or an open delete-confirm never lingers. */
function resetListUiState(): void {
  listCreateOpen = false;
  listRenameOpen = false;
  listDeleteConfirmId = null;
  listEditing = false;
}

/** Inline "name your list" form (input + confirm + cancel) — the in-app
 *  replacement for window.prompt, used for both create and rename. Enter
 *  submits, Esc cancels (stopping propagation so it doesn't also close an
 *  enclosing sheet); a blank name is ignored. */
function buildListNameForm(opts: {
  initial?: string;
  placeholder: string;
  submitLabel: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}): HTMLFormElement {
  const form = document.createElement('form');
  form.className = 'list-name-form';
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'list-name-form__input';
  input.placeholder = opts.placeholder;
  input.value = opts.initial ?? '';
  input.autocomplete = 'off';
  input.maxLength = 60;
  input.setAttribute('aria-label', opts.placeholder);
  const save = document.createElement('button');
  save.type = 'submit';
  save.className = 'list-name-form__save';
  save.textContent = opts.submitLabel;
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'list-name-form__cancel';
  cancel.textContent = 'Cancel';
  form.append(input, save, cancel);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const name = input.value.trim();
    if (!name) {
      input.focus();
      return;
    }
    opts.onSubmit(name);
  });
  cancel.addEventListener('click', (e) => {
    e.preventDefault();
    opts.onCancel();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      opts.onCancel();
    }
  });
  return form;
}

/** Inline two-step delete confirm ("Delete list? Cancel / Delete") — the
 *  in-app replacement for window.confirm. Used in the lists index row and
 *  the detail header. Button clicks stop propagation so a surrounding
 *  clickable row doesn't also fire. */
function buildDeleteConfirm(onConfirm: () => void, onCancel: () => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'list-confirm';
  const q = document.createElement('span');
  q.className = 'list-confirm__q';
  q.textContent = 'Delete list?';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'list-confirm__cancel';
  cancel.textContent = 'Cancel';
  cancel.addEventListener('click', (e) => {
    e.stopPropagation();
    onCancel();
  });
  const del = document.createElement('button');
  del.type = 'button';
  del.className = 'list-confirm__delete';
  del.textContent = 'Delete';
  del.addEventListener('click', (e) => {
    e.stopPropagation();
    onConfirm();
  });
  wrap.append(q, cancel, del);
  return wrap;
}

/** The Library home: the user's lists + a pinned Recents entry (matching
 *  iOS, whose Library home renders lists + Recents and excludes Favorites,
 *  which is its own tab). A specific list open → its detail view. */
function renderLibraryHome(query: string): void {
  // Detail view — a specific list is open.
  if (openListId) {
    const list = getList(openListId);
    if (list) {
      renderListDetail(list, query);
      return;
    }
    // The open list was deleted out from under us — fall back to the home.
    openListId = null;
  }
  renderLibraryIndex(query);
}

function renderLibraryIndex(query: string): void {
  const all = getLists();
  const q = query.toLowerCase();
  const lists = q ? all.filter((l) => l.name.toLowerCase().includes(q)) : all;
  // No lists left to manage → drop out of edit mode.
  if (all.length === 0) libEditing = false;
  // New list → the two-step add-list popup (iOS parity): name it (step 1),
  // then pick stations on Browse (step 2). The list is created on save.
  const newBtn = listActionBtn(ICON_PLUS, 'New list', () =>
    openAddList({ mode: 'pickStations' }),
  );
  // Trash toggles a per-row delete affordance (iOS edit mode), mirroring the
  // Favorites header — the delete control lives at the top, not on every
  // card. Only shown once there's a list to remove.
  const actions: HTMLElement[] = [newBtn];
  if (all.length > 0) {
    const editBtn = listActionBtn(
      ICON_TRASH,
      libEditing ? 'Done editing lists' : 'Edit lists',
      toggleLibEditing,
    );
    if (libEditing) editBtn.classList.add('is-active');
    actions.push(editBtn);
  }
  // Mobile mirrors the iOS LibraryPageStatusBar: search icon (left) ·
  // "Library" · + new-list (right). Desktop keeps the existing header (its
  // brand-row search field stays, so no header search icon).
  const wide = matchMedia('(min-width: 1024px)').matches;
  const lead = wide ? undefined : headerSearchLead('Search lists');
  $content.append(sectionLabel('Library', lists.length, actions, lead));

  // A search query scopes to list names; if none match, say so (the
  // Recents entry is hidden while filtering).
  if (q && lists.length === 0) {
    $content.append(emptyState(ICON_EMPTY, 'No matches', 'No list name matches that search'));
    return;
  }

  const wrap = document.createElement('div');
  wrap.className = 'lists-index';
  for (const l of lists) wrap.append(buildListIndexRow(l));
  // Recents is a system entry pinned at the tail of the Library home
  // (matching iOS); shown only on the unfiltered home.
  if (!q) wrap.append(buildRecentsRow());
  $content.append(wrap);
  // Settle each favicon strip now that the rows have a measured width,
  // and keep them in sync as the column width changes.
  bindStripResize();
  wrap.querySelectorAll<HTMLElement>('.list-item__strip').forEach(fitStrip);
  syncLibraryCardsPlaying();
  // Reorder the user's lists — the pinned Recents row (.list-item--system) is
  // excluded, so it stays at the tail. Only on the unfiltered home with no
  // create / delete-confirm row in the way. Grip drag on mobile, DnD on
  // desktop; a leading grip keeps clear of the trailing trash/chevron.
  if (!q && !listCreateOpen && listDeleteConfirmId === null) {
    enableReorder(wrap, {
      itemSelector: ':scope > .list-item--lib:not(.list-item--system)',
      idOf: (el) => el.dataset.listId ?? '',
      onCommit: reorderLists,
    });
  }
}

// Favicon-strip sizing for the Library-home rows — mirrors iOS, whose
// cards show a row of station favicons under the title. 36px icons, 6px
// gaps; the trailing "+N more" badge gets ~72px reserved. The visible
// count is recomputed from the row's measured width, so a narrow phone
// column sheds icons into "+N more" — but never shows more than the cap.
const STRIP_ICON = 36;
const STRIP_GAP = 6;
const STRIP_MORE_RESERVE = 72;
// iOS parity (StationListIconStrip.maxIcons): show at most 5 favicons, then
// collapse the rest into "+N more". This is a hard cap on every width — even
// a wide card stops at 5. It doubles as the DOM-build cap (no point building
// icons fitStrip will never reveal).
const MAX_STRIP_ICONS = 5;

/** Build the horizontal favicon strip shown under a Library-home row's
 *  title. Empty lists show a faint hint instead. The visible-icon count
 *  is settled later by fitStrip, once the row has a measured width. */
function buildIconStrip(stations: Station[], emptyHint: string): HTMLElement {
  const strip = document.createElement('div');
  strip.className = 'list-item__strip';
  if (stations.length === 0) {
    const hint = document.createElement('div');
    hint.className = 'list-item__empty';
    hint.textContent = emptyHint;
    strip.append(hint);
    return strip;
  }
  strip.dataset.total = String(stations.length);
  for (const st of stations.slice(0, MAX_STRIP_ICONS)) {
    const ico = buildFavicon(st, STRIP_ICON);
    ico.classList.add('strip-ico');
    strip.append(ico);
  }
  return strip;
}

/** Settle how many favicons a strip shows from its measured width: show
 *  every station when they all fit (and were all built), otherwise fill
 *  the row and collapse the rest into a trailing "+N more" badge. Reading
 *  clientWidth forces layout, so callers run this synchronously right
 *  after the row lands in the DOM. */
function fitStrip(strip: HTMLElement): void {
  const total = Number(strip.dataset.total ?? '0');
  if (!total) return;
  const avail = strip.clientWidth;
  if (!avail) return;
  const icons = Array.from(strip.querySelectorAll<HTMLElement>('.strip-ico'));
  const built = icons.length;
  const per = STRIP_ICON + STRIP_GAP;
  const fitPlain = Math.floor((avail + STRIP_GAP) / per);

  let show: number;
  let more: number;
  // iOS caps at MAX_STRIP_ICONS on every width, so the "show them all" path
  // only applies to lists at or under the cap (built is already capped too).
  if (built >= total && fitPlain >= total && total <= MAX_STRIP_ICONS) {
    show = total;
    more = 0;
  } else {
    const fitBadged = Math.max(1, Math.floor((avail - STRIP_MORE_RESERVE + STRIP_GAP) / per));
    show = Math.min(built, fitBadged, MAX_STRIP_ICONS);
    more = total - show;
  }

  icons.forEach((el, i) => {
    el.style.display = i < show ? '' : 'none';
  });
  strip.querySelector('.list-item__more')?.remove();
  if (more > 0) {
    const badge = document.createElement('span');
    badge.className = 'list-item__more';
    badge.textContent = `+${more} more`;
    strip.append(badge);
  }
}

// Re-fit every visible Library-home strip when the viewport changes
// width. Bound once, lazily, the first time the index renders.
let stripResizeBound = false;
function bindStripResize(): void {
  if (stripResizeBound) return;
  stripResizeBound = true;
  let pending = 0;
  window.addEventListener('resize', () => {
    if (pending) return;
    pending = requestAnimationFrame(() => {
      pending = 0;
      document.querySelectorAll<HTMLElement>('.list-item__strip').forEach(fitStrip);
    });
  });
}

/** The pinned Recents entry on the Library home — a non-removable system
 *  row that opens the Recents sub-view. */
function buildRecentsRow(): HTMLElement {
  const recents = getRecents();
  const row = document.createElement('div');
  row.className = 'list-item list-item--lib list-item--system';
  row.setAttribute('role', 'button');
  row.tabIndex = 0;

  const info = document.createElement('div');
  info.className = 'list-item__info';
  const name = document.createElement('div');
  name.className = 'list-item__name';
  name.textContent = 'Recents';
  info.append(name, buildIconStrip(recents, 'Stations appear here after you play them.'));

  row.append(info);
  const open = () => setTab('recent');
  row.addEventListener('click', open);
  row.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  });
  return row;
}

/** Library-home card play control (iOS LibraryHomeCardContent parity):
 *  plays the list's first station queued against the list. While this list
 *  is the active queue it shows the now-playing equalizer and toggles
 *  play/pause instead. Disabled for an empty list. */
function buildListPlayBtn(list: StationList): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'list-item__play';
  btn.dataset.listId = list.id;
  btn.innerHTML = ICON_PLAY;
  btn.append(buildEq(false));
  btn.disabled = list.stations.length === 0;
  btn.setAttribute('aria-label', `Play ${list.name}`);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const first = list.stations[0];
    if (!first) return;
    if (isListQueue(activeQueue, list.id) && list.stations.some((s) => s.id === currentNP.station.id)) {
      handlePlayToggle();
      return;
    }
    onRowPlay(first, { kind: 'list', listId: list.id });
  });
  return btn;
}

/** Reflect the active queue on the Library-home card play controls. */
function syncLibraryCardsPlaying(): void {
  const id = currentNP.station.id;
  $content.querySelectorAll<HTMLButtonElement>('.list-item__play').forEach((btn) => {
    const listId = btn.dataset.listId ?? '';
    const active = !!id && isListQueue(activeQueue, listId);
    btn.classList.toggle('is-active', active);
    btn.querySelector('.eq')?.classList.toggle('paused', currentNP.state !== 'playing');
    const name = btn.getAttribute('aria-label')?.replace(/^(Play|Pause) /, '') ?? '';
    btn.setAttribute(
      'aria-label',
      `${active && currentNP.state === 'playing' ? 'Pause' : 'Play'} ${name}`,
    );
  });
}

function buildListIndexRow(list: StationList): HTMLElement {
  const row = document.createElement('div');
  row.className = 'list-item list-item--lib';
  row.setAttribute('role', 'button');
  row.tabIndex = 0;
  row.dataset.listId = list.id;

  const info = document.createElement('div');
  info.className = 'list-item__info';
  const name = document.createElement('div');
  name.className = 'list-item__name';
  name.textContent = list.name;
  info.append(name, buildIconStrip(list.stations, 'Empty list'));

  row.append(info);
  if (!libEditing && listDeleteConfirmId !== list.id) row.append(buildListPlayBtn(list));

  // Delete-confirm mode: the row swaps its chevron + trash for an inline
  // "Delete list?" confirm and is no longer clickable.
  if (listDeleteConfirmId === list.id) {
    row.classList.add('list-item--confirming');
    row.append(
      buildDeleteConfirm(
        () => {
          deleteList(list.id);
          track('list-delete');
          listDeleteConfirmId = null;
          renderContent();
        },
        () => {
          listDeleteConfirmId = null;
          renderContent();
        },
      ),
    );
    return row;
  }

  // The trailing trash only appears in edit mode (toggled from the Library
  // header); outside it the card is tap-to-open only. Tapping it opens the
  // inline "Delete list?" confirm handled above.
  if (libEditing) {
    const del = listActionBtn(ICON_TRASH, `Delete ${list.name}`, () => {
      listDeleteConfirmId = list.id;
      renderContent();
    });
    del.classList.add('list-item__del');
    row.append(del);
  }
  const open = () => {
    openListId = list.id;
    resetListUiState();
    renderContent();
    $content.scrollTo({ top: 0 });
  };
  row.addEventListener('click', open);
  row.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  });
  return row;
}

function renderListDetail(list: StationList, query: string): void {
  const back = listActionBtn(ICON_BACK, 'Back to lists', () => {
    openListId = null;
    resetListUiState();
    renderContent();
  });
  back.classList.add('section-label__back');

  const stations = query ? filterStations(list.stations, query) : list.stations;

  if (listRenameOpen) {
    // Inline rename: the header title becomes a text input (prefilled).
    const header = document.createElement('div');
    header.className = 'section-label section-label--list-detail section-label--editing';
    header.append(back);
    const form = buildListNameForm({
      initial: list.name,
      placeholder: 'List name',
      submitLabel: 'Save',
      onSubmit: (name) => {
        renameList(list.id, name);
        track('list-rename');
        listRenameOpen = false;
        renderContent();
      },
      onCancel: () => {
        listRenameOpen = false;
        renderContent();
      },
    });
    header.append(form);
    $content.append(header);
    const input = form.querySelector('input') as HTMLInputElement | null;
    input?.focus();
    input?.select();
  } else {
    const trashBtn = listActionBtn(ICON_TRASH, 'Remove stations', () => {
      listEditing = !listEditing;
      renderContent();
    });
    if (listEditing) trashBtn.classList.add('is-active');
    trashBtn.setAttribute('aria-pressed', String(listEditing));
    const actions: HTMLElement[] = [
      listActionBtn(ICON_PENCIL, 'Rename list', () => {
        listRenameOpen = true;
        listEditing = false;
        renderContent();
      }),
      ...(list.stations.length > 0 ? [trashBtn] : []),
    ];
    // The "+" enters Browse multi-select targeting this list — on both
    // breakpoints. Mobile additionally adopts the iOS status-bar layout
    // ([back · search] · name · actions); desktop keeps its back-prepended
    // header and only gains the "+".
    const wide = matchMedia('(min-width: 1024px)').matches;
    const fullActions = [
      headerActionBtn(ICON_PLUS, 'Add stations to this list', () => {
        listEditing = false;
        enterListSelect(list);
      }),
      ...actions,
    ];
    if (!wide) {
      const label = sectionLabel(list.name, stations.length, fullActions, [
        back,
        headerSearchLead('Search this list'),
      ]);
      label.classList.add('section-label--list-detail');
      $content.append(label);
    } else {
      const label = sectionLabel(list.name, stations.length, fullActions);
      label.classList.add('section-label--list-detail');
      label.prepend(back);
      $content.append(label);
    }
  }

  if (list.stations.length === 0) {
    listEditing = false;
    $content.append(
      emptyState(
        ICON_LIST,
        'This list is empty',
        'Add stations with the list icon on any station row.',
      ),
    );
    return;
  }
  if (stations.length === 0) {
    $content.append(emptyState(ICON_EMPTY, 'No matches', 'Nothing in this list matches that search'));
    return;
  }
  // List detail matches the Favorites layout: iOS-style cards on desktop,
  // single-column stack on mobile, with now-playing cover + track.
  const grid = rowsGrid(stations, { cover: true, queue: { kind: 'list', listId: list.id } });
  $content.append(grid);
  armFavCovers(stations);
  // Edit mode: per-row remove (−), like Favorites. Otherwise reorder the
  // list's stations (full, unfiltered list only — a search subset isn't the
  // stored order). Grip drag on mobile, DnD on desktop.
  if (listEditing) {
    decorateRowRemoval(grid, stations, (station) => ({
      label: `Remove ${station.name} from ${list.name}`,
      onRemove: () => {
        removeFromList(list.id, station.id);
        track('list-remove-station');
        renderContent(); // stays in edit mode until the list empties
      },
    }));
  } else if (!query) {
    enableReorder(grid, {
      itemSelector: ':scope > .row',
      idOf: (el) => el.dataset.id ?? '',
      onCommit: (ids) => reorderListStations(list.id, ids),
    });
  }
}

// ── Add-to-list sheet ────────────────────────────────────────────────

// ─── Add-to-list popup (iOS AddListPopupCard) ─────────────────────────
function openAddList(opts: { mode: AddListMode; station?: Station }): void {
  addListState = { mode: opts.mode, station: opts.station ?? null, target: null, nameDraft: '' };
  renderAddList();
  $listSheet.classList.add('open');
  $listSheet.setAttribute('aria-hidden', 'false');
}

function closeAddList(): void {
  $listSheet.classList.remove('open');
  $listSheet.setAttribute('aria-hidden', 'true');
  addListState = null;
}

/** Trimmed draft name for the "Create list" field. */
function addListName(): string {
  return (addListState?.nameDraft ?? '').trim();
}
/** A drafted new-list name that collides with an existing list (case-insensitive). */
function addListNameTaken(): boolean {
  const name = addListName().toLowerCase();
  if (!name) return false;
  return getLists().some((l) => l.name.trim().toLowerCase() === name);
}
/** Step-1 confirm is allowed once a valid target is chosen: an existing list,
 *  or a non-empty, non-duplicate new name. */
function addListCanConfirm(): boolean {
  const st = addListState;
  if (!st?.target) return false;
  if (st.target.kind === 'existing') return true;
  return addListName().length > 0 && !addListNameTaken();
}

/** Two-segment progress strip (iOS AddListStepStrip): "1 Choose list — 2 Pick
 *  stations", with the active step inked and completed steps showing a check. */
function buildStepStrip(activeStep: 1 | 2): HTMLElement {
  const strip = document.createElement('div');
  strip.className = 'addlist-steps';
  const seg = (n: 1 | 2, label: string): HTMLElement => {
    const el = document.createElement('div');
    el.className = 'addlist-step';
    if (n === activeStep) el.classList.add('is-active');
    if (n < activeStep) el.classList.add('is-done');
    const badge = document.createElement('span');
    badge.className = 'addlist-step__badge';
    if (n < activeStep) badge.innerHTML = ICON_CHECK;
    else badge.textContent = String(n);
    const text = document.createElement('span');
    text.className = 'addlist-step__label';
    text.textContent = label;
    el.append(badge, text);
    return el;
  };
  const conn = document.createElement('span');
  conn.className = 'addlist-steps__conn';
  if (activeStep > 1) conn.classList.add('is-done');
  strip.append(seg(1, 'Choose list'), conn, seg(2, 'Pick stations'));
  return strip;
}

function renderAddList(): void {
  const st = addListState;
  if (!st) return;
  $listSheetBody.replaceChildren();
  $listSheetLabel.textContent = st.mode === 'pickStations' ? 'New list' : 'Add to list';

  // Step strip — only the two-step (pickStations) flow signals a step 2.
  if (st.mode === 'pickStations') $listSheetBody.append(buildStepStrip(1));

  const scroll = document.createElement('div');
  scroll.className = 'addlist-scroll';
  scroll.append(buildAddListCreateRow());
  for (const l of getLists()) scroll.append(buildAddListRow(l, st.station));
  $listSheetBody.append(scroll, buildAddListFoot());
}

/** Update the footer confirm's enabled state + the duplicate-name notice
 *  without a re-render, so the "Create list" field keeps focus while typing. */
function syncAddListLive(): void {
  const confirm = $listSheetBody.querySelector<HTMLButtonElement>('.addlist-foot__confirm');
  if (confirm) confirm.disabled = !addListCanConfirm();
  const err = $listSheetBody.querySelector<HTMLElement>('.addlist-create__err');
  if (err) err.hidden = !addListNameTaken();
}

/** "Create list" row — a label that morphs into a focused text field once
 *  selected (same idiom as the Favorites search field). */
function buildAddListCreateRow(): HTMLElement {
  const st = addListState!;
  const row = document.createElement('div');
  row.className = 'addlist-create';
  if (st.target?.kind !== 'new') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'addlist-create__btn';
    const icon = document.createElement('span');
    icon.className = 'addlist-create__icon';
    icon.innerHTML = ICON_PLUS;
    const label = document.createElement('span');
    label.className = 'addlist-create__label';
    label.textContent = 'Create list';
    btn.append(icon, label);
    btn.addEventListener('click', () => {
      st.target = { kind: 'new' };
      renderAddList();
      $listSheetBody.querySelector<HTMLInputElement>('.addlist-create__input')?.focus();
    });
    row.append(btn);
    return row;
  }

  row.classList.add('is-selected');
  const field = document.createElement('div');
  field.className = 'addlist-create__field';
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'addlist-create__input';
  input.placeholder = 'Create list';
  input.value = st.nameDraft;
  input.autocomplete = 'off';
  input.maxLength = 60;
  input.setAttribute('aria-label', 'New list name');
  const clear = document.createElement('button');
  clear.type = 'button';
  clear.className = 'addlist-create__clear';
  clear.setAttribute('aria-label', 'Clear name');
  clear.innerHTML = ICON_CLOSE;
  clear.hidden = st.nameDraft.length === 0;
  input.addEventListener('input', () => {
    st.nameDraft = input.value;
    clear.hidden = input.value.length === 0;
    syncAddListLive();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (addListCanConfirm()) confirmAddList();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeAddList();
    }
  });
  clear.addEventListener('click', () => {
    st.nameDraft = '';
    input.value = '';
    clear.hidden = true;
    input.focus();
    syncAddListLive();
  });
  field.append(input, clear);
  const err = document.createElement('div');
  err.className = 'addlist-create__err';
  err.textContent = 'Name already in use';
  err.hidden = !addListNameTaken();
  row.append(field, err);
  return row;
}

/** A selectable existing-list row. In 'addNow' a list already holding the
 *  station is disabled and reads "Already in list" (iOS parity). */
function buildAddListRow(l: StationList, station: Station | null): HTMLElement {
  const st = addListState!;
  const already = st.mode === 'addNow' && station ? listContains(l.id, station.id) : false;
  const selected = st.target?.kind === 'existing' && st.target.id === l.id;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'list-picker__btn';
  btn.classList.toggle('is-in', selected);
  if (already) {
    btn.classList.add('is-already');
    btn.disabled = true;
  }
  btn.setAttribute('aria-pressed', String(selected));

  const check = document.createElement('span');
  check.className = 'list-picker__check';
  check.innerHTML = ICON_CHECK;
  const name = document.createElement('span');
  name.className = 'list-picker__name';
  name.textContent = l.name;
  const tail = document.createElement('span');
  tail.className = 'list-picker__count';
  tail.textContent = already ? 'Already in list' : String(l.stations.length);
  btn.append(check, name, tail);

  if (!already) {
    btn.addEventListener('click', () => {
      st.target = { kind: 'existing', id: l.id };
      renderAddList();
    });
  }
  return btn;
}

/** Footer: cancel (✕) + confirm. Confirm is "Pick stations →" in pickStations
 *  (advances to the select dock) or a ✓ in addNow (saves immediately). */
function buildAddListFoot(): HTMLElement {
  const st = addListState!;
  const foot = document.createElement('div');
  foot.className = 'addlist-foot';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'addlist-foot__cancel';
  cancel.setAttribute('aria-label', 'Cancel');
  cancel.innerHTML = ICON_CLOSE;
  cancel.addEventListener('click', closeAddList);

  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'addlist-foot__confirm';
  confirm.disabled = !addListCanConfirm();
  if (st.mode === 'pickStations') {
    confirm.classList.add('addlist-foot__confirm--next');
    const text = document.createElement('span');
    text.textContent = 'Pick stations';
    const arrow = document.createElement('span');
    arrow.className = 'addlist-foot__arrow';
    arrow.innerHTML = ICON_CHEVRON_RIGHT;
    confirm.append(text, arrow);
    confirm.setAttribute('aria-label', 'Pick stations');
  } else {
    confirm.classList.add('addlist-foot__confirm--ok');
    confirm.innerHTML = ICON_CHECK;
    confirm.setAttribute('aria-label', 'Add to list');
  }
  confirm.addEventListener('click', () => {
    if (addListCanConfirm()) confirmAddList();
  });
  foot.append(cancel, confirm);
  return foot;
}

function confirmAddList(): void {
  const st = addListState;
  if (!st?.target) return;

  if (st.mode === 'addNow') {
    const station = st.station;
    if (!station) return;
    let listId: string;
    if (st.target.kind === 'existing') {
      listId = st.target.id;
    } else {
      listId = createList(addListName()).id;
      track('list-create');
    }
    addToList(listId, station);
    track('list-add');
    closeAddList();
    if (activeTab === 'library' || activeTab === 'fav') renderContent();
    renderTopBar();
    return;
  }

  // pickStations → hand off to the Browse select dock (step 2). A new list is
  // carried as a 'newList' target and created on save, so a cancel here (or in
  // step 2) leaves nothing behind.
  let target: SelectTarget | null;
  if (st.target.kind === 'existing') {
    const l = getList(st.target.id);
    target = l ? { kind: 'list', id: l.id, name: l.name } : null;
  } else {
    target = { kind: 'newList', name: addListName() };
  }
  closeAddList();
  if (!target) return;
  selectMode = {
    target,
    picked: new Map(),
    existing:
      target.kind === 'list'
        ? new Set(getList(target.id)?.stations.map((s) => s.id) ?? [])
        : new Set(),
    withSteps: true,
  };
  setTab('browse'); // setTab won't clear selectMode when going to browse
  syncSelectDock();
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/** Snapshot the current Browse-tab inputs into a shape composeBrowseFilter
 *  can operate on. One read site, used by runQuery + loadMore so they
 *  cannot drift out of sync (the audit-#70 bug). */
async function runQuery(): Promise<void> {
  if (activeTab !== 'browse') {
    renderContent();
    return;
  }
  const myToken = ++queryToken;
  // Filter changed → page resets.
  browseOffset = 0;
  browseHasMore = false;
  browseLoadingMore = false;
  pendingLoadMore = null;
  const query = $search.value.trim();
  // Radio Browser is fetched only for a text search. "Browse all" and
  // every filtered view (genre / country / news / quality) list the
  // published catalog directly — no network. Sponsor decision 2026-09-08:
  // the unfiltered list must only contain stations the daily probe has
  // tested; live RB rows never enter the catalog or the health record,
  // and RB's most-voted record (an http listing of BBC World Service)
  // used to be the first thing a new visitor tapped — and it failed.
  const needsRb = !inDiscovery() && query.length > 0;
  if (!needsRb) {
    if (myToken !== queryToken) return;
    lastBrowseStations = [];
    renderContent();
    return;
  }
  $content.replaceChildren(statusLine('Tuning in…'));
  try {
    const stations = await searchStations({ query: query || undefined, offset: 0 });
    if (myToken !== queryToken) return;
    lastBrowseStations = stations;
    // RB's searchStations dedupes by streamUrl, so a 60-result page
    // typically lands at ≤59 — `=== PAGE_SIZE` would false-negative
    // every time. Treat any non-empty page as "there's more"; an
    // empty response means we've actually exhausted the catalog or
    // the request errored.
    browseHasMore = stations.length > 0;
    renderContent();
  } catch (err) {
    if (myToken !== queryToken) return;
    lastBrowseStations = [];
    $content.replaceChildren(
      statusLine(`Off air · ${err instanceof Error ? err.message : String(err)}`),
    );
  }
}

async function loadMore(): Promise<void> {
  if (browseLoadingMore || !browseHasMore || activeTab !== 'browse') return;
  browseLoadingMore = true;
  renderContent(); // flips the button into a "Loading…" state
  const myToken = queryToken;
  const nextOffset = browseOffset + PAGE_SIZE;
  const query = $search.value.trim();
  try {
    // Text search paginates via searchStations (carries the query);
    // the unfiltered Browse-all view uses fetchStations (top-by-votes).
    const more = query
      ? await searchStations({ query, offset: nextOffset })
      : await fetchStations(nextOffset);
    if (myToken !== queryToken) return;
    // Radio Browser sometimes returns duplicates across page boundaries
    // (when records shift between requests). De-dupe by id.
    const seen = new Set(lastBrowseStations.map((s) => s.id));
    const fresh = more.filter((s) => !seen.has(s.id));
    lastBrowseStations = lastBrowseStations.concat(fresh);
    browseOffset = nextOffset;
    // See the runQuery comment — `> 0` instead of `=== PAGE_SIZE`
    // because RB's per-page dedupe keeps trimming below the limit.
    browseHasMore = more.length > 0;
  } catch {
    browseHasMore = false;
  } finally {
    browseLoadingMore = false;
    renderContent();
  }
}

function loadMoreButton(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'load-more';
  btn.disabled = browseLoadingMore;
  btn.textContent = browseLoadingMore ? 'Loading…' : 'Load more';
  btn.addEventListener('click', () => void loadMore());
  return btn;
}

/** Local-catalog "Show more" button — bumps the in-memory cap and
 *  re-renders. No network. Pairs with the HOME_VIEW_PAGE_SIZE cap
 *  applied to the home view + filtered "My stations" list. */
function homeShowMoreButton(remaining: number): HTMLButtonElement {
  const next = Math.min(HOME_VIEW_PAGE_SIZE, remaining);
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'load-more';
  btn.textContent = `Show ${next} more · ${remaining} remaining`;
  btn.addEventListener('click', () => {
    homeViewLimit += HOME_VIEW_PAGE_SIZE;
    renderContent();
  });
  return btn;
}

// ─────────────────────────────────────────────────────────────
// Actions
// ─────────────────────────────────────────────────────────────

function onRowPlay(station: Station, queue?: QueueSource): void {
  // The context this tap came from becomes the active queue for
  // previous/next (no context → Favorites fallback).
  activeQueue = queue ?? null;
  syncLibraryCardsPlaying();
  // If already current and playing → pause; else play & record recent
  if (currentNP.station.id === station.id) {
    handlePlayToggle();
    return;
  }
  pushRecent(station);
  void player.play(station);
  track(`play: ${station.name}`);
  // Keep the Recents sub-view and the Library home's Recents count fresh.
  if (activeTab === 'recent' || activeTab === 'library') renderContent();
  // On desktop, playing keeps you on the list and pops the mini-player —
  // Now Playing opens on demand (tap the mini). On mobile the small screen
  // makes NP the focus, so jump straight there.
  if (!isDesktop()) openNp(true);
  // Reflect the active station in the URL so the user can copy it /
  // refresh / share. Only built-in stations get a pre-rendered
  // /station/<id>/ page; for custom + RB rows we leave the URL alone.
  syncUrlForStation(station);
}

function syncUrlForStation(station: Station): void {
  const isBuilt = BUILTIN_STATIONS.some((b) => b.id === station.id);
  if (!isBuilt) return;
  const next = `/station/${station.id}/`;
  if (window.location.pathname === next) return;
  window.history.pushState({ stationId: station.id }, '', next);
}

function onToggleFav(station: Station): void {
  const added = toggleFavorite(station);
  track(`${added ? 'favorite' : 'unfavorite'}: ${station.name}`);
  // Refresh affected UI bits
  if (activeTab === 'fav' || activeTab === 'browse') {
    if (activeTab === 'fav') renderContent();
    else syncRowHearts();
  }
  renderTopBar();
  // Update NP fav state if it's this station
  if (currentNP.station.id === station.id) {
    $npFav.classList.toggle('is-fav', isFavorite(station.id));
  }
}

function syncRowHearts(): void {
  const favs = favIdSet();
  $content.querySelectorAll<HTMLButtonElement>('.heart').forEach((heart) => {
    const row = heart.closest<HTMLElement>('.row');
    const id = row?.dataset.id;
    if (!id) return;
    heart.classList.toggle('is-fav', favs.has(id));
  });
}

function syncRowPlayingState(): void {
  const id = currentNP.station.id;
  const isPaused = currentNP.state !== 'playing';
  $content.querySelectorAll<HTMLElement>('.row').forEach((row) => {
    const isCurrent = !!id && row.dataset.id === id;
    row.classList.toggle('is-playing', isCurrent);
    const eq = row.querySelector<HTMLElement>('.eq');
    if (eq) eq.classList.toggle('paused', isCurrent && isPaused);
  });
}

function syncSearchClear(): void {
  $searchClear.hidden = $search.value === '';
}

function clearSearch(refocus: boolean): void {
  if ($search.value !== '') {
    $search.value = '';
    syncSearchClear();
  }
  // Clearing also collapses the expandable feed-page search back to its icon.
  $body.classList.remove('search-open');
  if (refocus) $search.focus();
}

function goHome(): void {
  // Close Now Playing if open, then reset Browse to its discovery landing.
  if ($np.classList.contains('open')) openNp(false);
  const wasDiscovery = activeTab === 'browse' && inDiscovery();
  clearSearch(false);
  // Reset every Browse narrowing back to the discovery landing.
  filterGenres.clear();
  filterCountries.clear();
  filterNews = false;
  activeQuality.clear();
  browseAll = false;
  activeSort = null;
  syncFilterDot();
  if (activeTab !== 'browse') {
    setTab('browse'); // setTab also runs the query
  } else if (!wasDiscovery) {
    void runQuery();
  }
  $content.scrollTo({ top: 0, behavior: 'smooth' });
  // Restore the homepage URL when returning home — symmetric with
  // syncUrlForStation pushing /station/<id>/ on row click.
  if (window.location.pathname !== '/') {
    window.history.pushState({}, '', '/');
  }
}

function setTab(tab: Tab): void {
  // No-op when already there. Playing-tab tap with no station also no-ops.
  if (activeTab === tab) return;
  if (tab === 'playing' && !currentNP.station.id) return;

  // Drop any half-finished list create/rename/delete when changing tabs.
  resetListUiState();

  // Track the last list tab so closing Now Playing returns there.
  if (tab !== 'playing') {
    lastListTab = tab;
  }

  activeTab = tab;
  $body.classList.toggle('tab-playing', tab === 'playing');
  // Favorites carries its own mobile chrome (search-icon-expands-the-field,
  // edit mode). Tag the body so the CSS can hide the brand-row search field,
  // and drop any half-open edit/search state when leaving the tab.
  // Favorites + Library (home / list detail / recents) carry the expandable
  // search-icon chrome on mobile; tag the body so the CSS hides the brand-row
  // field there. Any tab change collapses an open search and drops edit mode.
  $body.classList.toggle('tab-fav', tab === 'fav');
  $body.classList.toggle('tab-library', tab === 'library');
  $body.classList.remove('search-open');
  if (tab !== 'fav') favEditing = false;
  if (tab !== 'library') libEditing = false;
  // The /ios promo banner is Browse-only — reveal/hide it as the tab changes.
  syncPromo();
  // The select dock belongs to Browse; navigating elsewhere abandons the
  // pick (saveSelect clears selectMode itself before its setTab, so this
  // only fires on a genuine bail-out).
  if (selectMode && tab !== 'browse') {
    selectMode = null;
    syncSelectDock();
  }
  $np.classList.toggle('open', tab === 'playing');
  // NP is only present (and in the a11y tree) when it's the active
  // destination, on every breakpoint.
  $np.setAttribute('aria-hidden', String(tab !== 'playing'));
  // The wide album/schedule/lyrics column count depends on the breakpoint
  // and available panes; re-sync when entering/leaving the destination.
  syncNpTabs();

  renderTabBar();
  renderTopBar();
  if (tab === 'browse') void runQuery();
  else if (tab !== 'playing') renderContent();
  // The player destination skips renderContent, so stop the library cover
  // poll here when leaving a feed for Now Playing.
  else favCovers.stop();

  track(`tab/${tab}`);
}

// Desktop (≥1024px) shows Now Playing as a persistent docked pane next
// to the list, so opening/closing it is a no-op there — the pane is
// always mounted and the $np* updates run on every player event
// regardless of which list tab is active. On mobile it stays the
// slide-up destination reached via the 'playing' tab.
const desktopMq = matchMedia('(min-width: 1024px)');
function isDesktop(): boolean {
  return desktopMq.matches;
}

function openNp(open: boolean): void {
  // Now Playing is a full-area destination on every breakpoint (the
  // 'playing' tab), bridged by the persistent mini-player. The desktop
  // dock is gone — playing keeps you on the list, NP opens on demand.
  if (open) setTab('playing');
  else if (activeTab === 'playing') setTab(lastListTab);
}

/** Keep breakpoint-dependent state in sync. NP is a full-area destination
 *  on every breakpoint now (the 'playing' tab), so its a11y visibility
 *  just follows that tab. Crossing the wide breakpoint re-evaluates the
 *  album/schedule/lyrics column count. Called at boot + on breakpoint
 *  changes. */
function syncLayoutMode(): void {
  $np.setAttribute('aria-hidden', String(activeTab !== 'playing'));
  syncNpTabs();
}
desktopMq.addEventListener('change', syncLayoutMode);

// Theme persistence + DOM application live in ./theme. Boot wiring
// applies the persisted choice before first paint, then keeps the
// `<meta name="theme-color">` tint in sync with the OS preference if
// the user hasn't picked an explicit theme.
bootstrapTheme();
bootstrapAccent();

// About + Add are now tabs of the unified Settings sheet, not their own
// slide-up sheets. About is reachable only via the tab strip; Add keeps a
// programmatic opener (openAddSheet) for the post-submit close + any future
// "add station" affordance.

// ─────────────────────────────────────────────────────────────
// Dashboard sheet
// ─────────────────────────────────────────────────────────────

/** Rough country centroids — enough to place a circle on the map.
 *  Sourced from public-domain country-centroid data (truncated to the
 *  ~50 we'd plausibly have stations from). For a country we don't list
 *  here, we fall back to the geo of one of its curated stations. */
const COUNTRY_CENTROIDS: Record<string, [number, number]> = {
  AT: [47.5162, 14.5501], AU: [-25.2744, 133.7751], BE: [50.5039, 4.4699],
  BR: [-14.235, -51.9253], CA: [56.1304, -106.3468], CH: [46.8182, 8.2275],
  CN: [35.8617, 104.1954], CZ: [49.8175, 15.473], DE: [51.1657, 10.4515],
  DK: [56.2639, 9.5018], ES: [40.4637, -3.7492], FI: [61.9241, 25.7482],
  FR: [46.2276, 2.2137], GB: [55.3781, -3.436], GR: [39.0742, 21.8243],
  HU: [47.1625, 19.5033], IE: [53.4129, -8.2439], IL: [31.0461, 34.8516],
  IN: [20.5937, 78.9629], IT: [41.8719, 12.5674], JP: [36.2048, 138.2529],
  KR: [35.9078, 127.7669], MX: [23.6345, -102.5528], NL: [52.1326, 5.2913],
  NO: [60.472, 8.4689], NZ: [-40.9006, 174.886], PH: [12.8797, 121.774],
  PL: [51.9194, 19.1451], PT: [39.3999, -8.2245], RO: [45.9432, 24.9668],
  RU: [61.524, 105.3188], SE: [60.1282, 18.6435], TR: [38.9637, 35.2433],
  UA: [48.3794, 31.1656], UK: [55.3781, -3.436], US: [37.0902, -95.7129],
  ZA: [-30.5595, 22.9375], AR: [-38.4161, -63.6167], CO: [4.5709, -74.2973],
  CL: [-35.6751, -71.543], PE: [-9.19, -75.0152], EC: [-1.8312, -78.1834],
  AE: [23.4241, 53.8478], SK: [48.669, 19.699], BG: [42.7339, 25.4858],
  HR: [45.1, 15.2], RS: [44.0165, 21.0059], BA: [43.9159, 17.6791],
  ID: [-0.7893, 113.9213], TW: [23.6978, 120.9605], UY: [-32.5228, -55.7658],
  VE: [6.4238, -66.5897], UG: [1.3733, 32.2903],
};

// Dashboard types + aggregation helpers live in ./dashboard.

interface DashboardPayload {
  range_days: number;
  days: string[];
  totals: PublicTotals;
  top_stations: {
    items: TopStationItem[];
    total: number;
    distinct_stations: number;
  };
  locations: { items: PublicLocationItem[]; total: number };
}

// `cache: 'no-store'` so the browser's HTTP cache never serves a stale
// snapshot from an earlier visit. Cloudflare's edge cache still answers
// (the response carries `Cache-Control: public, max-age=300`), so the
// upstream GC load is one fetch per 5 min regardless of traffic.
async function fetchDashboardPayload(): Promise<DashboardPayload | null> {
  try {
    const res = await fetch(DASHBOARD_URL, { cache: 'no-store' });
    if (!res.ok) {
      reportWorkerError(new Error(`HTTP ${res.status}`), '/api/public/dashboard', res.status);
      return null;
    }
    return (await res.json()) as DashboardPayload;
  } catch (err) {
    reportWorkerError(err, '/api/public/dashboard');
    return null;
  }
}

function renderDashKpis(d: DashboardData, totals: PublicTotals | null): void {
  // Visits is GoatCounter's `/stats/total` `total` field — the
  // headline number on GC's own dashboard. Prominent so the rrradio
  // sheet matches what shows up there.
  $dashVisits.textContent = totals?.total != null ? totals.total.toLocaleString() : '—';
  // Plays is the rrradio-specific derived metric (sum of `play:`
  // events). Verifiable in GC by summing the same path prefix; muted
  // because it's a filtered view, not a native GC number.
  $dashPlays.textContent = d.totalPlays > 0 ? d.totalPlays.toLocaleString() : '—';
}

function renderDashCountryTable(d: DashboardData): void {
  $dashCountryTable.replaceChildren();
  const sorted = [...d.byListenerCountry.entries()].sort((a, b) => b[1] - a[1]);
  const max = sorted[0]?.[1] ?? 1;
  const total = sorted.reduce((s, [, c]) => s + c, 0);
  sorted.forEach(([cc, count], i) => {
    const tr = document.createElement('tr');
    const rank = document.createElement('td');
    rank.className = 'rank';
    rank.textContent = String(i + 1).padStart(2, '0');
    const country = document.createElement('td');
    country.className = 'country';
    country.textContent = countryName(cc);
    const bar = document.createElement('td');
    bar.className = 'bar';
    bar.innerHTML = `<div class="bar__track"><div class="bar__fill" style="width:${(count / max) * 100}%"></div></div>`;
    const num = document.createElement('td');
    num.className = 'count';
    num.textContent = String(count);
    const pct = document.createElement('td');
    pct.className = 'pct';
    pct.textContent = fmtSharePct(count, total);
    tr.append(rank, country, bar, num, pct);
    $dashCountryTable.append(tr);
  });
}

/** Build a fixed-height bar sparkline. Each `series` entry is one
 *  calendar day; bars share a single max across the whole table (passed
 *  in `maxAcrossTable`) so a row with one big day reads as taller than
 *  a row with a flat baseline. Days run oldest → newest, so the
 *  rightmost bar is today. */
function renderSparkline(series: number[], maxAcrossTable: number, days: string[]): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'bar__spark';
  wrap.setAttribute('role', 'img');
  const totalRow = series.reduce((s, v) => s + v, 0);
  wrap.setAttribute(
    'aria-label',
    `${totalRow} plays over the last ${series.length} days`,
  );
  series.forEach((v, idx) => {
    const cell = document.createElement('div');
    cell.className = 'bar__spark-cell';
    const fill = document.createElement('div');
    fill.className = 'bar__spark-fill';
    // sqrt-scale would compress big spikes; the brief is "show the
    // shape per day", so a linear scale stays honest. Floor at 4% so
    // a single-play day still leaves a visible nub instead of vanishing.
    const pct = v > 0 ? Math.max(4, (v / maxAcrossTable) * 100) : 0;
    fill.style.height = `${pct}%`;
    if (v === 0) fill.classList.add('is-empty');
    const day = days[idx];
    cell.title = day ? `${day}: ${v}` : String(v);
    cell.append(fill);
    wrap.append(cell);
  });
  return wrap;
}

function renderDashStationTable(items: TopStationItem[], days: string[]): void {
  $dashStationTable.replaceChildren();
  if (items.length === 0) return;
  const max = items[0]?.count ?? 1;
  const total = items.reduce((s, it) => s + it.count, 0);
  // Per-day plays sparkline when the worker ships a series. Falls back
  // to the single share-of-max bar for older worker builds. Max is the
  // highest single-day value across every visible row so the bars are
  // cross-station-comparable.
  const showSpark = items.some((it) => it.series && it.series.length > 0);
  let sparkMax = 0;
  if (showSpark) {
    for (const it of items) {
      for (const v of it.series ?? []) if (v > sparkMax) sparkMax = v;
    }
    if (sparkMax === 0) sparkMax = 1;
  }
  items.forEach((it, i) => {
    const tr = document.createElement('tr');
    const rank = document.createElement('td');
    rank.className = 'rank';
    rank.textContent = String(i + 1).padStart(2, '0');
    const name = document.createElement('td');
    name.className = 'country'; // reuse existing column class for the auto-width name slot
    name.textContent = it.name;
    const bar = document.createElement('td');
    bar.className = 'bar';
    if (showSpark && it.series && it.series.length > 0) {
      bar.append(renderSparkline(it.series, sparkMax, days));
    } else {
      bar.innerHTML = `<div class="bar__track"><div class="bar__fill" style="width:${(it.count / max) * 100}%"></div></div>`;
    }
    const num = document.createElement('td');
    num.className = 'count';
    num.textContent = String(it.count);
    const pct = document.createElement('td');
    pct.className = 'pct';
    pct.textContent = fmtSharePct(it.count, total);
    tr.append(rank, name, bar, num, pct);
    $dashStationTable.append(tr);
  });
}

function getCountryCentroid(cc: string): [number, number] | null {
  if (COUNTRY_CENTROIDS[cc]) return COUNTRY_CENTROIDS[cc];
  // Fallback: any curated station from that country
  const s = BUILTIN_STATIONS.find((x) => x.country?.toUpperCase() === cc && x.geo);
  return s?.geo ?? null;
}

// Dashboard map: Leaflet with one circle marker per country at the
// centroid, over self-hosted Natural Earth 110m country outlines
// (public/world-countries-110m.json, public domain). No tile CDN —
// CARTO's basemaps started demanding an API key (2026-10), and dropping
// tiles also keeps visitor IPs away from a third party. The outlines are
// fetched once, on first open of the Stats sheet.
let worldOutlines: Promise<GeoJSON.GeoJsonObject | null> | null = null;

function loadWorldOutlines(): Promise<GeoJSON.GeoJsonObject | null> {
  worldOutlines ??= fetch(`${import.meta.env.BASE_URL}world-countries-110m.json`)
    .then((res) => (res.ok ? (res.json() as Promise<GeoJSON.GeoJsonObject>) : null))
    .catch(() => null);
  return worldOutlines;
}

let dashLeafletMap: L.Map | null = null;
let dashMapResize: ResizeObserver | null = null;

function teardownDashMap(): void {
  dashMapResize?.disconnect();
  dashMapResize = null;
  dashLeafletMap?.remove();
  dashLeafletMap = null;
  $dashMap.replaceChildren();
}

function renderDashMap(d: DashboardData): void {
  teardownDashMap();
  const data = d.byListenerCountry;

  if (data.size === 0) {
    const empty = document.createElement('div');
    empty.className = 'dash-map-empty';
    empty.textContent = 'No listener-location data yet';
    $dashMap.append(empty);
    return;
  }

  const mapEl = document.createElement('div');
  mapEl.className = 'dash-map-leaflet';
  $dashMap.append(mapEl);

  // Leaflet measures its container at init; the wrap has to be in
  // the DOM and laid out first. queueMicrotask defers to the next
  // tick after the synchronous append above.
  queueMicrotask(() => {
    if (!mapEl.isConnected) return;
    const lmap = L.map(mapEl, {
      zoomSnap: 0.25,
      worldCopyJump: true,
      zoomControl: false,
      attributionControl: true,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
    });
    dashLeafletMap = lmap;

    lmap.attributionControl.setPrefix(false);
    void loadWorldOutlines().then((outlines) => {
      if (!outlines || dashLeafletMap !== lmap) return;
      // Fill/stroke come from .dash-map-land in style.css so the land
      // tracks theme switches; added behind the markers.
      L.geoJSON(outlines, {
        interactive: false,
        style: { className: 'dash-map-land' },
        attribution: '<a href="https://www.naturalearthdata.com/">Natural Earth</a>',
      })
        .addTo(lmap)
        .bringToBack();
    });

    // Show the inhabited band only — fitWorld zooms out enough to
    // include Antarctica, leaving lots of empty space at the bottom.
    // Refit whenever the box changes size: the first measure happens
    // mid-way through the sheet's open animation, which used to leave
    // the world at zoom 0 in a corner of a wide box.
    const fit = (): void => {
      lmap.invalidateSize();
      lmap.fitBounds([
        [-55, -170],
        [70, 170],
      ]);
    };
    fit();
    dashMapResize = new ResizeObserver(fit);
    dashMapResize.observe(mapEl);

    // Resolve the theme accent at render time so the markers track
    // theme switches (Warm/Cool/Yellow × light/dark).
    const accent =
      getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#ffff00';

    const max = Math.max(...data.values());
    for (const [cc, count] of data) {
      const centroid = getCountryCentroid(cc);
      if (!centroid) continue;
      // sqrt(share) → area-proportional. Pixel radius is constant
      // at every zoom level (circleMarker, not circle), so the dot
      // sizes stay readable as the user pans/zooms.
      const share = count / max;
      const r = 4 + Math.sqrt(share) * 12;

      const marker = L.circleMarker(centroid, {
        radius: r,
        color: accent,
        weight: 1,
        opacity: 0.85,
        fillColor: accent,
        fillOpacity: 0.45,
      }).addTo(lmap);
      marker.bindTooltip(`${countryName(cc)} · ${count} visitors`, {
        direction: 'top',
        offset: [0, -r],
        opacity: 0.95,
      });
    }
  });
}

async function openDashboardSheet(open: boolean): Promise<void> {
  $dashboardSheet.classList.toggle('open', open);
  $dashboardSheet.setAttribute('aria-hidden', String(!open));
  if (!open) {
    teardownDashMap();
    return;
  }
  $dashVisits.textContent = '…';
  $dashPlays.textContent = '…';
  const payload = await fetchDashboardPayload();
  const items = (payload?.top_stations.items ?? []).filter(
    (i) => typeof i.name === 'string' && i.name.length > 0,
  );
  const data = aggregateDashboard(
    payload?.locations.items ?? [],
    payload?.top_stations.total ?? 0,
    payload?.top_stations.distinct_stations ?? 0,
    payload?.days ?? [],
  );
  renderDashKpis(data, payload?.totals ?? null);
  renderDashCountryTable(data);
  renderDashStationTable(items, payload?.days ?? []);
  void renderDashMap(data);
}

function openAddSheet(open: boolean): void {
  if (open) {
    openSettingsSheet(true);
    selectSettingsTab('add');
    renderCustomList();
    $addError.hidden = true;
    // Focus the first field when opening
    window.setTimeout(() => {
      const first = $addForm.querySelector<HTMLInputElement>('input[name="name"]');
      first?.focus();
    }, 280);
  } else {
    openSettingsSheet(false);
  }
}

function buildId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `custom-${crypto.randomUUID()}`;
  }
  return `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

function showAddError(msg: string): void {
  $addError.textContent = msg;
  $addError.hidden = false;
}

function handleAddSubmit(e: SubmitEvent): void {
  e.preventDefault();
  const data = new FormData($addForm);
  const name = String(data.get('name') ?? '').trim();
  const streamUrl = String(data.get('streamUrl') ?? '').trim();
  const homepage = String(data.get('homepage') ?? '').trim();
  const country = String(data.get('country') ?? '').trim().toUpperCase();
  const tagsRaw = String(data.get('tags') ?? '').trim();

  if (!name) {
    showAddError('Name is required.');
    return;
  }
  if (!streamUrl) {
    showAddError('Stream URL is required.');
    return;
  }
  if (!safeUrl(streamUrl)) {
    showAddError('Stream URL must be a valid http:// or https:// URL.');
    return;
  }
  // The page is served over https, so an http:// stream is blocked by
  // mixed-content. Reject up-front rather than letting the user save a
  // station that will silently fail to play. Audit #71.
  if (streamUrl.startsWith('http://')) {
    showAddError('Stream URL must use https://. Mixed-content browsers block http:// audio.');
    return;
  }
  if (homepage && !safeUrl(homepage)) {
    showAddError('Homepage must be a valid http:// or https:// URL.');
    return;
  }
  if (country && !/^[A-Z]{2}$/.test(country)) {
    showAddError('Country must be a 2-letter code (e.g. CH).');
    return;
  }

  const id = buildId();
  const station: Station = {
    id,
    name,
    streamUrl,
    homepage: homepage || undefined,
    country: country || undefined,
    tags: parseTags(tagsRaw),
    frequency: pseudoFrequency(id),
  };

  addCustom(station);
  track('add-custom-station');
  $addForm.reset();
  $addError.hidden = true;
  openAddSheet(false);

  // Refresh whatever list is visible, then play
  if (activeTab === 'browse') void runQuery();
  else renderContent();
  pushRecent(station);
  void player.play(station);
  openNp(true);
}

function renderCustomList(): void {
  const all = getCustom();
  $customList.replaceChildren();
  if (all.length === 0) {
    const empty = document.createElement('li');
    empty.className = 'custom-empty';
    empty.textContent = 'No custom stations yet.';
    $customList.append(empty);
    return;
  }
  for (const s of all) {
    const li = document.createElement('li');
    li.className = 'custom-row';

    const main = document.createElement('div');
    main.className = 'custom-row__main';

    const name = document.createElement('div');
    name.className = 'custom-row__name';
    name.textContent = s.name;

    const url = document.createElement('div');
    url.className = 'custom-row__url';
    const display = urlDisplay(s.streamUrl);
    url.textContent = display ? display.host : s.streamUrl;

    main.append(name, url);

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'custom-row__delete';
    del.setAttribute('aria-label', `Delete ${s.name}`);
    del.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>';
    del.addEventListener('click', () => {
      removeCustom(s.id);
      renderCustomList();
      if (activeTab === 'browse') void runQuery();
      else renderContent();
    });

    li.append(main, del);
    $customList.append(li);
  }
}

/** Reflect the muted flag across the mute button + the volume slider's
 *  speaker icon, without re-toggling the audio element. */
function reflectMuteUi(muted: boolean): void {
  $body.classList.toggle('is-muted', muted);
  $npVolume.classList.toggle('is-muted', muted);
  $miniVolume.classList.toggle('is-muted', muted);
  $npMute.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
}

// Play/pause click router. Tap during loading = cancel the connection.
// Without this the user is stuck waiting on a slow / dead stream with
// no obvious way out short of opening another station.
function handlePlayToggle(): void {
  cancelSleepFadeOnUserAction();
  if (currentNP.state === 'playing' || currentNP.state === 'loading') {
    player.pause();
    return;
  }
  player.toggle();
}

// Sleep-timer fade-out (#103): the last SLEEP_FADE_MS of the timer ramp
// the volume to 0, then a plain pause, then the volume is put back so the
// next play isn't silent. Uses player.setVolume directly so the fade is
// neither persisted nor reflected on the volume sliders.
const sleepFade = new SleepFade({
  getVolume: () => player.getVolume(),
  setVolume: (v) => player.setVolume(v),
  stop: () => {
    player.pause();
    sleepIndex = 0;
    setSleep(0);
  },
});

/** A manual action (play/pause, station change, volume drag) during the
 *  fade means the listener is awake: cancel the fade + timer and restore
 *  the volume. No-op outside the fade. */
function cancelSleepFadeOnUserAction(): void {
  if (!sleepFade.active) return;
  sleepIndex = 0;
  setSleep(0);
}

function setSleep(minutes: number): void {
  if (sleepTimer !== undefined) {
    window.clearTimeout(sleepTimer);
    sleepTimer = undefined;
  }
  sleepFade.cancel();
  if (minutes === 0) {
    $npSleep.classList.remove('is-fav');
    $npSleepChip.hidden = true;
    $npSleepChip.textContent = '';
    $npSleep.setAttribute('aria-label', 'Sleep timer');
    renderMiniPlayer(currentNP);
    return;
  }
  $npSleep.classList.add('is-fav');
  $npSleepChip.hidden = false;
  $npSleepChip.textContent = `${minutes}m`;
  $npSleep.setAttribute('aria-label', `Sleep timer · ${minutes}m`);
  // Start the fade SLEEP_FADE_MS before the deadline; the fade's own
  // timer does the pause at the deadline.
  sleepTimer = window.setTimeout(() => {
    sleepTimer = undefined;
    sleepFade.start();
  }, Math.max(0, minutes * 60 * 1000 - SLEEP_FADE_MS));
  renderMiniPlayer(currentNP);
}

// ─────────────────────────────────────────────────────────────
// Event wiring
// ─────────────────────────────────────────────────────────────

// Bottom tab bar (mobile) and top-nav section links (desktop) both carry
// the Browse / Favorites / Library buttons; one handler serves both.
function handleNavClick(e: Event): void {
  const target = e.target as HTMLElement;
  const btn = target.closest<HTMLButtonElement>('.tab-btn');
  if (!btn) return;
  const raw = btn.dataset.tab;
  // Opening Library always lands on its home (openListId cleared), not a
  // stale list detail from a previous visit.
  if (raw === 'library') openListId = null;
  if (raw) setTab(raw as Tab);
}
$tabbar.addEventListener('click', handleNavClick);
$topnavNav.addEventListener('click', handleNavClick);

// Desktop "close player" (×, NP top-right): stop playback and dismiss the
// player. miniClose() clears the station, which hides the mini bar and bounces
// the Playing tab back to the last list (updateNowPlaying's stationLost path).
// The back chevron (np-back, top-left) stays for "leave NP but keep listening".
$npClose.addEventListener('click', () => miniClose());

$search.addEventListener('input', () => {
  // Typing while the Now Playing destination is open jumps back to Browse:
  // the results belong on a list, so we leave NP (which reveals the
  // mini-player) and let Browse run the query. setTab('browse') re-runs
  // runQuery itself, so the debounced handler below just refreshes it.
  if (activeTab === 'playing' && $search.value.trim()) setTab('browse');
  syncSearchClear();
});
$search.addEventListener(
  'input',
  debounce(() => {
    void runQuery();
    if ($search.value.trim()) track('search');
  }, 300),
);

// ─── Filter sheet (port of the iOS BrowseFiltersSheet) ───
// Selections pile up in a draft while the sheet is open; they apply to
// the live filter only when the user taps "Show N stations" (iOS parity).
type BfSection = 'genre' | 'country' | 'quality';
const draftGenres = new Set<string>();
const draftCountries = new Set<string>();
let draftNews = false;
const draftQuality = new Set<QualityBucket>();
const bfExpanded = new Set<BfSection>();
let bfCountrySearch = '';

function copySet<T>(dst: Set<T>, src: Iterable<T>): void {
  dst.clear();
  for (const v of src) dst.add(v);
}

function draftIsEmpty(): boolean {
  return (
    draftGenres.size === 0 && draftCountries.size === 0 && !draftNews && draftQuality.size === 0
  );
}

/** Abbreviate large match counts ("1.4k") so the apply pill stays narrow,
 *  matching the discovery chips. */
function bfCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(n);
}

const QUALITY_BUCKETS: { id: QualityBucket; label: string; level: number }[] = [
  { id: 'low', label: 'Low', level: 2 },
  { id: 'medium', label: 'Medium', level: 3 },
  { id: 'high', label: 'High', level: 4 },
];

const BF_SECTION_ICON: Record<BfSection, string> = {
  genre:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
  country:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 22V4"/><path d="M5 4h12l-2 4 2 4H5"/></svg>',
  quality:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 20v-4"/><path d="M12 20V9"/><path d="M19 20V4"/></svg>',
};
const CHECK_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

/** A multi-select pick row (label + optional leading glyph + checkmark),
 *  mirroring iOS `pickerRow`. */
function bfPickRow(
  label: string,
  selected: boolean,
  onToggle: () => void,
  lead?: HTMLElement,
): HTMLButtonElement {
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'bf-row';
  row.setAttribute('aria-pressed', String(selected));
  const leadEl = document.createElement('span');
  leadEl.className = 'bf-row__lead';
  if (lead) leadEl.append(lead);
  const name = document.createElement('span');
  name.className = 'bf-row__label';
  name.textContent = label;
  const check = document.createElement('span');
  check.className = 'bf-row__check';
  check.innerHTML = CHECK_SVG;
  row.append(leadEl, name, check);
  row.addEventListener('click', onToggle);
  return row;
}

/** The 4-bar ascending quality meter, sized for a pick row (iOS
 *  qualityMeterGraphic). */
function bfQualityMeter(level: number): HTMLElement {
  const meter = document.createElement('span');
  meter.className = 'bf-meter';
  for (let i = 0; i < 4; i += 1) {
    const bar = document.createElement('span');
    bar.className = 'bf-meter__bar' + (i < level ? ' is-on' : '');
    meter.append(bar);
  }
  return meter;
}

/** Repaint the footer: live "Show N stations" count + Clear visibility. */
function bfUpdateFooter(): void {
  const count = filterMatchCount(draftGenres, draftCountries, draftNews, draftQuality);
  $bfApply.textContent = count === 1 ? 'Show 1 station' : `Show ${bfCount(count)} stations`;
  $bfApply.disabled = count === 0;
  $bfApply.classList.toggle('is-active', count > 0);
  $bfClear.hidden = draftIsEmpty();
}

/** Build one collapsible section (header + body) into the sheet. */
function bfSection(
  section: BfSection,
  title: string,
  badgeCount: number,
  fillBody: (body: HTMLElement) => void,
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'bf-section';

  const head = document.createElement('button');
  head.type = 'button';
  head.className = 'bf-section__head';
  const expanded = bfExpanded.has(section);
  head.setAttribute('aria-expanded', String(expanded));
  head.innerHTML =
    `<span class="bf-section__icon${badgeCount > 0 ? ' is-on' : ''}">${BF_SECTION_ICON[section]}</span>` +
    `<span class="bf-section__title">${title}</span>` +
    (badgeCount > 0 ? `<span class="bf-section__badge">${badgeCount}</span>` : '') +
    `<span class="bf-section__chev" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${expanded ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'}"/></svg></span>`;
  head.addEventListener('click', () => {
    if (bfExpanded.has(section)) bfExpanded.delete(section);
    else bfExpanded.add(section);
    renderFilterSheet();
  });
  wrap.append(head);

  if (expanded) {
    const body = document.createElement('div');
    body.className = 'bf-section__body';
    fillBody(body);
    wrap.append(body);
  }
  return wrap;
}

/** Rebuild the filter sheet sections from the current draft. */
function renderFilterSheet(): void {
  $bfSections.replaceChildren();

  // Genre — News toggle first, then the taxonomy.
  $bfSections.append(
    bfSection('genre', 'Genre', draftGenres.size + (draftNews ? 1 : 0), (body) => {
      body.append(
        bfPickRow('News', draftNews, () => {
          draftNews = !draftNews;
          renderFilterSheet();
        }),
      );
      for (const g of GENRES) {
        // News has its own dedicated toggle above (iOS parity), so skip
        // the 'news' genre here to avoid showing it twice.
        if (g.id === 'news') continue;
        body.append(
          bfPickRow(g.label, draftGenres.has(g.id), () => {
            if (draftGenres.has(g.id)) draftGenres.delete(g.id);
            else draftGenres.add(g.id);
            renderFilterSheet();
          }),
        );
      }
    }),
  );

  // Country — search box, selected pinned to the top.
  $bfSections.append(
    bfSection('country', 'Country', draftCountries.size, (body) => {
      const search = document.createElement('div');
      search.className = 'bf-search';
      search.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';
      const input = document.createElement('input');
      input.type = 'search';
      input.className = 'bf-search__input';
      input.placeholder = 'Search countries…';
      input.value = bfCountrySearch;
      input.setAttribute('aria-label', 'Search countries');
      const list = document.createElement('div');
      list.className = 'bf-country-list';
      const paint = (): void => {
        list.replaceChildren();
        for (const code of orderedDraftCountries()) {
          const flag = document.createElement('span');
          flag.className = 'bf-row__flag';
          flag.textContent = flagEmoji(code) ?? '';
          list.append(
            bfPickRow(
              `${countryName(code)} (${code})`,
              draftCountries.has(code),
              () => {
                if (draftCountries.has(code)) draftCountries.delete(code);
                else draftCountries.add(code);
                // Targeted repaint keeps the search input focused.
                paint();
                bfUpdateBadges();
                bfUpdateFooter();
              },
              flag,
            ),
          );
        }
      };
      input.addEventListener('input', () => {
        bfCountrySearch = input.value;
        paint();
      });
      search.append(input);
      body.append(search, list);
      paint();
    }),
  );

  // Quality — Low / Medium / High buckets with the ascending meter.
  $bfSections.append(
    bfSection('quality', 'Quality', draftQuality.size, (body) => {
      for (const q of QUALITY_BUCKETS) {
        body.append(
          bfPickRow(
            q.label,
            draftQuality.has(q.id),
            () => {
              if (draftQuality.has(q.id)) draftQuality.delete(q.id);
              else draftQuality.add(q.id);
              renderFilterSheet();
            },
            bfQualityMeter(q.level),
          ),
        );
      }
    }),
  );

  bfUpdateFooter();
}

/** Update only the section header badges in place (used by the country
 *  list's targeted repaint so the search input keeps focus). */
function bfUpdateBadges(): void {
  const counts: Record<BfSection, number> = {
    genre: draftGenres.size + (draftNews ? 1 : 0),
    country: draftCountries.size,
    quality: draftQuality.size,
  };
  const heads = $bfSections.querySelectorAll<HTMLElement>('.bf-section');
  const order: BfSection[] = ['genre', 'country', 'quality'];
  heads.forEach((wrap, i) => {
    const section = order[i];
    const icon = wrap.querySelector('.bf-section__icon');
    icon?.classList.toggle('is-on', counts[section] > 0);
    let badge = wrap.querySelector<HTMLElement>('.bf-section__badge');
    const chev = wrap.querySelector('.bf-section__chev');
    if (counts[section] > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'bf-section__badge';
        chev?.before(badge);
      }
      badge.textContent = String(counts[section]);
    } else {
      badge?.remove();
    }
  });
}

/** Draft countries ordered for display: selected pinned to the top, the
 *  rest filtered by the search box. Mirrors iOS `orderedCountries`. */
function orderedDraftCountries(): string[] {
  const all = catalogCountries();
  const q = bfCountrySearch.trim().toLowerCase();
  const matches = (code: string): boolean => {
    if (!q) return true;
    return `${countryName(code)} ${code}`.toLowerCase().includes(q);
  };
  if (draftCountries.size === 0) return all.filter(matches);
  const selected = all.filter((c) => draftCountries.has(c));
  const rest = all.filter((c) => !draftCountries.has(c) && matches(c));
  return [...selected, ...rest];
}

function applyDraftFilter(): void {
  copySet(filterGenres, draftGenres);
  copySet(filterCountries, draftCountries);
  filterNews = draftNews;
  copySet(activeQuality, draftQuality);
  // An empty filter narrows nothing — applying it means "show everything",
  // so drop into the flat browse-all list (matching the footer's "Show N
  // stations" count) instead of bouncing back to the discovery landing.
  browseAll = draftIsEmpty();
  syncFilterDot();
  openFilterSheet(false);
  track('filter/apply');
  void runQuery();
}

$bfCancel.addEventListener('click', () => openFilterSheet(false));
$bfClear.addEventListener('click', () => {
  draftGenres.clear();
  draftCountries.clear();
  draftNews = false;
  draftQuality.clear();
  renderFilterSheet();
});
$bfApply.addEventListener('click', () => {
  if ($bfApply.disabled) return;
  applyDraftFilter();
});

$searchClear.addEventListener('click', () => {
  clearSearch(true);
  void runQuery();
});

$wordmark.addEventListener('click', goHome);

// ─── PSA banner: redesign + iPhone-app announcement ───
// Vintage-tuner banner under the top nav linking to the /ios landing. It's a
// discovery-landing-only banner that returns on every load — no persisted
// dismissal and no close button — so syncPromo() tracks whether the Browse
// discovery landing is showing. It hides the moment the user drops into
// Browse-all, a search, a filter, or any other tab (Favorites, lists, …).
// The CTA opens in a new tab (target="_blank" in the markup) so playback keeps
// going. renderContent() calls syncPromo() on every render so Browse-all and
// back-to-discovery flip the banner without a tab change.
const $promo = document.getElementById('promo') as HTMLElement;
const $promoLink = document.getElementById('promo-link') as HTMLAnchorElement;
/** Reveal the promo only on the Browse discovery landing. */
function syncPromo(): void {
  $promo.hidden = !inDiscovery();
}
syncPromo();
$promoLink.addEventListener('click', () => track('promo/ios'));
// Permanent /ios entry on the About page (the banner above is dismissable).
document.getElementById('about-ios')?.addEventListener('click', () => track('about/ios'));

$addForm.addEventListener('submit', handleAddSubmit);

$listCancel.addEventListener('click', () => closeAddList());
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && $listSheet.classList.contains('open')) closeAddList();
  if (e.key === 'Escape' && $filterSheet.classList.contains('open')) openFilterSheet(false);
});

// ─── Top-toolbar sheets: filters (funnel) + settings (gear) ───

/** Open/close the filter sheet. On open, seed the draft from the live
 *  filter, auto-expand any section that already carries a selection, and
 *  build the sheet (mirrors how the iOS sheet starts from `initial`). */
function openFilterSheet(open: boolean): void {
  if (open) {
    copySet(draftGenres, filterGenres);
    copySet(draftCountries, filterCountries);
    draftNews = filterNews;
    copySet(draftQuality, activeQuality);
    bfCountrySearch = '';
    bfExpanded.clear();
    if (draftGenres.size > 0 || draftNews) bfExpanded.add('genre');
    if (draftCountries.size > 0) bfExpanded.add('country');
    if (draftQuality.size > 0) bfExpanded.add('quality');
    renderFilterSheet();
  }
  $filterSheet.classList.toggle('open', open);
  $filterSheet.setAttribute('aria-hidden', String(!open));
}
function openSettingsSheet(open: boolean): void {
  if (open) {
    syncThemeSeg();
    syncAccentSeg();
    syncLandingSeg();
    syncMusicToggles();
  }
  $settingsSheet.classList.toggle('open', open);
  $settingsSheet.setAttribute('aria-hidden', String(!open));
}

// ─── Settings sheet tabs: Settings · About · Add ───
// About + Add used to be their own slide-up sheets; their markup is
// authored once in #about-src / #add-src and relocated into the matching
// tab panels here at boot (same pattern as the filter-row relocation),
// so every id + handler inside them is preserved.
type SettingsTab = 'settings' | 'about' | 'add';
const settingsPanels = new Map<SettingsTab, HTMLElement>();
for (const panel of $settingsSheet.querySelectorAll<HTMLElement>('[data-settings-panel]')) {
  settingsPanels.set(panel.dataset.settingsPanel as SettingsTab, panel);
}
function relocateInto(srcId: string, tab: SettingsTab): void {
  const src = document.getElementById(srcId);
  const panel = settingsPanels.get(tab);
  if (src && panel) panel.append(...Array.from(src.childNodes));
}
relocateInto('about-src', 'about');
relocateInto('add-src', 'add');

// About hero (mirrors the iOS AboutContentView header): stamp the build
// version, and reveal the share button only where Web Share is supported.
declare const __BUILD_VERSION__: string;
const $aboutVersion = document.getElementById('about-version');
if ($aboutVersion) {
  const v = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev';
  $aboutVersion.textContent = `Version ${v}`;
}
const $aboutShare = document.getElementById('about-share');
if ($aboutShare && typeof navigator.share === 'function') {
  $aboutShare.hidden = false;
  $aboutShare.addEventListener('click', () => {
    void navigator
      .share({
        title: 'rrradio.org',
        text: 'Free internet radio without ads.',
        url: 'https://rrradio.org',
      })
      .catch(() => {
        /* user dismissed the share sheet — nothing to do */
      });
  });
}

function selectSettingsTab(tab: SettingsTab): void {
  for (const btn of $settingsTabs.querySelectorAll<HTMLButtonElement>('.sheet-tab')) {
    const on = btn.dataset.settingsTab === tab;
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-selected', String(on));
  }
  for (const [key, panel] of settingsPanels) panel.hidden = key !== tab;
  // Scroll the body back to the top when switching tabs.
  const body = $settingsSheet.querySelector<HTMLElement>('.sheet-body');
  if (body) body.scrollTop = 0;
}

$settingsTabs.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.sheet-tab');
  if (!btn) return;
  selectSettingsTab((btn.dataset.settingsTab as SettingsTab) ?? 'settings');
});

/** Lights the funnel's accent dot when any filter narrows Browse. */
function syncFilterDot(): void {
  $filterDot.hidden = !hasActiveFilter();
}

$filterBtn.addEventListener('click', () => openFilterSheet(true));
$filterClose.addEventListener('click', () => openFilterSheet(false));
$settingsBtn.addEventListener('click', () => {
  openSettingsSheet(true);
  selectSettingsTab('settings');
});
$settingsClose.addEventListener('click', () => openSettingsSheet(false));
// ─── Settings: theme · landing page · music services ───
const LANDING_KEY = 'rrradio.landing';
type MusicService = 'apple' | 'spotify' | 'youtube';
const MS_KEYS: Record<MusicService, string> = {
  apple: 'rrradio.ms.apple',
  spotify: 'rrradio.ms.spotify',
  youtube: 'rrradio.ms.youtube',
};
/** Music-service deep-links default ON; '0' = user turned it off. */
function msEnabled(svc: MusicService): boolean {
  return getString(MS_KEYS[svc]) !== '0';
}

function syncSeg(seg: HTMLElement, attr: string, value: string): void {
  for (const btn of seg.querySelectorAll<HTMLButtonElement>('.seg__btn')) {
    const on = btn.dataset[attr] === value;
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-checked', String(on));
  }
}
function syncThemeSeg(): void {
  syncSeg($themeSeg, 'themeChoice', readStoredTheme() ?? 'system');
}
/** Reflect the custom accent for the *current* appearance: Standard vs
 *  Custom segment, the picker swatch, and whether the picker row shows. */
function syncAccentSeg(): void {
  const theme = effectiveTheme();
  const custom = readAccent(theme);
  syncSeg($accentSeg, 'accent', custom ? 'custom' : 'standard');
  $accentRow.hidden = !custom;
  $accentPicker.value = custom ?? DEFAULT_ACCENT[theme];
}
function syncLandingSeg(): void {
  syncSeg($landingSeg, 'landing', getString(LANDING_KEY) || 'browse');
}
function syncMusicToggles(): void {
  $msApple.setAttribute('aria-pressed', String(msEnabled('apple')));
  $msSpotify.setAttribute('aria-pressed', String(msEnabled('spotify')));
  $msYoutube.setAttribute('aria-pressed', String(msEnabled('youtube')));
}
/** Hide the open-in links the user turned off in Settings. */
function syncMusicServiceLinks(): void {
  $npTrackAppleMusic.style.display = msEnabled('apple') ? '' : 'none';
  $npTrackSpotify.style.display = msEnabled('spotify') ? '' : 'none';
  $npTrackYoutubeMusic.style.display = msEnabled('youtube') ? '' : 'none';
}

// Now Playing Best / Data-saver toggle (ADR 001, #623): persist the
// global preference, re-play the current station on the chosen variant,
// and repaint the active segment.
$npQualitySeg.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-quality]');
  const pref = btn?.dataset.quality;
  if (pref !== 'best' && pref !== 'data') return;
  if (pref === player.getQualityPref()) return;
  setQualityPref(pref);
  player.setQualityPref(pref);
  renderNowPlaying(player.getCurrent());
  track(`quality/${pref}`);
});

$themeSeg.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.seg__btn');
  if (!btn) return;
  const choice = btn.dataset.themeChoice ?? 'system';
  applyTheme(choice === 'system' ? null : (choice as 'light' | 'dark'));
  syncThemeSeg();
  // The effective appearance may have changed — repaint the accent picker
  // for the appearance now in effect (applyAccent already ran via the
  // onThemeApplied hook).
  syncAccentSeg();
  track(`theme/${choice}`);
});
// Accent: Standard clears the override; Custom seeds from the current value
// (default if none) so the override takes effect immediately, then the
// swatch fine-tunes it live. Stored per appearance.
$accentSeg.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.seg__btn');
  if (!btn) return;
  const choice = btn.dataset.accent ?? 'standard';
  const theme = effectiveTheme();
  setAccent(theme, choice === 'custom' ? readAccent(theme) ?? DEFAULT_ACCENT[theme] : null);
  syncAccentSeg();
  track(`accent/${choice}`);
});
$accentPicker.addEventListener('input', () => {
  setAccent(effectiveTheme(), $accentPicker.value);
});
$landingSeg.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.seg__btn');
  if (!btn) return;
  const landing = btn.dataset.landing ?? 'browse';
  setString(LANDING_KEY, landing);
  syncLandingSeg();
  track(`landing/${landing}`);
});
function bindMusicToggle(btn: HTMLButtonElement, svc: MusicService): void {
  btn.addEventListener('click', () => {
    const next = !msEnabled(svc);
    setString(MS_KEYS[svc], next ? '1' : '0');
    syncMusicToggles();
    syncMusicServiceLinks();
    track(`music-service/${svc}/${next ? 'on' : 'off'}`);
  });
}
bindMusicToggle($msApple, 'apple');
bindMusicToggle($msSpotify, 'spotify');
bindMusicToggle($msYoutube, 'youtube');

$settingsBackup.addEventListener('click', () => {
  openSettingsSheet(false);
  exportBackupNow();
});
$settingsRestore.addEventListener('click', () => {
  openSettingsSheet(false);
  pickImportFile();
});

$settingsStats.addEventListener('click', () => {
  openSettingsSheet(false);
  void openDashboardSheet(true);
});

// ─── Backup & restore ──────────────────────────────────────────────
// UI lives on the Favorites tab header (icons rendered by
// renderFavoritesActions below). About sheet describes the feature
// but holds no buttons of its own. A small toast under the topbar
// reports the result; replaces itself on the next action.

let $backupToast: HTMLElement | null = null;
let backupToastTimer: ReturnType<typeof setTimeout> | null = null;

function showBackupToast(text: string, tone: 'ok' | 'err'): void {
  if (!$backupToast) {
    $backupToast = document.createElement('div');
    $backupToast.className = 'backup-toast';
    $backupToast.setAttribute('role', 'status');
    document.body.appendChild($backupToast);
  }
  $backupToast.textContent = text;
  $backupToast.dataset.tone = tone;
  $backupToast.hidden = false;
  if (backupToastTimer) clearTimeout(backupToastTimer);
  // Errors persist longer — users need time to read them.
  backupToastTimer = setTimeout(() => {
    if ($backupToast) $backupToast.hidden = true;
  }, tone === 'err' ? 7_000 : 4_000);
}

/** Snapshot the current app settings for a backup. Reads through the
 *  same accessors the app uses at boot, so the file always reflects the
 *  live state. Omitted (undefined) keys drop out of the JSON. */
function collectSettings(): BackupSettings {
  const accent: { light?: string; dark?: string } = {};
  const lightAccent = readAccent('light');
  const darkAccent = readAccent('dark');
  if (lightAccent) accent.light = lightAccent;
  if (darkAccent) accent.dark = darkAccent;
  return {
    theme: readStoredTheme() ?? undefined,
    accent: Object.keys(accent).length > 0 ? accent : undefined,
    landing: getString(LANDING_KEY) ?? undefined,
    musicServices: {
      apple: msEnabled('apple'),
      spotify: msEnabled('spotify'),
      youtube: msEnabled('youtube'),
    },
  };
}

/** Apply imported settings live — persist each key and call the same
 *  apply functions the toggles use, so a restore takes effect without a
 *  reload. Only the keys present in the file are touched. */
function applySettings(s: BackupSettings): void {
  if (s.theme) applyTheme(s.theme);
  if (s.accent) {
    if (s.accent.light) setAccent('light', s.accent.light);
    if (s.accent.dark) setAccent('dark', s.accent.dark);
  }
  if (s.landing) setString(LANDING_KEY, s.landing);
  if (s.musicServices) {
    const ms = s.musicServices;
    if (typeof ms.apple === 'boolean') setString(MS_KEYS.apple, ms.apple ? '1' : '0');
    if (typeof ms.spotify === 'boolean') setString(MS_KEYS.spotify, ms.spotify ? '1' : '0');
    if (typeof ms.youtube === 'boolean') setString(MS_KEYS.youtube, ms.youtube ? '1' : '0');
    syncMusicServiceLinks();
  }
}

function exportBackupNow(): void {
  const favs = getFavorites();
  const cus = getCustom();
  const lists = getLists();
  const recents = getRecents();
  const settings = collectSettings();
  const text = serializeBackup(favs, cus, lists, recents, settings);
  // Blob URL + temporary anchor for the download. Works on desktop;
  // iOS Safari opens inline (Save to Files is two taps from there).
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = backupFilename();
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  const exportParts: string[] = [];
  if (favs.length > 0) exportParts.push(`${favs.length} favorite${favs.length === 1 ? '' : 's'}`);
  if (cus.length > 0) exportParts.push(`${cus.length} custom station${cus.length === 1 ? '' : 's'}`);
  if (lists.length > 0) exportParts.push(`${lists.length} list${lists.length === 1 ? '' : 's'}`);
  if (recents.length > 0)
    exportParts.push(`${recents.length} recent${recents.length === 1 ? '' : 's'}`);
  exportParts.push('settings');
  showBackupToast(`Exported ${joinNatural(exportParts)}.`, 'ok');
  track(
    'backup-export',
    `favs=${favs.length} custom=${cus.length} lists=${lists.length} recents=${recents.length}`,
  );
}

/** "a", "a and b", "a, b and c" — matches backup.ts's joinParts wording. */
function joinNatural(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function importBackupFromFile(file: File): void {
  const reader = new FileReader();
  reader.onerror = () => showBackupToast("Couldn't read that file.", 'err');
  reader.onload = () => {
    try {
      const text = String(reader.result ?? '');
      const snap = parseBackup(text);
      const summary = mergeSnapshot(
        getFavorites(),
        getCustom(),
        getLists(),
        getRecents(),
        snap,
      );
      setFavorites(summary.mergedFavorites);
      setCustom(summary.mergedCustom);
      setLists(summary.mergedLists);
      setRecents(summary.mergedRecents);
      applySettings(summary.mergedSettings);
      showBackupToast(summaryMessage(summary), 'ok');
      track(
        'backup-import',
        `favsAdded=${summary.favoritesAdded} customAdded=${summary.customAdded} listsAdded=${summary.listsAdded} recentsAdded=${summary.recentsAdded} settings=${summary.settingsApplied}`,
      );
      void runQuery();
      renderCustomList();
      // Favorites / Library / Recents all may have changed — re-render if visible.
      if (activeTab === 'fav' || isLibraryTab(activeTab)) renderContent();
    } catch (err) {
      const msg =
        err instanceof BackupParseError
          ? err.message
          : `Import failed: ${err instanceof Error ? err.message : String(err)}`;
      showBackupToast(msg, 'err');
    }
  };
  reader.readAsText(file);
}

/** Trigger the file-picker. The input is created on demand so the DOM
 *  doesn't carry a permanently-mounted hidden file input. */
function pickImportFile(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'application/json,.json';
  input.style.display = 'none';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) importBackupFromFile(file);
    input.remove();
  });
  document.body.appendChild(input);
  input.click();
}

/** Build the export + import icon buttons for the Favorites tab header.
 *  The backup now carries everything (favorites, custom, lists, recents
 *  and settings), so export is always available — even an empty dial
 *  exports the user's settings. Import is how a fresh device populates. */
/** Builds a 32×32 section-label icon button (search / + / trash / backup). */
function headerActionBtn(icon: string, label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'section-label__action';
  btn.setAttribute('aria-label', label);
  btn.title = label;
  btn.innerHTML = icon;
  btn.addEventListener('click', onClick);
  return btn;
}

// Mobile favorites header — iOS LibraryPageStatusBar accessories.
// Left: search; right: add-to-favorites (+) and edit/remove mode (trash).
// The iOS LibraryPageStatusBar search accessory — shared by every feed
// page (Favorites, Library home, list detail, Recents). On mobile the
// brand-row search field is hidden (body.tab-fav / .tab-library); tapping
// this expands it (body.search-open).
function headerSearchLead(label = 'Search'): HTMLElement {
  const btn = headerActionBtn(ICON_SEARCH, label, toggleHeaderSearch);
  if ($body.classList.contains('search-open')) btn.classList.add('is-active');
  return btn;
}

function favoriteHeaderActions(): HTMLElement[] {
  // "+" enters Browse multi-select targeting Favorites (iOS #57): tick
  // stations, then "Add N to Favorites" adds them all and returns here.
  const addBtn = headerActionBtn(ICON_PLUS, 'Add stations to favorites', enterFavoriteSelect);
  const trashBtn = headerActionBtn(ICON_TRASH, 'Remove favorites', toggleFavEditing);
  if (favEditing) trashBtn.classList.add('is-active');
  return [addBtn, trashBtn];
}

// ── Browse multi-select "add to <target>" (iOS #57) ───────────────────
function enterFavoriteSelect(): void {
  if (favEditing) favEditing = false;
  selectMode = { target: { kind: 'favorites' }, picked: new Map(), existing: favIdSet() };
  setTab('browse'); // setTab won't clear selectMode when going to browse
  syncSelectDock();
}

function enterListSelect(list: StationList): void {
  selectMode = {
    target: { kind: 'list', id: list.id, name: list.name },
    picked: new Map(),
    existing: new Set(list.stations.map((s) => s.id)),
  };
  setTab('browse');
  syncSelectDock();
}

function exitSelectMode(): void {
  selectMode = null;
  syncSelectDock();
  if (activeTab === 'browse') renderContent();
}

function saveSelect(): void {
  if (!selectMode || selectMode.picked.size === 0) return;
  const { target, picked } = selectMode;
  const count = picked.size;
  // The list the stations land in — null for Favorites. A 'newList' target is
  // created here (iOS atomic create+add) so a cancelled flow leaves no list.
  let listId: string | null = null;
  if (target.kind === 'favorites') {
    for (const station of picked.values()) {
      if (!isFavorite(station.id)) toggleFavorite(station);
    }
  } else {
    if (target.kind === 'newList') {
      listId = createList(target.name).id;
      track('list-create');
    } else {
      listId = target.id;
    }
    for (const station of picked.values()) addToList(listId, station);
  }
  selectMode = null;
  syncSelectDock();
  track(`${target.kind === 'favorites' ? 'favorites' : 'list'}/add-multi: ${count}`);
  renderTopBar();
  // The Browse search is the shared global field — clear it so the target
  // opens on its full list, not filtered by what was typed to find these.
  clearSearch(false);
  if (listId) {
    openListId = listId;
    setTab('library');
  } else {
    setTab('fav');
  }
}

/** Toggle a station's pick in select mode and refresh its tick + the dock. */
function toggleSelectPick(station: Station, tick: HTMLElement): void {
  if (!selectMode) return;
  if (selectMode.picked.has(station.id)) {
    selectMode.picked.delete(station.id);
    tick.classList.remove('is-picked');
    tick.innerHTML = '';
  } else {
    selectMode.picked.set(station.id, station);
    tick.classList.add('is-picked');
    tick.innerHTML = ICON_CHECK;
  }
  syncSelectDock();
}

/** Build / update / tear down the accent-tinted select dock pinned above
 *  the station list (the iOS BrowseSelectionDock). Lives outside #content
 *  so a search re-render (runQuery → replaceChildren) can't wipe it. */
function syncSelectDock(): void {
  if (!selectMode) {
    $selectDock?.remove();
    $selectDock = null;
    $body.classList.remove('selecting');
    return;
  }
  if (!$selectDock) {
    $selectDock = document.createElement('div');
    $selectDock.className = 'select-dock';
    const stage = document.querySelector('.stage');
    stage?.parentElement?.insertBefore($selectDock, stage);
    $body.classList.add('selecting');
  }
  const n = selectMode.picked.size;

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'select-dock__cancel';
  cancel.setAttribute('aria-label', 'Cancel');
  cancel.innerHTML = ICON_CLOSE;
  cancel.addEventListener('click', exitSelectMode);

  const label = document.createElement('div');
  label.className = 'select-dock__label';
  const lead = document.createElement('span');
  lead.textContent = 'Adding to ';
  const target = document.createElement('b');
  target.textContent =
    selectMode.target.kind === 'favorites' ? 'Favorites' : selectMode.target.name;
  label.append(lead, target);
  if (n > 0) {
    const badge = document.createElement('span');
    badge.className = 'select-dock__count';
    badge.textContent = String(n);
    label.append(badge);
  }

  const save = document.createElement('button');
  save.type = 'button';
  save.className = 'select-dock__save';
  save.disabled = n === 0;
  save.textContent = n > 0 ? `Add ${n}` : 'Select stations';
  save.addEventListener('click', saveSelect);

  const row = document.createElement('div');
  row.className = 'select-dock__row';
  row.append(cancel, label, save);

  // The step strip rides above the action row only when this select reached
  // step 2 via the choose-list popup (Favorites / existing-list "+" skip it).
  if (selectMode.withSteps) $selectDock.replaceChildren(buildStepStrip(2), row);
  else $selectDock.replaceChildren(row);
}

function toggleFavEditing(): void {
  favEditing = !favEditing;
  if (activeTab === 'fav') renderContent();
}

function toggleLibEditing(): void {
  libEditing = !libEditing;
  if (activeTab === 'library') renderContent();
}

function toggleHeaderSearch(): void {
  const opening = !$body.classList.contains('search-open');
  $body.classList.toggle('search-open', opening);
  if (opening) {
    $search.focus();
  } else {
    clearSearch(false);
  }
  renderContent();
}

/** Prepend a remove (−) button to each favorite card in edit mode. */
function decorateFavoriteRemoval(grid: HTMLElement, list: Station[]): void {
  decorateRowRemoval(grid, list, (station) => ({
    label: `Remove ${station.name} from favorites`,
    // un-favorites + re-renders (stays in edit mode)
    onRemove: () => onToggleFav(station),
  }));
}

/** Edit mode (iOS delete mode): prepend a red remove (−) button to each
 *  row of `grid`, which renders `list` in order. Shared by Favorites and
 *  list detail. */
function decorateRowRemoval(
  grid: HTMLElement,
  list: Station[],
  removal: (station: Station) => { label: string; onRemove: () => void },
): void {
  grid.classList.add('rows--editing');
  const rows = grid.querySelectorAll<HTMLElement>(':scope > .row');
  rows.forEach((row, i) => {
    const station = list[i];
    if (!station) return;
    const { label, onRemove } = removal(station);
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'row-remove';
    rm.setAttribute('aria-label', label);
    rm.innerHTML = ICON_MINUS_CIRCLE;
    rm.addEventListener('click', (e) => {
      e.stopPropagation();
      onRemove();
    });
    row.prepend(rm);
  });
}
$dashboardClose.addEventListener('click', () => void openDashboardSheet(false));

// Tapping anywhere on the mini-player bar opens the Now Playing destination
// (iOS parity) — except the transport + volume controls, which keep their own
// behavior. prev/toggle/skip already stop propagation; the closest() guard
// also covers the volume slider. The #mini-open button bubbles here too, so a
// keyboard Enter on it still expands to Now Playing.
$mini.addEventListener('click', (e) => {
  if ((e.target as HTMLElement).closest('.mini-prev, .mini-toggle, .mini-skip, .mini-volume, .mini-close')) {
    return;
  }
  // A swipe just ended — swallow the click it would otherwise fire.
  if (miniSuppressClick) {
    miniSuppressClick = false;
    return;
  }
  // Tapping while the close zone is revealed cancels it (iOS parity), rather
  // than opening Now Playing.
  if (miniRested) {
    miniSpringBack();
    return;
  }
  openNp(true);
});

// ── Mini-player swipe-to-close (iOS MiniPlayerView parity) ───────────
// Drag the bar's content left to reveal a red close zone; release past a
// small threshold rests there (tap the X to confirm), a fling / far swipe
// auto-closes. Close = player.stop(), which clears the station so the bar
// hides and the Playing tab bounces back to the list.
const MINI_REVEAL_WIDTH = 76;
const MINI_REVEAL_THRESHOLD = 40;
let miniDx = 0;
let miniRested = false;
let miniSwipePointer: number | null = null;
let miniSwipeStartX = 0;
let miniSwipeStartY = 0;
let miniSwiping = false;
let miniSuppressClick = false;
/** Last pointer sample, for the release velocity (iOS predictedEndTranslation). */
let miniLastX = 0;
let miniLastT = 0;
let miniVelocity = 0; // px/ms, negative = moving left
/** How far ahead (ms) a release "throws" the card when predicting a fling. */
const MINI_FLING_PROJECTION_MS = 200;

function miniBarWidth(): number {
  const w = $mini.getBoundingClientRect().width;
  return w > 0 ? w : 360;
}
function miniCloseThreshold(): number {
  return Math.max(miniBarWidth() * 0.5, 160);
}
function setMiniDx(dx: number, animate: boolean): void {
  miniDx = dx;
  $miniSlide.classList.toggle('is-animating', animate);
  $miniSlide.style.setProperty('--mini-dx', `${dx}px`);
  $miniClose.hidden = dx >= 0;
}
function miniSpringBack(): void {
  miniRested = false;
  setMiniDx(0, true);
}
function miniRest(): void {
  miniRested = true;
  setMiniDx(-MINI_REVEAL_WIDTH, true);
}
function miniReset(): void {
  miniRested = false;
  miniSwiping = false;
  setMiniDx(0, false);
}
function miniClose(): void {
  player.stop();
  miniReset();
  track('mini/close');
}
/** Swipe-close commit (iOS performSwipeClose): slide the content fully off
 *  the bar, then stop. The reduced-motion rule shortens the transition to
 *  ~0, so transitionend still fires promptly; the timeout is a safety net. */
function miniSwipeClose(): void {
  miniRested = false;
  $miniSlide.classList.add('is-closing');
  setMiniDx(-(miniBarWidth() + 40), false);
  let done = false;
  const finish = (): void => {
    if (done) return;
    done = true;
    $miniSlide.classList.remove('is-closing');
    miniClose();
  };
  $miniSlide.addEventListener('transitionend', finish, { once: true });
  window.setTimeout(finish, 260);
}

// The gesture lives on the whole sliding card (iOS: the DragGesture is on
// playerCard), so a swipe can start on the text, the thumb, the gaps or the
// play button — only the desktop volume slider keeps its own drag.
$miniSlide.addEventListener('pointerdown', (e) => {
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  if ((e.target as HTMLElement).closest('.mini-volume')) return;
  miniSwipePointer = e.pointerId;
  miniSwipeStartX = e.clientX;
  miniSwipeStartY = e.clientY;
  miniSwiping = false;
  miniSuppressClick = false;
  miniLastX = e.clientX;
  miniLastT = e.timeStamp;
  miniVelocity = 0;
});
$miniSlide.addEventListener('pointermove', (e) => {
  if (miniSwipePointer !== e.pointerId) return;
  const dx = e.clientX - miniSwipeStartX;
  const dy = e.clientY - miniSwipeStartY;
  if (!miniSwiping) {
    // Only claim the gesture once it's clearly a horizontal drag, so taps
    // (open NP) and vertical scrolls still pass through.
    if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
    miniSwiping = true;
    miniSuppressClick = true;
    try { $miniSlide.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  }
  e.preventDefault();
  const dt = e.timeStamp - miniLastT;
  if (dt > 0) miniVelocity = (e.clientX - miniLastX) / dt;
  miniLastX = e.clientX;
  miniLastT = e.timeStamp;
  const base = miniRested ? -MINI_REVEAL_WIDTH : 0;
  const lower = -miniBarWidth();
  setMiniDx(Math.min(0, Math.max(lower, base + dx)), false);
});
function endMiniSwipe(e: PointerEvent): void {
  if (miniSwipePointer !== e.pointerId) return;
  miniSwipePointer = null;
  if (!miniSwiping) return;
  miniSwiping = false;
  try { $miniSlide.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  const distance = -miniDx;
  // A quick flick counts as far as it would have travelled (iOS fling). A
  // finger that paused before lifting has no throw left.
  if (e.timeStamp - miniLastT > 80) miniVelocity = 0;
  const fling = distance - miniVelocity * MINI_FLING_PROJECTION_MS;
  if (distance >= miniCloseThreshold() || fling >= miniCloseThreshold()) miniSwipeClose();
  else if (distance >= MINI_REVEAL_THRESHOLD) miniRest();
  else miniSpringBack();
}
$miniSlide.addEventListener('pointerup', endMiniSwipe);
$miniSlide.addEventListener('pointercancel', endMiniSwipe);

$miniClose.addEventListener('click', (e) => {
  e.stopPropagation();
  miniClose();
});
// Desktop-only "×" in the bar's top-right corner (no swipe on desktop).
$miniCloseX.addEventListener('click', (e) => {
  e.stopPropagation();
  miniClose();
});

// ── Swipe between the main destinations (iOS root-swipe parity) ──────
// A horizontal TOUCH swipe on the content area cycles Browse ⇄ Favorites ⇄
// Library. Threshold-based (commit on release) so it doesn't fight vertical
// scroll; guarded against the horizontal chip carousels, card reorder grips,
// open sheets, multi-select, and list-detail sub-views. Touch-only — desktop
// keeps mouse selection/drag.
const PAGE_SWIPE_TABS: Tab[] = ['browse', 'fav', 'library'];
const PAGE_SWIPE_THRESHOLD = 60;
let pageSwipePointer: number | null = null;
let pageSwipeStartX = 0;
let pageSwipeStartY = 0;
let pageSwipeArmed = false;

/** True when the start point sits inside a horizontally scrollable element
 *  (a chip carousel / featured rail) — those own the horizontal gesture. */
function hasHorizontalScrollAncestor(start: HTMLElement | null): boolean {
  let el: HTMLElement | null = start;
  while (el && el !== $content) {
    if (el.scrollWidth > el.clientWidth + 4) {
      const ox = getComputedStyle(el).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
    el = el.parentElement;
  }
  return false;
}

function pageSwipeEligible(target: HTMLElement | null): boolean {
  if (!PAGE_SWIPE_TABS.includes(activeTab)) return false;
  if (openListId || listCreateOpen || selectMode) return false;
  if (!$npInfo.hidden) return false;
  if (document.querySelector('.sheet.open')) return false;
  if (hasHorizontalScrollAncestor(target)) return false;
  if (target?.closest('.row-grip, input, .np-volume')) return false;
  return true;
}

$content.addEventListener('pointerdown', (e) => {
  if (e.pointerType !== 'touch') return;
  if (!pageSwipeEligible(e.target as HTMLElement)) {
    pageSwipeArmed = false;
    return;
  }
  pageSwipePointer = e.pointerId;
  pageSwipeStartX = e.clientX;
  pageSwipeStartY = e.clientY;
  pageSwipeArmed = true;
});
$content.addEventListener('pointerup', (e) => {
  if (!pageSwipeArmed || pageSwipePointer !== e.pointerId) return;
  pageSwipeArmed = false;
  pageSwipePointer = null;
  const dx = e.clientX - pageSwipeStartX;
  const dy = e.clientY - pageSwipeStartY;
  // Mostly-horizontal and past the threshold (so vertical scroll + small
  // taps never switch pages).
  if (Math.abs(dx) < PAGE_SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
  const idx = PAGE_SWIPE_TABS.indexOf(activeTab);
  if (idx < 0) return;
  const next = dx < 0 ? idx + 1 : idx - 1; // swipe left → next destination
  if (next < 0 || next >= PAGE_SWIPE_TABS.length) return;
  setTab(PAGE_SWIPE_TABS[next]);
  track(`page-swipe/${PAGE_SWIPE_TABS[next]}`);
});
$content.addEventListener('pointercancel', () => {
  pageSwipeArmed = false;
  pageSwipePointer = null;
});

const $npBack = document.getElementById('np-back') as HTMLButtonElement;
$npBack.addEventListener('click', () => openNp(false));
$miniToggle.addEventListener('click', (e) => {
  e.stopPropagation();
  // Disabled while the stream loads (iOS parity); the tap is swallowed, not
  // passed on to the bar's open-Now-Playing handler.
  if ($miniToggle.getAttribute('aria-disabled') === 'true') return;
  handlePlayToggle();
});

// Mini-player prev/next — same gesture as the lock-screen previous/next
// controls: step through the active queue.
$miniPrev.addEventListener('click', (e) => {
  e.stopPropagation();
  skipStation(-1);
});
$miniSkip.addEventListener('click', (e) => {
  e.stopPropagation();
  skipStation(1);
});

$npPlay.addEventListener('click', () => handlePlayToggle());

// Open in — the music-service marks are shown inline (the render toggles
// #np-track-open-in-wrap once a track verifies). Per-service Settings
// toggles hide individual marks via syncMusicServiceLinks(), called after
// each NP render. Tapping a mark just follows its link (handlers below).

// ── Station-info popup ──────────────────────────────────────────────
// Opened by tapping the station logo or the album art (iOS parity);
// carries the stream details the removed bottom strip used to expand.
function openStationInfo(): void {
  if (!currentNP.station.id) return;
  $npInfo.hidden = false;
  track('np-info/open');
}
function closeStationInfo(): void {
  $npInfo.hidden = true;
}
$npStationLogoBtn.addEventListener('click', openStationInfo);
$npTrackCoverWrap.addEventListener('click', openStationInfo);
$npTrackCoverWrap.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    openStationInfo();
  }
});
$npInfoScrim.addEventListener('click', closeStationInfo);
$npInfoClose.addEventListener('click', closeStationInfo);
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !$npInfo.hidden) closeStationInfo();
});

// ── Transport: previous / next — step through the active queue, the same
// gesture as the mini-player + lock-screen prev/next controls. ──
$npPrev.addEventListener('click', () => skipStation(-1));
$npNext.addEventListener('click', () => skipStation(1));

// ── Add the current station to a list (+ button on the name row). ──
$npAdd.addEventListener('click', () => {
  if (currentNP.station.id) openAddList({ mode: 'addNow', station: currentNP.station });
});

// Infinite scroll — load the next batch as the result list nears the bottom
// (iOS parity). pendingLoadMore is set by renderContent() to the current
// view's loader (reveal-more / fetch-next) and cleared when nothing remains.
// The async loaders self-guard against re-entry, so firing on every scroll
// tick near the bottom is safe.
$content.addEventListener(
  'scroll',
  () => {
    if (!pendingLoadMore) return;
    if ($content.scrollTop + $content.clientHeight >= $content.scrollHeight - 600) {
      pendingLoadMore();
    }
  },
  { passive: true },
);

/** If the freshly rendered list is too short to scroll, pull more so the
 *  user can actually reach the bottom (and infinite scroll can take over).
 *  Capped per view via autoFillTries. Call at the end of a browse render. */
function maybeAutoFill(): void {
  if (!pendingLoadMore || autoFillTries >= AUTO_FILL_MAX) return;
  if (browseLoadingMore) return; // a fetch is already in flight — wait for it
  if ($content.scrollHeight > $content.clientHeight + 8) return; // already scrollable
  autoFillTries++;
  pendingLoadMore();
}

// Streaming-service deep links — count taps so we can see if anyone
// uses them. Track strings stay out of telemetry.
$npTrackSpotify.addEventListener('click', () => track('open-spotify'));
$npTrackAppleMusic.addEventListener('click', () => track('open-apple-music'));
$npTrackYoutubeMusic.addEventListener('click', () => track('open-youtube-music'));
$npMute.addEventListener('click', () => {
  reflectMuteUi(player.toggleMute());
});

// ── Volume slider ─────────────────────────────────────────────────
// Desktop has no hardware volume rocker, so the Now Playing surface
// carries its own slider. Level (0–1) persists across sessions; the
// accent-filled portion is drawn by the `--vol` custom property.
const VOLUME_KEY = 'rrradio.volume.v1';

function applyVolumeUi(v: number): void {
  const pct = Math.round(v * 100);
  $npVolumeSlider.value = String(pct);
  $npVolume.style.setProperty('--vol', `${pct}%`);
  $npVolumeValue.textContent = `${pct}%`;
  // The desktop mini-player carries its own slider — keep it in lockstep
  // so the two never disagree (same shared volume state).
  $miniVolumeSlider.value = String(pct);
  $miniVolume.style.setProperty('--vol', `${pct}%`);
}

function setVolume(v: number, persist = true): void {
  const clamped = Math.max(0, Math.min(1, v));
  player.setVolume(clamped);
  applyVolumeUi(clamped);
  if (persist) setString(VOLUME_KEY, clamped.toFixed(2));
}

// Restore the stored level at boot (default full). audio.volume is
// read-only on iOS Safari, so setVolume is a no-op there — the slider
// still reflects the stored value but won't change playback (expected;
// the slider is a desktop affordance).
{
  // getString → null when unset; Number(null) is 0, which would wrongly
  // start a fresh listener at 0% volume. Treat unset/empty as "default to
  // full" rather than coercing to 0.
  const raw = getString(VOLUME_KEY);
  const stored = raw === null || raw === '' ? NaN : Number(raw);
  const initial = Number.isFinite(stored) && stored >= 0 && stored <= 1 ? stored : 1;
  setVolume(initial, false);
}

function handleVolumeInput(slider: HTMLInputElement): void {
  cancelSleepFadeOnUserAction();
  const v = Number(slider.value) / 100;
  // Dragging up from a muted state unmutes — the slider becoming the
  // primary control would otherwise feel broken.
  if (v > 0 && player.isMuted()) {
    player.toggleMute();
    reflectMuteUi(false);
  }
  setVolume(v);
}

$npVolumeSlider.addEventListener('input', () => handleVolumeInput($npVolumeSlider));
// Desktop mini-player slider — same behaviour, shared volume state.
$miniVolumeSlider.addEventListener('input', () => handleVolumeInput($miniVolumeSlider));

// ── Broken-station report (#614) ────────────────────────────────────
// Report row in the station-info popup → report sheet (category + optional
// comment) → POST → local receipt → per-station status line, refreshed by
// an opportunistic status poll on load / tab foreground. Contract:
// docs/spec/contracts/broken-reports.md.
let reportReceipts = pruneReceipts(loadReceipts(), Date.now());
saveReceipts(reportReceipts);

function setReportBrokenState(state: 'idle' | 'sending' | 'sent' | 'error'): void {
  $npReportBroken.classList.toggle('is-sent', state === 'sent');
  $npReportBroken.classList.toggle('is-error', state === 'error');
  $npReportBroken.disabled = state === 'sending' || !currentNP.station.id;
  $npReportBrokenLabel.textContent =
    state === 'sending'
      ? 'Sending...'
      : state === 'sent'
        ? 'Report sent'
        : state === 'error'
          ? 'Could not send'
          : 'Broken station';
  // Failure offers the email fallback (iOS parity).
  const s = currentNP.station;
  $npReportMail.hidden = state !== 'error' || !s.id;
  if (state === 'error' && s.id) {
    const subject = `Broken station: ${s.name}`;
    const body =
      `The station "${s.name}" failed to play in rrradio.\n\n` +
      `Station id: ${s.id}\nStream: ${s.streamUrl}\nState: ${currentNP.state}`;
    $npReportMail.href =
      `mailto:support@rrradio.org?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;
  }
  renderReportStatus();
}

/** Per-station status line from the latest receipt ("Reported" state). */
function renderReportStatus(): void {
  const id = currentNP.station.id;
  const receipt = id ? receiptForStation(reportReceipts, id) : undefined;
  $npReportStatus.hidden = !receipt;
  $npReportStatus.textContent = receipt ? receiptStatusText(receipt) : '';
  $npReportStatus.classList.toggle('is-resolved', receipt?.status === 'resolved');
}

function reportCategory(): ReportCategory | null {
  const checked = $npReportForm.querySelector<HTMLInputElement>(
    'input[name="np-report-category"]:checked',
  );
  const v = checked?.value;
  return v && (REPORT_CATEGORIES as readonly string[]).includes(v) ? (v as ReportCategory) : null;
}

function syncReportSheet(): void {
  const category = reportCategory();
  $npReportComment.placeholder =
    category === 'other' ? 'Describe the problem' : 'Comment (optional)';
  $npReportSend.disabled = !canSendReport(category, $npReportComment.value);
}

function openReportSheet(): void {
  const station = currentNP.station;
  if (!station.id) return;
  $npReportForm.reset();
  $npReportStation.textContent = station.name;
  syncReportSheet();
  closeStationInfo();
  $npReport.hidden = false;
  $npReportForm.querySelector<HTMLInputElement>('input[name="np-report-category"]')?.focus();
}

function closeReportSheet(): void {
  $npReport.hidden = true;
}

async function sendReport(): Promise<void> {
  const station = currentNP.station;
  const category = reportCategory();
  const comment = $npReportComment.value;
  if (!station.id || !canSendReport(category, comment) || !category) return;
  closeReportSheet();
  openStationInfo();
  setReportBrokenState('sending');
  try {
    const reportId = await reportBrokenStation(station, category, comment, currentNP.errorMessage);
    if (reportId) {
      reportReceipts = addReceipt(reportReceipts, {
        id: reportId,
        stationId: station.id,
        stationName: station.name,
        category,
        sentAt: Date.now(),
        status: 'received',
        seen: false,
      });
      saveReceipts(reportReceipts);
    }
    if (currentNP.station.id === station.id) setReportBrokenState('sent');
  } catch (err) {
    reportWorkerError(err, '/api/public/report-broken');
    if (currentNP.station.id === station.id) setReportBrokenState('error');
  }
}

$npReportBroken.addEventListener('click', openReportSheet);

// Error panel actions (#98).
function retryCurrentStation(): void {
  if (currentNP.state === 'error' && currentNP.station.id) void player.play(currentNP.station);
}
$npErrorRetry.addEventListener('click', retryCurrentStation);
$npErrorReport.addEventListener('click', openReportSheet);
// Failed because the device was offline: start again once it's back.
window.addEventListener('online', () => {
  if (currentNP.errorView?.kind === 'offline') retryCurrentStation();
});
$npReportForm.addEventListener('change', syncReportSheet);
$npReportComment.addEventListener('input', syncReportSheet);
$npReportForm.addEventListener('submit', (e) => {
  e.preventDefault();
  void sendReport();
});
$npReportClose.addEventListener('click', closeReportSheet);
$npReportScrim.addEventListener('click', closeReportSheet);
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !$npReport.hidden) closeReportSheet();
});

/** Opportunistic receipt poll (load + tab foreground, never on a timer).
 *  Failures are silent; a newly resolved receipt shows a one-shot toast. */
async function refreshReportReceipts(): Promise<void> {
  reportReceipts = pruneReceipts(reportReceipts, Date.now());
  const ids = idsToPoll(reportReceipts);
  if (ids.length === 0) return;
  const payload = await fetchReportStatuses(ids);
  if (payload === null) return;
  reportReceipts = applyStatuses(reportReceipts, payload, ids);
  const unseen = reportReceipts.find((r) => r.status === 'resolved' && !r.seen);
  if (unseen) {
    showBackupToast(resolvedToastText(unseen), 'ok');
    unseen.seen = true;
  }
  saveReceipts(reportReceipts);
  renderReportStatus();
}
void refreshReportReceipts();
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void refreshReportReceipts();
});

$npFav.addEventListener('click', () => {
  const s = currentNP.station;
  if (!s.id) return;
  onToggleFav(s);
});

$npSleep.addEventListener('click', () => {
  sleepIndex = (sleepIndex + 1) % SLEEP_CYCLE_MIN.length;
  setSleep(SLEEP_CYCLE_MIN[sleepIndex]);
});

// ── Keyboard shortcuts (#101) ───────────────────────────────────────
// Routing rules live in src/shortcuts.ts (ignore typing / modified keys /
// Space on a focused button / open sheets); each action calls the same
// function as its on-screen control.
const $kbdHelp = document.getElementById('kbd-help') as HTMLElement;
const $kbdHelpList = document.getElementById('kbd-help-list') as HTMLElement;
let kbdHelpReturnFocus: HTMLElement | null = null;

function renderShortcutHelp(): void {
  const rows: HTMLElement[] = [];
  for (const sc of SHORTCUTS) {
    const dt = document.createElement('dt');
    sc.keys.forEach((k, i) => {
      if (i > 0) dt.append(' ');
      const kbd = document.createElement('kbd');
      kbd.textContent = k;
      dt.append(kbd);
    });
    const dd = document.createElement('dd');
    dd.textContent = sc.label;
    rows.push(dt, dd);
  }
  $kbdHelpList.replaceChildren(...rows);
}

function setShortcutHelp(open: boolean): void {
  if (open === !$kbdHelp.hidden) return;
  if (open) {
    if (!$kbdHelpList.firstChild) renderShortcutHelp();
    kbdHelpReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    $kbdHelp.hidden = false;
    $kbdHelp.querySelector<HTMLElement>('.kbd-help__card')?.focus();
  } else {
    $kbdHelp.hidden = true;
    kbdHelpReturnFocus?.focus();
    kbdHelpReturnFocus = null;
  }
}

function focusSearchShortcut(): void {
  // Search lives on the list pages; from Now Playing (or wherever the
  // field is collapsed/hidden) jump to Browse first.
  if (activeTab === 'playing' || $search.offsetParent === null) setTab('browse');
  $search.focus();
  $search.select();
}

function runShortcut(action: ShortcutAction): void {
  const station = currentNP.station;
  switch (action) {
    case 'toggle-play':
      handlePlayToggle();
      break;
    case 'focus-search':
      focusSearchShortcut();
      break;
    case 'toggle-favorite':
      if (station.id) onToggleFav(station);
      break;
    case 'next':
      skipStation(1);
      break;
    case 'previous':
      skipStation(-1);
      break;
    case 'toggle-mute':
      reflectMuteUi(player.toggleMute());
      break;
    case 'toggle-help':
      setShortcutHelp($kbdHelp.hidden);
      break;
  }
}

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !$kbdHelp.hidden) {
    setShortcutHelp(false);
    return;
  }
  const action = shortcutFor(e, {
    modalOpen: !$kbdHelp.hidden || !$npInfo.hidden || !!document.querySelector('.sheet.open'),
    helpOpen: !$kbdHelp.hidden,
  });
  if (!action) return;
  e.preventDefault();
  runShortcut(action);
  track(`shortcut/${action}`);
});
document.getElementById('kbd-help-close')?.addEventListener('click', () => setShortcutHelp(false));
document.getElementById('kbd-help-scrim')?.addEventListener('click', () => setShortcutHelp(false));
document.getElementById('settings-shortcuts')?.addEventListener('click', () => {
  openSettingsSheet(false);
  setShortcutHelp(true);
});

// ─────────────────────────────────────────────────────────────
// Player subscription
// ─────────────────────────────────────────────────────────────

// Stale-build reload (#666): a tab left open for weeks reloads onto the
// latest deploy at a quiet moment — never mid-playback.
const checkForNewBuild = createBuildChecker({
  current: typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev',
  fetchVersion: () => fetchDeployedVersion(import.meta.env.BASE_URL),
  isBusy: () => currentNP.state === 'playing' || currentNP.state === 'loading',
  reload: () => window.location.reload(),
});
document.addEventListener('visibilitychange', () => void checkForNewBuild());

let lastIcyKey = '';
let prevState: typeof currentNP.state = 'idle';
let prevStationId = '';
let lastErrorMessage = '';

player.subscribe((np) => {
  // Translate a generic stream-failed error into a friendly
  // geo-restricted message when the curated `availableIn` flag tells
  // us this station is region-locked and the visitor is outside the
  // allow-list. The browser only surfaces a vague MediaError code on
  // the 401 the AIS9 streaming server returns, so without this
  // override the user would see "Cannot decode stream" or "Stream
  // error" for what's really a licensing geo-gate. Telemetry then
  // records the override message too, which is actually more useful
  // than the generic code — confirmed-geo errors get their own bucket.
  if (np.state === 'error' && !isAvailableInUserRegion(np.station)) {
    const label = geoRestrictionLabel(np.station, countryName);
    if (label) {
      np = { ...np, errorMessage: `${label} — ${REGION_LOCK_SUFFIX}` };
    }
  }
  // Listener-facing wording for the final error (#98). errorMessage stays
  // raw for telemetry and broken reports.
  if (np.state === 'error') {
    np = { ...np, errorView: playbackErrorView(np.errorMessage, navigator.onLine) };
  }
  // Switching station mid sleep-fade is a manual action: cancel the fade.
  if (np.station.id !== currentNP.station.id) cancelSleepFadeOnUserAction();
  const stationLost = !np.station.id && currentNP.station.id && activeTab === 'playing';
  const stationChanged = np.station.id && np.station.id !== currentNP.station.id;
  currentNP = np;
  // Refresh schedule when the user starts a new station — schedules
  // are per-station, fetched once on station change.
  if (stationChanged) {
    void loadSchedule(np.station);
    resetLyrics();
    setReportBrokenState('idle');
    // A new station starts the mini bar at rest (cancel any open close-swipe).
    miniReset();
  }
  $body.classList.toggle('is-playing', np.state === 'playing');
  $body.classList.toggle('has-station', !!np.station.id);
  // If the station was unloaded while the Playing tab was active,
  // bounce back to the last list tab so the user isn't stranded.
  if (stationLost) setTab(lastListTab);
  renderMiniPlayer(np);
  renderNowPlaying(np);
  syncRowPlayingState();
  syncLibraryCardsPlaying();

  // Telemetry: state transitions on the same station. Initial play is
  // already tracked by onRowPlay; here we capture pause/resume cycles
  // and stream errors. Station changes are skipped (the play event from
  // the row click already covers them).
  if (np.station.id && np.station.id === prevStationId) {
    if (prevState === 'playing' && np.state === 'paused') {
      track(`pause: ${np.station.name}`);
    } else if (prevState === 'paused' && np.state === 'loading') {
      track(`resume: ${np.station.name}`);
    }
  }
  // Offline failures are the listener's connection, not the station: no
  // error event, so error-watch and the broken-station list stay clean.
  if (np.state === 'error' && prevState !== 'error' && np.errorView?.kind !== 'offline') {
    const reason = np.errorMessage ?? 'unknown';
    if (reason !== lastErrorMessage) {
      lastErrorMessage = reason;
      // Keep the existing per-station error event (the dashboard reads
      // `error: <station>` for the broken-station list), AND emit a
      // structured `error/stream` event so the same regression shows up
      // in the global error feed alongside catalog/worker/runtime
      // errors. Audit #76.
      const sanitizedReason = truncateErrorMessage(reason);
      track(`error: ${np.station.name || 'unknown'}`, sanitizedReason);
      reportStreamError(sanitizedReason, np.station.id);
    }
  } else if (np.state !== 'error') {
    lastErrorMessage = '';
  }
  // Playback stopped in a background tab: a pending update can land unseen.
  const wasBusy = prevState === 'playing' || prevState === 'loading';
  if (document.hidden && wasBusy && np.state !== 'playing' && np.state !== 'loading') {
    void checkForNewBuild();
  }
  prevState = np.state;
  prevStationId = np.station.id;

  // Drive the metadata poller off the loaded station, not the
  // playback state — the user wants to see what's on air before
  // they tap play, and on a paused/loading station the broadcast
  // is still happening, so the current title is meaningful even
  // when audio isn't actively playing. Per-station overrides win
  // (e.g. Grrif uses /live/covers.json); falls back to
  // ICY-over-fetch. Stops automatically when the station is
  // unloaded (state goes back to idle, station.id becomes '').
  const key = np.station.id;
  if (key !== lastIcyKey) {
    lastIcyKey = key;
    if (key) {
      const matched = findFetcher(np.station);
      if (matched) {
        meta.start(matched.station, matched.fetcher, 30_000);
      } else {
        meta.start(np.station, icyFetcher, 30_000);
      }
    } else {
      meta.stop();
    }
  }
});

// ─────────────────────────────────────────────────────────────
// Init
// ─────────────────────────────────────────────────────────────

renderTabBar();
renderTopBar();
syncLayoutMode();
syncSearchClear();
syncFilterDot();
syncMusicServiceLinks();
// Landing-page preference: open Favorites / Recents on launch when the
// user picked one (and there's no inbound ?q search / station deep-link
// taking precedence — those still win via runQuery / autoLoadStationFromUrl).
// Inbound `?genre=<id>` (the "Open in player" link on the static
// /genre/<id>/ landing pages, #61) opens Browse with that genre filter
// applied — the same state a discovery genre chip produces. Unknown ids are
// ignored. Applied before the first runQuery below.
const inboundGenre = findGenre(new URLSearchParams(window.location.search).get('genre'));
if (inboundGenre) {
  filterGenres.add(inboundGenre.id);
  syncFilterDot();
  track(`deeplink/genre/${inboundGenre.id}`);
}
{
  const landing = getString(LANDING_KEY);
  const hasQuery = !!new URLSearchParams(window.location.search).get('q');
  if (!hasQuery && !inboundGenre && (landing === 'fav' || landing === 'recent')) setTab(landing);
}
// Paint the discovery landing from the few-KB summary first (genre/country
// chips + the "Browse all N" count) so Browse is usable immediately instead
// of waiting on the 21 MB catalog below. Cheap re-render; no-op off-Browse.
void loadDiscoverySummary().then(() => {
  if (inDiscovery()) renderContent();
});
// Stations.json defines the built-in catalog (Featured strip + per-station
// metadata fetcher overrides). It loads in the background; re-render once it
// lands so the Featured tiles + exact counts replace the summary view.
void loadBuiltinStations().then(() => {
  if (activeTab === 'browse') renderContent();
  autoLoadStationFromUrl();
  // Preload the Surprise me exclusions (~19 KB gz) once the catalog is in,
  // so the shuffle tap plays synchronously inside the user gesture.
  void loadSurpriseExclude();
});
// Editorial Featured rail on the discovery landing — load once, then
// re-render if we're still on the discovery view when it lands.
void loadHighlights().then((list) => {
  highlightsRaw = list;
  if (inDiscovery()) renderContent();
});
// Fetch the visitor's country from the worker so geo-restricted
// station rows can show a "<Country> only" badge instead of failing
// silently at play time. Fire-and-forget — the result is cached in
// localStorage for 24h, and the rerender on resolve is cheap when
// nothing geo-restricted is visible. We re-render unconditionally
// because the answer could just as easily be "unknown" (in which
// case we already render correctly), but a single extra renderContent
// pass on page load is harmless.
void fetchUserRegion().then(() => {
  renderContent();
});
// Sitelinks search box (Google / Bing) and any inbound link with
// `?q=...` lands on '/' with a query — prefill the search input so
// the visitor sees results without an extra step. Declared in the
// WebSite SearchAction JSON-LD in index.html.
{
  const q = new URLSearchParams(window.location.search).get('q');
  if (q && q.trim()) {
    $search.value = q.trim();
    syncSearchClear();
  }
}
void runQuery();
// Wake-to-radio left the web app (iOS-only now) — drop its stale keys.
clearLegacyWakeKeys();

// Previous/next — Now Playing, mini-player and the lock-screen /
// Bluetooth / AirPods / CarPlay controls. Steps circularly through the
// active queue: the open list, Favorites, Recents or the Browse/search
// result the station was started from, falling back to Favorites
// (./queue). No-op when the queue is empty or holds only this station.
function skipStation(direction: 1 | -1): void {
  const queue = resolveQueue(activeQueue, {
    listStations: (id) => getList(id)?.stations,
    favorites: getFavorites,
  });
  const next = stepQueue(queue, currentNP.station.id, direction);
  if (!next) return;
  void player.play(next);
  pushRecent(next);
  track(direction === 1 ? 'lock-skip-next' : 'lock-skip-prev', next.name);
  // Keep the Recents sub-view and the Library home's Recents strip fresh.
  if (activeTab === 'recent' || activeTab === 'library') renderContent();
}
player.setSkipHandlers(
  () => skipStation(1),
  () => skipStation(-1),
);

/** Pre-rendered /station/<id>/ landing pages set window.__STATION_ID__
 *  so the SPA can auto-play the station the visitor landed on. We also
 *  parse the URL path as a fallback (in case the injection was stripped
 *  or the user shared a link to a non-prerendered station id), plus the
 *  `?play=<id>` query — that's the Universal Link form the AASA hands to
 *  the iOS app (public/.well-known/apple-app-site-association), so the
 *  same link auto-plays in the browser when the app isn't installed.
 *  onRowPlay → syncUrlForStation then rewrites the address bar to the
 *  canonical /station/<id>/. The match is deferred until BUILTIN_STATIONS
 *  has hydrated. */
function autoLoadStationFromUrl(): void {
  const declared = (window as unknown as { __STATION_ID__?: unknown }).__STATION_ID__;
  const fromGlobal = typeof declared === 'string' ? declared : undefined;
  const fromPath = window.location.pathname.match(/\/station\/([^/]+)\/?$/)?.[1];
  const fromQuery =
    new URLSearchParams(window.location.search).get('play')?.trim() || undefined;
  const id = fromGlobal ?? fromPath ?? fromQuery;
  if (!id) return;
  const station = BUILTIN_STATIONS.find((s) => s.id === id);
  if (!station) return;
  onRowPlay(station);
}

/** Push a shareable URL when a station is selected, so the user can
 *  copy the address bar / hit refresh and land back on the same
 *  station. popstate restores the URL → no reload, audio keeps
 *  playing during in-app navigation. */
window.addEventListener('popstate', () => {
  // Don't auto-stop or auto-play on back/forward — radio sessions
  // are long-running and a page navigation shouldn't interrupt
  // playback. If the user wants to switch they can click another
  // row. We just keep the URL state coherent.
});
